import { Debugger, WorkspaceIndex, allBuiltinModules, buildBuiltinCompletions, buildParseDiagnostics, buildSymbolDiagnostics, buildTypeDiagnostics, createDvala, findCallContext, findTypeAtPosition, formatHoverType, formatSource, isDvalaIdentifierName, minifyTokenStream, parseToAst, symbolDefToCompletion, tokenizeSource, typecheck } from "@mojir/dvala-core-tooling";
import { NodeTypes, ParseError, TokenizerError, asUserDefinedSymbolNode, assertNumberOfParams, bindingTargetTypes, canBeOperator, isBuiltinSymbolNode, isNumberReservedSymbol, isSpecialSymbolNode, isSpreadNode, isUserDefinedSymbolNode, numberReservedSymbolRecord, reservedSymbolRecord, resolveSourceCodeInfo, specialExpressionTypes } from "@mojir/dvala-types";
import { allStandardEffectDefinitions, arrayNormalExpression, assertModule, assertionNormalExpression, astModule, bitwiseNormalExpression, bitwiseUtilsModule, builtin, collectionNormalExpression, collectionUtilsModule, convertModule, deserializeFromObject, extractCheckpointSnapshots, functionalNormalExpression, functionalUtilsModule, getAllBindingTargetNames, getMetaNormalExpression, gridModule, handlerModule, isFunctionDocs, isFunctionReference, isFunctionReference as isFunctionReference$1, jsonModule, linearAlgebraModule, macrosModule, mathNormalExpression, mathUtilsModule, matrixModule, miscNormalExpression, normalExpressionTypes, normalExpressions, numberTheoryModule, objectNormalExpression, predicatesNormalExpression, regexpNormalExpression, resume, retrigger, sequenceNormalExpression, sequenceUtilsModule, specialExpressions, stringNormalExpression, stringUtilsModule, timeModule, vectorModule } from "@mojir/dvala-engine";
//#region ../dvala-core-tooling/dist/reference/_chunks/dvala.js
const binaryOperators = [
	"^",
	"*",
	"/",
	"%",
	"+",
	"-",
	"<<",
	">>",
	">>>",
	"++",
	"<",
	"<=",
	"≤",
	">",
	">=",
	"≥",
	"==",
	"!=",
	"!=",
	"&",
	"xor",
	"|",
	"&&",
	"||",
	"??",
	"|>"
];
const unaryPrefixOperators = ["!"];
const otherOperators = [
	"@",
	":",
	"->",
	"...",
	"?.",
	".",
	"?",
	",",
	"=",
	":",
	";"
];
const symbolicOperators = [
	...binaryOperators,
	...unaryPrefixOperators,
	...otherOperators
];
const nonFunctionOperatorSet = new Set([
	"comment",
	"block",
	"if",
	"let",
	"loop",
	"recur",
	"while",
	"handler",
	"transform",
	"resume"
]);
function isFunctionOperator(operator) {
	return !nonFunctionOperatorSet.has(operator);
}
const binaryOperatorSet = new Set(binaryOperators);
function isBinaryOperator(operator) {
	return binaryOperatorSet.has(operator);
}
const symbolicOperatorSet = new Set(symbolicOperators);
function isSymbolicOperator(operator) {
	return symbolicOperatorSet.has(operator);
}
const jsIdentifierFirstCharRegExp = /[a-zA-Z_$]/;
const jsIdentifierCharRegExp = /[a-zA-Z0-9_$]/;
const whitespaceRegExp = /\s/;
const NO_MATCH = [0];
const tokenizeLParen = (input, position) => tokenizeToken("LParen", "(", input, position);
const tokenizeRParen = (input, position) => tokenizeToken("RParen", ")", input, position);
const tokenizeLBracket = (input, position) => tokenizeToken("LBracket", "[", input, position);
const tokenizeRBracket = (input, position) => tokenizeToken("RBracket", "]", input, position);
const tokenizeLBrace = (input, position) => tokenizeToken("LBrace", "{", input, position);
const tokenizeRBrace = (input, position) => tokenizeToken("RBrace", "}", input, position);
const tokenizeString = (input, position) => {
	if (input[position] !== "\"") return NO_MATCH;
	let value = "\"";
	let length = 1;
	let char = input[position + length];
	let escaping = false;
	while (char && (char !== "\"" || escaping)) {
		length += 1;
		if (escaping) {
			escaping = false;
			value += char;
		} else {
			if (char === "\\") escaping = true;
			value += char;
		}
		char = input[position + length];
	}
	if (!char) return [length, [
		"Error",
		value,
		void 0,
		`Unclosed string at position ${position}`
	]];
	value += "\"";
	return [length + 1, ["string", value]];
};
const tokenizeRegexpShorthand = (input, position) => {
	if (input[position] !== "#") return NO_MATCH;
	const [stringLength, token] = tokenizeString(input, position + 1);
	if (!token) return NO_MATCH;
	if (token[0] === "Error") {
		const errorToken = [
			"Error",
			`#${token[1]}`,
			void 0,
			`Unclosed regexp at position ${position}`
		];
		return [stringLength + 1, errorToken];
	}
	position += stringLength + 1;
	let length = stringLength + 1;
	let options = "";
	while (input[position] === "g" || input[position] === "i") {
		options += input[position];
		length += 1;
		position += 1;
		if (options.includes(input[position])) return [length, [
			"Error",
			`#${token[1]}${options}`,
			void 0,
			`Duplicated regexp option "${input[position]}"`
		]];
	}
	return [length, ["RegexpShorthand", `#${token[1]}${options}`]];
};
const tokenizeMacroPrefix = (input, position) => {
	if (input[position] !== "#") return NO_MATCH;
	const nextChar = input[position + 1];
	if (!nextChar || !jsIdentifierFirstCharRegExp.test(nextChar)) return NO_MATCH;
	let i = position + 2;
	while (i < input.length && jsIdentifierCharRegExp.test(input[i])) i++;
	const name = input.slice(position + 1, i);
	return [i - position, ["MacroPrefix", name]];
};
const tokenizeAtom = (input, position) => {
	if (input[position] !== ":") return NO_MATCH;
	if (position > 0 && jsIdentifierCharRegExp.test(input[position - 1])) return NO_MATCH;
	const nextChar = input[position + 1];
	if (!nextChar || !jsIdentifierFirstCharRegExp.test(nextChar)) return NO_MATCH;
	let i = position + 2;
	while (i < input.length && jsIdentifierCharRegExp.test(input[i])) i++;
	const name = input.slice(position + 1, i);
	return [i - position, ["Atom", name]];
};
function tokenizeToken(type, value, input, position) {
	if (value === input.slice(position, position + value.length)) return [value.length, [type, value]];
	else return NO_MATCH;
}
const tokenizeWhitespace = (input, position) => {
	let char = input[position];
	if (!char || !whitespaceRegExp.test(char)) return NO_MATCH;
	let value = char;
	position += 1;
	char = input[position];
	while (char && whitespaceRegExp.test(char)) {
		value += char;
		position += 1;
		char = input[position];
	}
	return [value.length, ["Whitespace", value]];
};
const decimalNumberRegExp = /\d/;
const octalNumberRegExp = /[0-7]/;
const hexNumberRegExp = /[0-9a-f]/i;
const binaryNumberRegExp = /[01]/;
const postNumberRegExp = /[\s)\]}(,;+\-*/%^<>=!&|.?~:#]/;
const tokenizeNumber = (input, position, prevToken) => {
	let i;
	const negate = input[position] === "-";
	const plusPrefix = input[position] === "+";
	if ((negate || plusPrefix) && prevToken) {
		const prevType = prevToken[0];
		if (prevType === "Number" || prevType === "BasePrefixedNumber" || prevType === "Symbol" || prevType === "Atom" || prevType === "ReservedSymbol" || prevType === "RParen" || prevType === "RBracket" || prevType === "RBrace") return NO_MATCH;
	}
	const start = negate || plusPrefix ? position + 1 : position;
	let hasDecimalPoint = false;
	let hasExponent = false;
	for (i = start; i < input.length; i += 1) {
		const char = input[i];
		if (char === "_") {
			if (!decimalNumberRegExp.test(input[i - 1]) || !decimalNumberRegExp.test(input[i + 1])) {
				if (i === start) return NO_MATCH;
				return [i - position + 1, [
					"Error",
					input.substring(position, i + 1),
					void 0,
					`Invalid number format at position ${i + 1}`
				]];
			}
		} else if (char === ".") {
			if (i === start) return NO_MATCH;
			if (hasDecimalPoint || hasExponent) return [i - position + 1, [
				"Error",
				input.substring(position, i + 1),
				void 0,
				`Invalid number format at position ${i + 1}`
			]];
			hasDecimalPoint = true;
		} else if (char === "e" || char === "E") {
			if (i === start) return NO_MATCH;
			if (hasExponent) return [i - position + 1, [
				"Error",
				input.substring(position, i + 1),
				void 0,
				`Invalid number format at position ${i + 1}`
			]];
			if (input[i - 1] === "." || input[i - 1] === "+" || input[i - 1] === "-") return [i - position + 1, [
				"Error",
				input.substring(position, i + 1),
				void 0,
				`Invalid number format at position ${i + 1}`
			]];
			if (input[i + 1] === "+" || input[i + 1] === "-") i += 1;
			hasExponent = true;
		} else if (!decimalNumberRegExp.test(char)) break;
	}
	if ((negate || plusPrefix) && i === start) return NO_MATCH;
	const length = i - position;
	if (length === 0) return NO_MATCH;
	const nextChar = input[i];
	if (nextChar && nextChar !== ":" && !postNumberRegExp.test(nextChar)) return [i - position + 1, [
		"Error",
		input.substring(position, i + 1),
		void 0,
		`Invalid number format at position ${i + 1}`
	]];
	return [length, ["Number", input.substring(position, i)]];
};
const tokenizeBasePrefixedNumber = (input, position) => {
	if (input[position] !== "0") return NO_MATCH;
	const baseChar = input[position + 1];
	const type = baseChar === "b" || baseChar === "B" ? "binary" : baseChar === "o" || baseChar === "O" ? "octal" : baseChar === "x" || baseChar === "X" ? "hex" : null;
	if (type === null) return NO_MATCH;
	let i;
	for (i = position + 2; i < input.length; i += 1) {
		const char = input[i];
		if (type === "binary" && !binaryNumberRegExp.test(char)) break;
		if (type === "octal" && !octalNumberRegExp.test(char)) break;
		if (type === "hex" && !hexNumberRegExp.test(char)) break;
	}
	const length = i - position;
	if (length <= 2) return NO_MATCH;
	const nextChar = input[i];
	if (nextChar && !postNumberRegExp.test(nextChar)) return NO_MATCH;
	return [length, ["BasePrefixedNumber", input.substring(position, i)]];
};
/**
* Tokenize effect name literal: @segment.segment.segment
* Each segment follows symbol naming rules. Dots separate segments.
* The token value is the name without the @ prefix.
*/
const tokenizeEffectName = (input, position) => {
	if (input[position] !== "@") return NO_MATCH;
	let i = position + 1;
	const firstChar = input[i];
	if (!firstChar || !jsIdentifierFirstCharRegExp.test(firstChar) && firstChar !== "*") return NO_MATCH;
	let name = "";
	while (i < input.length && (jsIdentifierCharRegExp.test(input[i]) || input[i] === "*")) {
		name += input[i];
		i++;
	}
	while (i < input.length && input[i] === ".") {
		const dotPos = i;
		i++;
		if (i >= input.length || !jsIdentifierFirstCharRegExp.test(input[i]) && input[i] !== "*") {
			i = dotPos;
			break;
		}
		const segStart = i;
		while (i < input.length && (jsIdentifierCharRegExp.test(input[i]) || input[i] === "*")) i++;
		name += `.${input.slice(segStart, i)}`;
	}
	return [i - position, ["EffectName", name]];
};
const tokenizeSymbol = (input, position) => {
	let value = input[position];
	if (value === "'") {
		let length = 1;
		let char = input[position + length];
		let escaping = false;
		while (char !== "'" || escaping) {
			if (char === void 0) return [length, [
				"Error",
				value,
				void 0,
				`Unclosed quoted symbol at position ${position}`
			]];
			length += 1;
			if (escaping) {
				escaping = false;
				value += char;
			} else {
				if (char === "\\") escaping = true;
				value += char;
			}
			char = input[position + length];
		}
		value += "'";
		return [length + 1, ["Symbol", value]];
	}
	if (jsIdentifierFirstCharRegExp.test(value)) {
		const initialPosition = position;
		position += 1;
		let char = input[position];
		while (char && jsIdentifierCharRegExp.test(char)) {
			value += char;
			position += 1;
			char = input[position];
		}
		return [position - initialPosition, ["Symbol", value]];
	}
	return NO_MATCH;
};
const tokenizeReservedSymbolToken = (input, position) => {
	const symbolMeta = tokenizeSymbol(input, position);
	if (symbolMeta[0] === 0 || !symbolMeta[1]) return NO_MATCH;
	let rawName = symbolMeta[1][1];
	rawName = rawName.startsWith("'") ? rawName.slice(1, rawName.length - 1) : rawName;
	const symbolName = rawName;
	if (reservedSymbolRecord[symbolName] === void 0) return NO_MATCH;
	return [symbolMeta[0], ["ReservedSymbol", symbolName]];
};
const tokenizeOperator = (input, position) => {
	const threeChars = input.slice(position, position + 3);
	if (position + 2 < input.length && isSymbolicOperator(threeChars)) return [3, ["Operator", threeChars]];
	const twoChars = input.slice(position, position + 2);
	if (position + 1 < input.length && isSymbolicOperator(twoChars)) return [2, ["Operator", twoChars]];
	const oneChar = input[position] ?? "";
	if (isSymbolicOperator(oneChar)) return [1, ["Operator", oneChar]];
	return NO_MATCH;
};
const tokenizeMultiLineComment = (input, position) => {
	if (input[position] === "/" && input[position + 1] === "*") {
		let length = 2;
		let value = "/*";
		while ((input[position + length] !== "*" || input[position + length + 1] !== "/") && position + length + 1 < input.length) {
			value += input[position + length];
			length += 1;
		}
		if (position + length + 1 >= input.length) return [length, [
			"Error",
			value,
			void 0,
			`Unclosed multi-line comment at position ${position}`
		]];
		value += "*/";
		length += 2;
		return [length, ["MultiLineComment", value]];
	}
	return NO_MATCH;
};
const tokenizeShebang = (input, position) => {
	if (input[position] === "#" && input[position + 1] === "!") {
		let length = 2;
		let value = "#!";
		while (input[position + length] !== "\n" && position + length < input.length) {
			value += input[position + length];
			length += 1;
		}
		return [length, ["Shebang", value]];
	}
	return NO_MATCH;
};
/**
* Tokenize a quote splice marker: $^+{
* Matches $ followed by one or more ^ followed by {.
* The value includes the full marker (e.g. "$^{", "$^^{").
* The opening { is consumed — the parser finds the matching } via RBrace tokens.
*/
const tokenizeQuoteSplice = (input, position) => {
	if (input[position] !== "$") return NO_MATCH;
	let caretCount = 0;
	while (input[position + 1 + caretCount] === "^") caretCount++;
	if (caretCount === 0 || input[position + 1 + caretCount] !== "{") return NO_MATCH;
	const length = 1 + caretCount + 1;
	return [length, ["QuoteSplice", input.slice(position, position + length)]];
};
const tokenizeTemplateString = (input, position) => {
	if (input[position] !== "`") return NO_MATCH;
	let value = "`";
	let length = 1;
	while (position + length < input.length) {
		const char = input[position + length];
		if (char === "`") {
			value += "`";
			length += 1;
			return [length, ["TemplateString", value]];
		}
		if (char === "$" && input[position + length + 1] === "{") {
			value += "${";
			length += 2;
			let braceDepth = 1;
			while (position + length < input.length && braceDepth > 0) {
				const c = input[position + length];
				if (c === "{") {
					braceDepth += 1;
					value += c;
					length += 1;
				} else if (c === "}") {
					braceDepth -= 1;
					value += c;
					length += 1;
				} else if (c === "\"") {
					value += c;
					length += 1;
					let escaping = false;
					while (position + length < input.length) {
						const sc = input[position + length];
						value += sc;
						length += 1;
						if (escaping) escaping = false;
						else if (sc === "\\") escaping = true;
						else if (sc === "\"") break;
					}
				} else if (c === "'") {
					value += c;
					length += 1;
					let escaping = false;
					while (position + length < input.length) {
						const sc = input[position + length];
						value += sc;
						length += 1;
						if (escaping) escaping = false;
						else if (sc === "\\") escaping = true;
						else if (sc === "'") break;
					}
				} else if (c === "`") {
					const [nestedLength, nestedToken] = tokenizeTemplateString(input, position + length);
					if (nestedLength === 0 || !nestedToken) return [length, [
						"Error",
						value,
						void 0,
						`Unclosed nested template string at position ${position + length}`
					]];
					if (nestedToken[0] === "Error") return [length + nestedLength, [
						"Error",
						value + nestedToken[1],
						void 0,
						nestedToken[3]
					]];
					value += nestedToken[1];
					length += nestedLength;
				} else {
					value += c;
					length += 1;
				}
			}
			if (braceDepth > 0) return [length, [
				"Error",
				value,
				void 0,
				`Unclosed interpolation in template string at position ${position}`
			]];
		} else {
			value += char;
			length += 1;
		}
	}
	return [length, [
		"Error",
		value,
		void 0,
		`Unclosed template string at position ${position}`
	]];
};
const tokenizeSingleLineComment = (input, position) => {
	if (input[position] === "/" && input[position + 1] === "/") {
		let length = 2;
		let value = "//";
		while (input[position + length] !== "\n" && position + length < input.length) {
			value += input[position + length];
			length += 1;
		}
		return [length, ["SingleLineComment", value]];
	}
	return NO_MATCH;
};
const tokenizers = [
	tokenizeWhitespace,
	tokenizeMultiLineComment,
	tokenizeSingleLineComment,
	tokenizeReservedSymbolToken,
	tokenizeLParen,
	tokenizeRParen,
	tokenizeLBracket,
	tokenizeRBracket,
	tokenizeLBrace,
	tokenizeRBrace,
	tokenizeString,
	tokenizeQuoteSplice,
	tokenizeTemplateString,
	tokenizeRegexpShorthand,
	tokenizeMacroPrefix,
	tokenizeAtom,
	tokenizeBasePrefixedNumber,
	tokenizeNumber,
	tokenizeEffectName,
	tokenizeOperator,
	tokenizeSymbol
];
function tokenize(input, debug, filePath) {
	let position = 0;
	const tokenStream = {
		tokens: [],
		filePath,
		source: debug ? input : void 0
	};
	let prevToken;
	while (position < input.length) {
		const debugInfo = debug ? createDebugInfo(input, position) : void 0;
		const [count, token] = getCurrentToken(input, position, prevToken);
		position += count;
		if (token) {
			/* v8 ignore next 3 */
			if (debugInfo) token[2] = debugInfo;
			tokenStream.tokens.push(token);
			if (token[0] !== "Whitespace") prevToken = token;
		}
	}
	return tokenStream;
}
/** Create a 0-based [line, column] debug position from a character offset. */
function createDebugInfo(input, position) {
	const lines = input.substring(0, position + 1).split(/\r\n|\r|\n/);
	const lastLine = lines[lines.length - 1];
	return [lines.length - 1, lastLine.length - 1];
}
function getCurrentToken(input, position, prevToken) {
	const initialPosition = position;
	if (position === 0) {
		const [nbrOfCharacters, token] = tokenizeShebang(input, position);
		position += nbrOfCharacters;
		if (nbrOfCharacters > 0) return [position - initialPosition, token];
	}
	for (const tokenizer of tokenizers) {
		const [nbrOfCharacters, token] = tokenizer(input, position, prevToken);
		position += nbrOfCharacters;
		if (nbrOfCharacters === 0) continue;
		return [position - initialPosition, token];
	}
	return [1, [
		"Error",
		input[initialPosition],
		void 0,
		"Unrecognized character"
	]];
}
var CstBuilder = class {
	/** Stack of nodes being built. The bottom is the root. */
	stack = [];
	/** Whether finish() has been called. */
	finished = false;
	/**
	* Open a new CST node of the given kind. All subsequent tokens and
	* child nodes will be added as children of this node until `endNode()`
	* is called.
	*/
	startNode(kind) {
		this.stack.push({
			kind,
			children: []
		});
	}
	/**
	* Add a leaf token to the current node.
	*/
	token(cstToken) {
		this.current().children.push(cstToken);
	}
	/**
	* Save a checkpoint at the current position in the current node's children.
	* Used with `startNodeAt()` to retroactively wrap already-parsed children
	* in a new node — needed by the Pratt parser for binary operators, property
	* access, and function calls where the left operand is parsed before the
	* wrapper node kind is known.
	*/
	checkpoint() {
		return this.current().children.length;
	}
	/**
	* Open a new node that retroactively wraps children from a prior checkpoint.
	* Children from `checkpoint` to the end of the current node's children list
	* are moved into the new node, which becomes the current node.
	* Call `endNode()` to close it (like any other node).
	*/
	startNodeAt(checkpoint, kind) {
		const node = {
			kind,
			children: this.current().children.splice(checkpoint)
		};
		this.stack.push(node);
	}
	/**
	* Close the current node and attach it as a child of its parent.
	* If this closes the root node, the tree is complete.
	*/
	endNode() {
		if (this.stack.length < 2) return;
		const completed = this.stack.pop();
		this.current().children.push(completed);
	}
	/**
	* Return the node currently open at the top of the builder's stack.
	* Used by parseQuote to snapshot the body children emitted during the
	* token-collection pass before truncating them.
	*/
	peekCurrent() {
		return this.current();
	}
	/**
	* Truncate children of the current node back to the given checkpoint.
	* Used by parseQuote to discard the raw body tokens emitted during the
	* token-collection pass before replacing them with a CST-structured body.
	*/
	truncateCurrent(checkpoint) {
		this.current().children.length = checkpoint;
	}
	/**
	* Append a pre-built child (token or node) to the current node.
	* Used by parseQuote to attach the structured body CST produced by the
	* sub-parse pass.
	*/
	appendChild(child) {
		this.current().children.push(child);
	}
	/**
	* Return the completed untyped CST tree.
	* Must be called exactly once after all events have been emitted.
	*/
	finish() {
		if (this.finished) throw new Error("CstBuilder.finish() called more than once");
		if (this.stack.length !== 1) throw new Error(`CstBuilder.finish(): expected exactly 1 node on stack, found ${this.stack.length}. Mismatched startNode/endNode calls.`);
		this.finished = true;
		return this.stack[0];
	}
	/** The node currently being built (top of stack). */
	current() {
		if (this.stack.length === 0) throw new Error("CstBuilder: no open node — call startNode() first");
		return this.stack[this.stack.length - 1];
	}
};
function isAtomToken(token) {
	return token?.[0] === "Atom";
}
function isSymbolToken(token, symbolName) {
	if (token?.[0] !== "Symbol") return false;
	if (symbolName && token[1] !== symbolName) return false;
	return true;
}
function assertSymbolToken(token, symbolName) {
	if (!isSymbolToken(token, symbolName)) throwUnexpectedToken("Symbol", void 0, token);
}
function asSymbolToken(token, symbolName) {
	assertSymbolToken(token, symbolName);
	return token;
}
function isEffectNameToken(token) {
	return token?.[0] === "EffectName";
}
function isReservedSymbolToken(token, symbolName) {
	if (token?.[0] !== "ReservedSymbol") return false;
	if (symbolName && token[1] !== symbolName) return false;
	return true;
}
function assertReservedSymbolToken(token, symbolName) {
	if (!isReservedSymbolToken(token, symbolName)) throwUnexpectedToken("ReservedSymbol", symbolName, token);
}
function asReservedSymbolToken(token, symbolName) {
	assertReservedSymbolToken(token, symbolName);
	return token;
}
function isShebangToken(token) {
	return token?.[0] === "Shebang";
}
function isSingleLineCommentToken(token) {
	return token?.[0] === "SingleLineComment";
}
function isMultiLineCommentToken(token) {
	return token?.[0] === "MultiLineComment";
}
function isOperatorToken(token, operatorName) {
	if (token?.[0] !== "Operator") return false;
	if (operatorName && token[1] !== operatorName) return false;
	return true;
}
function assertOperatorToken(token, operatorName) {
	if (!isOperatorToken(token, operatorName)) throwUnexpectedToken("Operator", operatorName, token);
}
function isWhitespaceToken(token) {
	return token?.[0] === "Whitespace";
}
function isNumberToken(token) {
	return token?.[0] === "Number";
}
function isBasePrefixedNumberToken(token) {
	return token?.[0] === "BasePrefixedNumber";
}
function isLParenToken(token) {
	return token?.[0] === "LParen";
}
function assertLParenToken(token) {
	if (!isLParenToken(token)) throwUnexpectedToken("LParen", void 0, token);
}
function isRParenToken(token) {
	return token?.[0] === "RParen";
}
function assertRParenToken(token) {
	if (!isRParenToken(token)) throwUnexpectedToken("RParen", void 0, token);
}
function isLBracketToken(token) {
	return token?.[0] === "LBracket";
}
function assertLBracketToken(token) {
	if (!isLBracketToken(token)) throwUnexpectedToken("LBracket", void 0, token);
}
function asLBracketToken(token) {
	assertLBracketToken(token);
	return token;
}
function isRBracketToken(token) {
	return token?.[0] === "RBracket";
}
function assertRBracketToken(token) {
	if (!isRBracketToken(token)) throwUnexpectedToken("RBracket", void 0, token);
}
function isLBraceToken(token) {
	return token?.[0] === "LBrace";
}
function assertLBraceToken(token) {
	if (!isLBraceToken(token)) throwUnexpectedToken("LBrace", void 0, token);
}
function asLBraceToken(token) {
	assertLBraceToken(token);
	return token;
}
function isRBraceToken(token) {
	return token?.[0] === "RBrace";
}
function assertRBraceToken(token) {
	if (!isRBraceToken(token)) throwUnexpectedToken("RBrace", void 0, token);
}
function isStringToken(token) {
	return token?.[0] === "string";
}
function isA_BinaryOperatorToken(token) {
	return token?.[0] === "Operator" && isBinaryOperator(token[1]);
}
function isTemplateStringToken(token) {
	return token?.[0] === "TemplateString";
}
/** Convert lightweight token debug info to SourceCodeInfo for error reporting. */
function debugInfoToSourceCodeInfo(debugInfo, source, filePath) {
	if (!debugInfo) return void 0;
	const [line, column] = debugInfo;
	const code = source ? source.split("\n")[line] ?? "" : "";
	return {
		position: {
			line: line + 1,
			column: column + 1
		},
		code,
		filePath
	};
}
/** Extract TokenDebugInfo from SourceCodeInfo (inverse of debugInfoToSourceCodeInfo). */
function sourceCodeInfoToDebugInfo(sci) {
	if (!sci) return void 0;
	return [sci.position.line - 1, sci.position.column - 1];
}
function throwUnexpectedToken(expected, expectedValue, actual) {
	const actualOutput = actual ? `${actual[0]} '${actual[1]}'` : "end of input";
	const sourceCodeInfo = actual?.[2] ? debugInfoToSourceCodeInfo(actual[2]) : void 0;
	throw new TokenizerError(`Unexpected token: ${actualOutput}, expected ${expected}${expectedValue ? ` '${expectedValue}'` : ""}`, sourceCodeInfo);
}
/**
* Assign a node ID from the parser context. When debug mode is active,
* the source position is recorded in the source map.
*/
const structuralLeafTypes = new Set([
	NodeTypes.Sym,
	NodeTypes.Builtin,
	NodeTypes.Special,
	NodeTypes.Reserved,
	NodeTypes.Effect,
	NodeTypes.Binding,
	NodeTypes.Spread,
	"symbol",
	"rest",
	"object",
	"array",
	"literal",
	"wildcard"
]);
function withSourceCodeInfo(node, debugInfo, ctx) {
	const isLeaf = structuralLeafTypes.has(node[0]);
	node[2] = ctx.allocateNodeId(debugInfo, isLeaf || void 0);
	return node;
}
function stringToSymbolNode(value, debugInfo, ctx) {
	if (specialExpressionTypes[value] !== void 0 && value !== "fn" && value !== "defn") {
		const node = withSourceCodeInfo([
			NodeTypes.Special,
			specialExpressionTypes[value],
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	}
	if (normalExpressionTypes.has(value)) {
		const node = withSourceCodeInfo([
			NodeTypes.Builtin,
			value,
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	}
	const node = withSourceCodeInfo([
		NodeTypes.Sym,
		value,
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
function stringFromQuotedSymbol(value) {
	return value.substring(1, value.length - 1).replace(/(\\{2})|(\\')|\\(.)/g, (_, backslash, singleQuote, normalChar) => {
		if (backslash) return "\\";
		if (singleQuote) return "'";
		return `\\${normalChar}`;
	});
}
/**
* Extract the symbol name from any symbol node type.
* All symbol node types now store the string name directly.
*/
function getSymbolName(symbol) {
	return symbol[1];
}
/**
* Get the start position of an already-parsed AstNode as TokenDebugInfo,
* by looking it up in the source map. Used by binary-op parsing so the
* wrapping `Call` / `And` / `Or` / `Qq` node's range starts at the LEFT
* operand's first token rather than the operator (which would skew the
* range and break LS features that rely on full-expression spans).
* Returns undefined if the node isn't in the source map (typecheck-only
* or no-source-map parse).
*/
function nodeStartDebugInfo(node, ctx) {
	const sourceMap = ctx.sourceMap;
	if (!sourceMap) return void 0;
	const nodeId = node[node.length - 1];
	const pos = sourceMap.positions.get(nodeId);
	if (!pos) return void 0;
	return [pos.start[0], pos.start[1]];
}
function createNamedNormalExpressionNode(symbolNode, params, debugInfo, ctx, hints) {
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[
			symbolNode,
			params,
			hints
		],
		0
	], debugInfo, ctx);
	if (isBuiltinSymbolNode(symbolNode)) assertNumberOfParams(normalExpressions[symbolNode[1]].arity, node[1][1].length, ctx.resolveTokenDebugInfo(debugInfo));
	ctx.setNodeEnd(node[2]);
	return node;
}
function isAtExpressionEnd(ctx) {
	if (ctx.isAtEnd()) return true;
	const token = ctx.tryPeek();
	if (isOperatorToken(token)) return [
		";",
		",",
		":"
	].includes(token[1]);
	if (isReservedSymbolToken(token)) return [
		"else",
		"when",
		"while",
		"case",
		"let",
		"then",
		"end",
		"do"
	].includes(token[1]);
	return false;
}
function fromBinaryOperatorToNode(operator, symbolNode, left, right, debugInfo, ctx) {
	const operatorName = operator[1];
	switch (operatorName) {
		case "^":
		case "*":
		case "/":
		case "%":
		case "+":
		case "-":
		case "<<":
		case ">>":
		case ">>>":
		case "++":
		case "<":
		case "<=":
		case "≤":
		case ">":
		case ">=":
		case "≥":
		case "==":
		case "!=":
		case "&":
		case "xor":
		case "|": return createNamedNormalExpressionNode(symbolNode, [left, right], debugInfo, ctx);
		case "|>": {
			const node = withSourceCodeInfo([
				NodeTypes.Call,
				[
					right,
					[left],
					{ isPipe: true }
				],
				0
			], debugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		case "&&": {
			const node = withSourceCodeInfo([
				NodeTypes.And,
				[left, right],
				0
			], debugInfo, ctx);
			ctx.clearStructuralLeaf(right[2]);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		case "||": {
			const node = withSourceCodeInfo([
				NodeTypes.Or,
				[left, right],
				0
			], debugInfo, ctx);
			ctx.clearStructuralLeaf(right[2]);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		case "??": {
			const node = withSourceCodeInfo([
				NodeTypes.Qq,
				[left, right],
				0
			], debugInfo, ctx);
			ctx.clearStructuralLeaf(right[2]);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		/* v8 ignore next 12 */
		case ".":
		case "?.":
		case "?":
		case ";":
		case ":":
		case "@":
		case "=":
		case ",":
		case "->":
		case "...":
		case "!": throw new ParseError(`Unknown binary operator: ${operatorName}`, ctx.resolveTokenDebugInfo(debugInfo));
		/* v8 ignore next 2 */
		default: throw new ParseError(`Unknown binary operator: ${operatorName}`, ctx.resolveTokenDebugInfo(debugInfo));
	}
}
function getPrecedence(operatorSign, sourceCodeInfo) {
	switch (operatorSign) {
		case "^": return 12;
		case "*":
		case "/":
		case "%": return 11;
		case "+":
		case "-": return 10;
		case "<<":
		case ">>":
		case ">>>": return 9;
		case "++": return 8;
		case "<":
		case "<=":
		case "≤":
		case ">":
		case ">=":
		case "≥": return 7;
		case "==":
		case "!=": return 6;
		case "&":
		case "xor":
		case "|": return 5;
		case "&&":
		case "||":
		case "??": return 4;
		case "|>": return 2;
		/* v8 ignore next 2 */
		default: throw new ParseError(`Unknown binary operator: ${operatorSign}`, sourceCodeInfo);
	}
}
function isTrivia(token) {
	const type = token[0];
	return type === "Whitespace" || type === "SingleLineComment" || type === "MultiLineComment" || type === "Shebang";
}
/**
* Get the raw source text for a token. Most tokens store raw text in
* token[1], but a few strip their prefix during tokenization:
*   - Atom: value "ok" → raw ":ok"
*   - EffectName: value "my.eff" → raw "@my.eff"
*   - MacroPrefix: value "name" → raw "#name"
*/
function rawTokenText(token) {
	switch (token[0]) {
		case "Atom": return `:${token[1]}`;
		case "EffectName": return `@${token[1]}`;
		case "MacroPrefix": return `#${token[1]}`;
		default: return token[1];
	}
}
function toTriviaNode(token) {
	switch (token[0]) {
		case "Whitespace": return {
			kind: "whitespace",
			text: token[1]
		};
		case "SingleLineComment": return {
			kind: "lineComment",
			text: token[1]
		};
		case "MultiLineComment": return {
			kind: "blockComment",
			text: token[1]
		};
		case "Shebang": return {
			kind: "shebang",
			text: token[1]
		};
		default: throw new Error(`Not a trivia token: ${token[0]}`);
	}
}
/**
* Split a trivia sequence at the first newline boundary.
*
* Everything up to and including the first `\n` character goes into
* `trailing`. Everything after goes into `leading`. If a single trivia
* node spans the boundary (e.g. whitespace "  \n  "), it is split into
* two nodes at the newline.
*
* If no newline exists in the trivia, everything is trailing (same-line).
*/
function splitTriviaAtNewline(trivia) {
	for (let i = 0; i < trivia.length; i++) {
		const t = trivia[i];
		const nlIndex = t.text.indexOf("\n");
		if (nlIndex === -1) continue;
		const before = t.text.substring(0, nlIndex + 1);
		const after = t.text.substring(nlIndex + 1);
		const trailing = trivia.slice(0, i);
		if (before) trailing.push({
			kind: t.kind,
			text: before
		});
		const leading = [];
		if (after) leading.push({
			kind: t.kind,
			text: after
		});
		leading.push(...trivia.slice(i + 1));
		return {
			trailing,
			leading
		};
	}
	return {
		trailing: [...trivia],
		leading: []
	};
}
var ParserContext = class {
	tokens;
	position;
	parseExpression;
	sourceMap;
	/** Full source text (available in debug mode). */
	source;
	/** File path of the source (available in debug mode). */
	filePath;
	/** Allocates a unique node ID — provided by the caller to scope uniqueness per instance. */
	allocateId;
	/** Type annotations collected during parsing, keyed by nodeId. */
	typeAnnotations = /* @__PURE__ */ new Map();
	/** Effect declarations: effect @name(ArgType) -> RetType */
	effectDeclarations = /* @__PURE__ */ new Map();
	/** Type alias declarations: type Name<Params> = TypeExpr */
	typeAliases = /* @__PURE__ */ new Map();
	/**
	* Binding-scoped type parameters from `let f<T: U> = ...` (Phase 0b).
	* Keyed by the binding-target nodeId; the typechecker reads this
	* side-table when inferring a let binding and registers the bounded
	* type vars for the whole RHS scope. Empty by default — the map only
	* holds entries for let-bindings that actually declared type params.
	*/
	typeParams = /* @__PURE__ */ new Map();
	/** When present, the parser operates in CST mode on the full token stream. */
	builder;
	/** Leading trivia accumulated for the next token to be consumed. */
	pendingLeadingTrivia = [];
	/** Position of the last consumed non-trivia token (for setNodeEnd in CST mode). */
	lastNonTriviaPosition = -1;
	constructor(tokenStream, allocateId, builder) {
		this.allocateId = allocateId;
		this.tokens = tokenStream.tokens;
		this.position = 0;
		this.source = tokenStream.source;
		this.filePath = tokenStream.filePath;
		this.builder = builder;
		if (tokenStream.source !== void 0) this.sourceMap = {
			sources: [{
				path: tokenStream.filePath ?? "<anonymous>",
				content: tokenStream.source
			}],
			positions: /* @__PURE__ */ new Map()
		};
		if (builder) this.skipTrivia();
	}
	allocateNodeId(debugInfo, structuralLeaf) {
		const id = this.allocateId();
		if (this.sourceMap && debugInfo) {
			const position = {
				source: 0,
				start: [debugInfo[0], debugInfo[1]],
				end: [debugInfo[0], debugInfo[1]],
				...structuralLeaf ? { structuralLeaf: true } : {}
			};
			this.sourceMap.positions.set(id, position);
		}
		return id;
	}
	/**
	* Update the end position of a node after it has been fully parsed.
	* Uses the debug info of the last consumed token (i.e. the token
	* just before the current position).
	*/
	setNodeEnd(nodeId) {
		if (!this.sourceMap) return;
		const pos = this.sourceMap.positions.get(nodeId);
		if (!pos) return;
		const lastPos = this.builder ? this.lastNonTriviaPosition : this.position - 1;
		const lastToken = this.tokens[lastPos];
		const debugInfo = lastToken?.[2];
		if (debugInfo) {
			const tokenLen = lastToken[1].length;
			pos.end = [debugInfo[0], debugInfo[1] + tokenLen];
		}
	}
	/**
	* Mark a node's source-map position as a structural leaf. Used when a parsed
	* node is unwrapped out of the final AST (e.g. a `do…end` block consumed as a
	* lambda body — its statements are kept, the block node is discarded). Its
	* position was already recorded by `allocateNodeId`, so coverage would count
	* it as a found-but-never-evaluated expression; flagging it structuralLeaf
	* drops it from coverage counting (the evaluator never fires onNodeEval for it
	* either), keeping the found/hit sides consistent.
	*/
	markStructuralLeaf(nodeId) {
		const pos = this.sourceMap?.positions.get(nodeId);
		if (pos) pos.structuralLeaf = true;
	}
	/**
	* Clear a node's structural-leaf flag, making it a first-class coverable unit.
	* Used for the arms of conditional constructs (`if`/`match`/`&&`/`||`/`??`): a
	* bare-symbol arm like `else acc` runs only when its branch is taken, so — unlike
	* an unconditional leaf — it carries real coverage signal. Clearing the flag puts
	* it in the coverage "found" set, so it shows uncovered (red) when its branch is
	* never taken and covered (green) when it is (the evaluator records it via the
	* `forceRecord` branch-arm path). No-op for non-leaf arms.
	*/
	clearStructuralLeaf(nodeId) {
		const pos = this.sourceMap?.positions.get(nodeId);
		if (pos) delete pos.structuralLeaf;
	}
	advance() {
		if (this.builder) this.advanceCst();
		else this.position += 1;
	}
	/**
	* CST-mode advance: consume the current non-trivia token, collect trivia,
	* create a CstToken, and feed it to the builder.
	*/
	advanceCst() {
		const token = this.tokens[this.position];
		const cstToken = {
			leadingTrivia: this.pendingLeadingTrivia,
			text: rawTokenText(token),
			trailingTrivia: []
		};
		this.lastNonTriviaPosition = this.position;
		this.position += 1;
		this.skipTrivia();
		const collected = this.pendingLeadingTrivia;
		if (collected.length > 0) {
			const { trailing, leading } = splitTriviaAtNewline(collected);
			cstToken.trailingTrivia = trailing;
			this.pendingLeadingTrivia = leading;
		} else {
			cstToken.trailingTrivia = [];
			this.pendingLeadingTrivia = [];
		}
		this.builder.token(cstToken);
	}
	/**
	* Skip trivia tokens from current position, accumulating them in
	* pendingLeadingTrivia. After this call, position points at the
	* next non-trivia token (or past end).
	*/
	skipTrivia() {
		const trivia = [];
		while (this.position < this.tokens.length && isTrivia(this.tokens[this.position])) {
			trivia.push(toTriviaNode(this.tokens[this.position]));
			this.position += 1;
		}
		this.pendingLeadingTrivia = trivia;
	}
	tryPeek() {
		return this.tokens[this.position];
	}
	peek() {
		const token = this.tokens[this.position];
		/* v8 ignore next 4 */
		if (!token) {
			const lastToken = this.tokens.at(-1);
			throw new ParseError("Unexpected end of input", this.resolveTokenDebugInfo(lastToken?.[2]));
		}
		return token;
	}
	isAtEnd() {
		return this.position >= this.tokens.length;
	}
	/**
	* Get the raw token debug info [line, column] for the current (or last) token.
	* Use this when passing to withSourceCodeInfo / allocateNodeId.
	*/
	peekDebugInfo() {
		const currentToken = this.tryPeek();
		return currentToken ? currentToken[2] : this.tokens.at(-1)?.[2];
	}
	/**
	* Get SourceCodeInfo for the current (or last) token position.
	* Constructs a full SourceCodeInfo from the token's [line, column] + source text.
	* Use this for error reporting (DvalaError).
	*/
	peekSourceCodeInfo() {
		return this.resolveTokenDebugInfo(this.peekDebugInfo());
	}
	/** Convert token debug info to SourceCodeInfo using stored source text. */
	resolveTokenDebugInfo(debugInfo) {
		return debugInfoToSourceCodeInfo(debugInfo, this.source, this.filePath);
	}
	peekAhead(count) {
		if (this.builder) {
			let skipped = 0;
			let offset = 1;
			while (this.position + offset < this.tokens.length) {
				const token = this.tokens[this.position + offset];
				if (!isTrivia(token)) {
					skipped += 1;
					if (skipped === count) return token;
				}
				offset += 1;
			}
			return;
		}
		return this.tokens[this.position + count];
	}
	getPosition() {
		return this.position;
	}
	getTokenAt(pos) {
		return this.tokens[pos];
	}
	/**
	* Get any remaining trivia that hasn't been attached to a token yet.
	* Called after parsing is complete to get file-trailing trivia.
	* Only meaningful in CST mode.
	*/
	getRemainingTrivia() {
		return this.pendingLeadingTrivia;
	}
};
function parseDo(ctx) {
	ctx.builder?.startNode("Block");
	const token = asReservedSymbolToken(ctx.tryPeek(), "do");
	ctx.advance();
	const expressions = [];
	while (!ctx.isAtEnd() && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		if (isReservedSymbolToken(ctx.tryPeek(), "with")) {
			const withNode = parseWithHandler(ctx);
			expressions.push(withNode);
			break;
		}
		expressions.push(ctx.parseExpression());
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!isReservedSymbolToken(ctx.tryPeek(), "end")) throw new ParseError("Expected end", ctx.peekSourceCodeInfo());
	}
	if (expressions.length === 0) throw new ParseError("Empty block is not allowed — every block must produce a value", ctx.peekSourceCodeInfo());
	assertReservedSymbolToken(ctx.tryPeek(), "end");
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Block,
		expressions,
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* Parse `with <expr>; <body>` inside a do...end block.
*
* Consumes `with`, parses the handler expression, consumes `;`,
* then collects remaining expressions until `end` as the handler's body.
* The handler is active for the body (like `let` scoping).
*/
function parseWithHandler(ctx) {
	const token = asReservedSymbolToken(ctx.tryPeek(), "with");
	ctx.advance();
	const handlerExpr = ctx.parseExpression();
	if (!isOperatorToken(ctx.tryPeek(), ";")) throw new ParseError("Expected \";\" after with handler expression", ctx.peekSourceCodeInfo());
	ctx.advance();
	const bodyExprs = [];
	while (!ctx.isAtEnd() && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		if (isReservedSymbolToken(ctx.tryPeek(), "with")) {
			bodyExprs.push(parseWithHandler(ctx));
			break;
		}
		bodyExprs.push(ctx.parseExpression());
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!isReservedSymbolToken(ctx.tryPeek(), "end")) throw new ParseError("Expected \";\" or \"end\"", ctx.peekSourceCodeInfo());
	}
	const node = withSourceCodeInfo([
		NodeTypes.WithHandler,
		[handlerExpr, bodyExprs],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
/**
* Collect a type annotation string from the token stream.
* Reads tokens until a delimiter is found: `=`, `,`, `)`, `->`, `;`, or `end`.
* Handles balanced parens/brackets/braces so nested types work.
*
* Used by parseBindingTarget (for `let x: T = ...` and `(a: T) -> ...`)
* and parseLambdaFunction (for `(a): T -> ...`).
*/
/**
* Is the token a right-angle-bracket-family operator (`>`, `>>`, `>>>`)?
* The tokenizer emits `>>` and `>>>` as single tokens for the shift
* operators, but inside type annotations these characters represent
* stacked closers for nested generics (`Box<Array<Number>>` → `>>`).
* The collector splits them back into individual `>` characters so
* angle-depth tracking and the stopAtGt delimiter behave correctly.
*/
function rightAngleRunLength(token) {
	if (!token) return 0;
	if (isOperatorToken(token, ">")) return 1;
	if (isOperatorToken(token, ">>")) return 2;
	if (isOperatorToken(token, ">>>")) return 3;
	return 0;
}
function collectTypeAnnotation(ctx, options) {
	const parts = [];
	let depth = 0;
	let angleDepth = 0;
	const braceStack = [];
	const stopAtArrow = options?.stopAtArrow ?? false;
	const stopAtRParen = options?.stopAtRParen ?? true;
	const stopAtGt = options?.stopAtGt ?? false;
	const inRefinementBrace = () => braceStack[braceStack.length - 1] === "refinement";
	while (!ctx.isAtEnd()) {
		const token = ctx.tryPeek();
		if (!token) break;
		const gtRun = rightAngleRunLength(token);
		if (gtRun > 0 && !inRefinementBrace()) {
			let consumed = 0;
			while (consumed < gtRun) {
				if (depth === 0 && angleDepth === 0 && stopAtGt) break;
				if (angleDepth > 0) angleDepth--;
				parts.push(">");
				consumed++;
			}
			if (consumed === gtRun) {
				ctx.advance();
				continue;
			}
			const remaining = gtRun - consumed;
			token[1] = ">".repeat(remaining);
			break;
		}
		if (depth === 0 && angleDepth === 0) {
			if (isOperatorToken(token, "=")) break;
			if (isOperatorToken(token, ",")) break;
			if (stopAtArrow && isOperatorToken(token, "->")) break;
			if (isOperatorToken(token, ";")) break;
			if (stopAtRParen && isRParenToken(token)) break;
			if (isReservedSymbolToken(token, "end")) break;
		}
		if (token[0] === "LParen" || token[0] === "LBracket") depth++;
		if (token[0] === "LBrace") {
			depth++;
			const next = ctx.peekAhead(1);
			const after = ctx.peekAhead(2);
			const isRefinement = next !== void 0 && (isSymbolToken(next) || isReservedSymbolToken(next)) && after !== void 0 && isOperatorToken(after, "|");
			braceStack.push(isRefinement ? "refinement" : "other");
		}
		if (token[0] === "RParen" || token[0] === "RBracket") depth--;
		if (token[0] === "RBrace") {
			depth--;
			braceStack.pop();
		}
		if (isOperatorToken(token, "<") && !inRefinementBrace()) angleDepth++;
		if (token[0] === "Atom") parts.push(`:${token[1]}`);
		else if (token[0] === "EffectName") parts.push(`@${token[1]}`);
		else parts.push(token[1]);
		ctx.advance();
	}
	return parts.join(" ").trim();
}
/**
* Check if the current token is `:` indicating a type annotation.
* Only valid in the symbol binding case (not inside object patterns,
* where `:` means nested destructuring — that's handled separately).
*/
function isTypeAnnotationColon(ctx) {
	return isOperatorToken(ctx.tryPeek(), ":");
}
/**
* Parse an effect declaration: effect @name(ArgType) -> RetType
*
* Effect declarations are type-only — they tell the typechecker what
* types flow through perform() and resume() for a given effect.
* They produce a null AST node at runtime (erased).
*
* Syntax: effect @name(ArgType) -> RetType
* Example: effect @log(String) -> Null
*/
function parseEffectDeclaration(ctx) {
	const token = ctx.peek();
	ctx.advance();
	const effectToken = ctx.peek();
	if (!isEffectNameToken(effectToken)) throw new ParseError("Expected effect name after \"effect\"", ctx.peekSourceCodeInfo());
	const effectName = effectToken[1];
	ctx.advance();
	if (!isLParenToken(ctx.peek())) throw new ParseError("Expected \"(\" after effect name", ctx.peekSourceCodeInfo());
	ctx.advance();
	const argType = collectTypeAnnotation(ctx);
	if (!argType) throw new ParseError("Expected argument type in effect declaration", ctx.peekSourceCodeInfo());
	if (!isRParenToken(ctx.peek())) throw new ParseError("Expected \")\" after argument type", ctx.peekSourceCodeInfo());
	ctx.advance();
	assertOperatorToken(ctx.peek(), "->");
	ctx.advance();
	const retType = collectTypeAnnotation(ctx);
	if (!retType) throw new ParseError("Expected return type in effect declaration", ctx.peekSourceCodeInfo());
	ctx.effectDeclarations.set(effectName, {
		argType,
		retType
	});
	return withSourceCodeInfo([
		NodeTypes.Reserved,
		"null",
		0
	], token[2], ctx);
}
function parseSymbol(ctx) {
	ctx.builder?.startNode("Symbol");
	const token = ctx.peek();
	ctx.advance();
	if (!isSymbolToken(token)) throw new ParseError(`Expected symbol token, got ${token[0]}`, ctx.resolveTokenDebugInfo(token[2]));
	if (token[1][0] === "'") {
		const node = stringToSymbolNode(stringFromQuotedSymbol(token[1]), token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	} else {
		const node = stringToSymbolNode(token[1], token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
}
function parseString(ctx, token) {
	ctx.builder?.startNode("StringLiteral");
	ctx.advance();
	const value = token[1].substring(1, token[1].length - 1).replace(/(\\{2})|(\\")|(\\n)|(\\t)|(\\r)|(\\b)|(\\f)|\\(.)/g, (_, backslash, doubleQuote, newline, tab, carriageReturn, backspace, formFeed, normalChar) => {
		if (backslash) return "\\";
		else if (newline) return "\n";
		else if (tab) return "	";
		else if (carriageReturn) return "\r";
		else if (backspace) return "\b";
		else if (formFeed) return "\f";
		else if (doubleQuote) return "\"";
		return normalChar;
	});
	const node = withSourceCodeInfo([
		NodeTypes.Str,
		value,
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseNumber(ctx) {
	ctx.builder?.startNode("NumberLiteral");
	const token = ctx.peek();
	ctx.advance();
	const value = token[1];
	const negative = value[0] === "-";
	const numberString = (negative ? value.substring(1) : value).replace(/_/g, "");
	const node = withSourceCodeInfo([
		NodeTypes.Num,
		negative ? -Number(numberString) : Number(numberString),
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function minifyTokenStream$1(tokenStream, { removeWhiteSpace }) {
	const tokens = tokenStream.tokens.filter((token) => {
		if (isSingleLineCommentToken(token) || isMultiLineCommentToken(token) || isShebangToken(token) || removeWhiteSpace && isWhitespaceToken(token)) return false;
		return true;
	});
	return {
		...tokenStream,
		tokens
	};
}
/** Count newline characters in a string. */
function countNewlines(s) {
	let count = 0;
	for (let i = 0; i < s.length; i++) if (s[i] === "\n") count++;
	return count;
}
/**
* Scan from `start` inside a `${...}` interpolation until the matching `}`.
* Returns the expression source text (without the outer `${` and `}`) and
* the number of characters consumed (including the closing `}`).
*/
function scanExpression(raw, start) {
	let i = start;
	let expr = "";
	let depth = 1;
	while (i < raw.length && depth > 0) {
		const c = raw[i];
		if (c === "{") {
			depth++;
			expr += c;
			i++;
		} else if (c === "}") {
			depth--;
			if (depth > 0) expr += c;
			i++;
		} else if (c === "\"") {
			const { str, consumed } = scanString(raw, i);
			expr += str;
			i += consumed;
		} else if (c === "'") {
			const { str, consumed } = scanQuotedSymbol(raw, i);
			expr += str;
			i += consumed;
		} else if (c === "`") {
			const { str, consumed } = scanNestedTemplate(raw, i);
			expr += str;
			i += consumed;
		} else {
			expr += c;
			i++;
		}
	}
	return {
		expr,
		consumed: i - start
	};
}
function scanString(raw, start) {
	let i = start + 1;
	let str = "\"";
	let escaping = false;
	while (i < raw.length) {
		const c = raw[i];
		str += c;
		i++;
		if (escaping) escaping = false;
		else if (c === "\\") escaping = true;
		else if (c === "\"") break;
	}
	return {
		str,
		consumed: i - start
	};
}
function scanQuotedSymbol(raw, start) {
	let i = start + 1;
	let str = "'";
	let escaping = false;
	while (i < raw.length) {
		const c = raw[i];
		str += c;
		i++;
		if (escaping) escaping = false;
		else if (c === "\\") escaping = true;
		else if (c === "'") break;
	}
	return {
		str,
		consumed: i - start
	};
}
/**
* Scan a full nested template string starting at `start` (pointing at the opening backtick).
* Handles ${...} spans inside the template recursively.
*/
function scanNestedTemplate(raw, start) {
	let i = start + 1;
	let str = "`";
	while (i < raw.length) {
		const c = raw[i];
		if (c === "`") {
			str += c;
			i++;
			break;
		} else if (c === "$" && raw[i + 1] === "{") {
			str += "${";
			i += 2;
			const { expr, consumed } = scanExpression(raw, i);
			str += `${expr}}`;
			i += consumed;
		} else {
			str += c;
			i++;
		}
	}
	return {
		str,
		consumed: i - start
	};
}
/**
* Split the raw content of a template string (between the surrounding backticks)
* into alternating literal and expression segments.
*/
function splitSegments(raw) {
	const segments = [];
	let i = 0;
	let literal = "";
	while (i < raw.length) if (raw[i] === "$" && raw[i + 1] === "{") {
		if (literal.length > 0 && literal[literal.length - 1] === "$") {
			let dollarCount = 1;
			while (literal.length >= dollarCount && literal[literal.length - dollarCount] === "$") dollarCount++;
			dollarCount--;
			literal = literal.slice(0, -dollarCount);
			if (literal.length > 0) {
				segments.push({
					type: "literal",
					value: literal
				});
				literal = "";
			}
			i += 2;
			const exprOffset = i;
			const { expr, consumed } = scanExpression(raw, i);
			i += consumed;
			segments.push({
				type: "deferred",
				value: expr,
				dollarCount: dollarCount + 1,
				offset: exprOffset
			});
			continue;
		}
		if (literal.length > 0) {
			segments.push({
				type: "literal",
				value: literal
			});
			literal = "";
		}
		i += 2;
		const exprOffset = i;
		const { expr, consumed } = scanExpression(raw, i);
		i += consumed;
		segments.push({
			type: "expression",
			value: expr,
			offset: exprOffset
		});
	} else {
		literal += raw[i];
		i++;
	}
	if (literal.length > 0) segments.push({
		type: "literal",
		value: literal
	});
	return segments;
}
function parseTemplateString(ctx, token) {
	ctx.builder?.startNode("TemplateString");
	ctx.advance();
	const debugInfo = token[2];
	const resolvedSci = ctx.resolveTokenDebugInfo(debugInfo);
	const raw = token[1].slice(1, -1);
	const segments = splitSegments(raw);
	if (segments.length === 0) {
		const node = withSourceCodeInfo([
			NodeTypes.Str,
			"",
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	if (segments.length === 1 && segments[0].type === "literal") {
		const node = withSourceCodeInfo([
			NodeTypes.Str,
			segments[0].value,
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	const segmentNodes = [];
	for (const segment of segments) if (segment.type === "literal") {
		if (segment.value.length === 0) continue;
		segmentNodes.push(withSourceCodeInfo([
			NodeTypes.Str,
			segment.value,
			0
		], debugInfo, ctx));
	} else {
		if (segment.value.trim().length === 0) throw new ParseError("Empty interpolation in template string", resolvedSci);
		const hasDebug = ctx.sourceMap !== void 0;
		const minified = minifyTokenStream$1(tokenize(segment.value, hasDebug, resolvedSci?.filePath), { removeWhiteSpace: true });
		for (const t of minified.tokens) if (t[0] === "Error") throw new ParseError(`Template string interpolation error: ${t[3]}`, resolvedSci);
		const innerCtx = createParserContext(minified, ctx.allocateId);
		const expr = parseExpression(innerCtx, 0);
		if (ctx.sourceMap && innerCtx.sourceMap && debugInfo) {
			const rawBefore = raw.substring(0, segment.offset);
			const newlinesBefore = countNewlines(rawBefore);
			const lastNewlineIdx = rawBefore.lastIndexOf("\n");
			const segStartLine = debugInfo[0] + newlinesBefore;
			const segStartCol = newlinesBefore === 0 ? debugInfo[1] + 1 + segment.offset : segment.offset - lastNewlineIdx - 1;
			for (const [nodeId, innerPos] of innerCtx.sourceMap.positions) ctx.sourceMap.positions.set(nodeId, {
				source: 0,
				start: [segStartLine + innerPos.start[0], innerPos.start[0] === 0 ? segStartCol + innerPos.start[1] : innerPos.start[1]],
				end: [segStartLine + innerPos.end[0], innerPos.end[0] === 0 ? segStartCol + innerPos.end[1] : innerPos.end[1]],
				...innerPos.structuralLeaf ? { structuralLeaf: true } : {}
			});
		}
		segmentNodes.push(expr);
	}
	const node = withSourceCodeInfo([
		NodeTypes.TmplStr,
		segmentNodes,
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* Convert any symbol to a Sym for use in binding targets.
* Normal builtins (map, filter, etc.) are allowed — they can be shadowed.
* Special expressions (if, let, for, etc.) cannot be used as variable names.
*/
function toUserDefinedSymbol(symbol, debugInfo, ctx) {
	if (isSpecialSymbolNode(symbol)) throw new ParseError("Expected user defined symbol", ctx.resolveTokenDebugInfo(debugInfo));
	if (isUserDefinedSymbolNode(symbol)) return symbol;
	const name = getSymbolName(symbol);
	return withSourceCodeInfo([
		NodeTypes.Sym,
		name,
		0
	], debugInfo, ctx);
}
function parseBindingTarget(ctx, { requireDefaultValue, noRest, allowLiteralPatterns, stopTypeAnnotationAtRParen, allowTypeParams } = {}) {
	const firstToken = ctx.tryPeek();
	if (allowLiteralPatterns && isReservedSymbolToken(firstToken, "_")) {
		ctx.advance();
		const target = withSourceCodeInfo([
			bindingTargetTypes.wildcard,
			[],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		return target;
	}
	if (allowLiteralPatterns && isLiteralToken(firstToken)) {
		if (isNumberToken(firstToken) || isBasePrefixedNumberToken(firstToken)) {
			const node = parseNumber(ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isTemplateStringToken(firstToken)) {
			const node = parseTemplateString(ctx, firstToken);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isStringToken(firstToken)) {
			const node = parseString(ctx, firstToken);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isAtomToken(firstToken)) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Atom,
				firstToken[1],
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isReservedSymbolToken(firstToken, "true")) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Reserved,
				"true",
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isReservedSymbolToken(firstToken, "false")) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Reserved,
				"false",
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		/* v8 ignore next 7 */
		if (isReservedSymbolToken(firstToken, "null")) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Reserved,
				"null",
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
	}
	if (isSymbolToken(firstToken)) {
		const symbol = toUserDefinedSymbol(parseSymbol(ctx), firstToken[2], ctx);
		let typeParams;
		if (allowTypeParams && isOperatorToken(ctx.tryPeek(), "<")) typeParams = parseBindingTypeParams(ctx);
		let typeAnnotation;
		if (isTypeAnnotationColon(ctx)) {
			ctx.advance();
			typeAnnotation = collectTypeAnnotation(ctx, { stopAtRParen: stopTypeAnnotationAtRParen ?? false });
			if (!typeAnnotation) throw new ParseError("Expected type after \":\"", ctx.peekSourceCodeInfo());
		}
		const defaultValue = parseOptionalDefaulValue(ctx);
		if (requireDefaultValue && !defaultValue) throw new ParseError("Expected assignment", ctx.peekSourceCodeInfo());
		const target = withSourceCodeInfo([
			bindingTargetTypes.symbol,
			[symbol, defaultValue],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		if (typeAnnotation) ctx.typeAnnotations.set(target[2], typeAnnotation);
		if (typeParams) ctx.typeParams.set(target[2], typeParams);
		return target;
	}
	if (isOperatorToken(firstToken, "...")) {
		if (noRest) throw new ParseError("Rest element not allowed", ctx.resolveTokenDebugInfo(firstToken[2]));
		ctx.advance();
		const symbol = toUserDefinedSymbol(parseSymbol(ctx), firstToken[2], ctx);
		if (isOperatorToken(ctx.tryPeek(), "=")) throw new ParseError("Rest argument can not have default value", ctx.peekSourceCodeInfo());
		const target = withSourceCodeInfo([
			bindingTargetTypes.rest,
			[symbol[1], void 0],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		return target;
	}
	if (isLBracketToken(firstToken)) {
		ctx.advance();
		const elements = [];
		let token = ctx.peek();
		let rest = false;
		while (!isRBracketToken(token)) {
			if (rest) throw new ParseError("Rest argument must be last", ctx.resolveTokenDebugInfo(token[2]));
			if (isOperatorToken(token, ",")) {
				elements.push(null);
				ctx.advance();
				token = ctx.peek();
				continue;
			}
			const target = parseBindingTarget(ctx, { allowLiteralPatterns });
			if (target[0] === bindingTargetTypes.rest) rest = true;
			elements.push(target);
			token = ctx.peek();
			if (!isRBracketToken(token)) {
				assertOperatorToken(token, ",");
				ctx.advance();
			}
			token = ctx.peek();
		}
		ctx.advance();
		const target = withSourceCodeInfo([
			bindingTargetTypes.array,
			[elements, void 0],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		const defaultValue = parseOptionalDefaulValue(ctx);
		if (requireDefaultValue && !defaultValue) throw new ParseError("Expected assignment", ctx.peekSourceCodeInfo());
		target[1][1] = defaultValue;
		return target;
	}
	if (isLBraceToken(firstToken)) {
		ctx.advance();
		const elements = [];
		const seenKeys = /* @__PURE__ */ new Set();
		let token = ctx.peek();
		let rest = false;
		while (!isRBraceToken(token)) {
			if (rest) throw new ParseError("Rest argument must be last", ctx.resolveTokenDebugInfo(token[2]));
			if (isOperatorToken(token, "...")) {
				rest = true;
				ctx.advance();
			}
			const keySymbol = parseSymbol(ctx);
			const keyName = getSymbolName(keySymbol);
			const keyNodeId = keySymbol[2] ?? 0;
			const keyDebugInfo = keySymbol[2] !== void 0 ? ctx.sourceMap?.positions.get(keySymbol[2]) : void 0;
			const keyTokenDebug = keyDebugInfo ? [keyDebugInfo.start[0], keyDebugInfo.start[1]] : void 0;
			token = ctx.peek();
			const assertUniqueKey = (errToken) => {
				if (seenKeys.has(keyName)) throw new ParseError(`Duplicate binding name: ${keyName}`, ctx.resolveTokenDebugInfo(errToken));
				seenKeys.add(keyName);
			};
			if (isReservedSymbolToken(token, "as")) {
				if (rest) throw new ParseError("Rest argument can not have alias", ctx.resolveTokenDebugInfo(token[2]));
				ctx.advance();
				const name = toUserDefinedSymbol(parseSymbol(ctx), token[2], ctx);
				assertUniqueKey(token[2]);
				const symTarget = withSourceCodeInfo([
					bindingTargetTypes.symbol,
					[name, parseOptionalDefaulValue(ctx)],
					0
				], token[2], ctx);
				ctx.setNodeEnd(symTarget[2]);
				elements.push({
					key: keyName,
					keyNodeId,
					target: symTarget
				});
			} else if (isRBraceToken(token) || isOperatorToken(token, ",") || isOperatorToken(token, "=")) {
				const key = toUserDefinedSymbol(keySymbol, keyTokenDebug, ctx);
				assertUniqueKey(token[2]);
				if (rest && isOperatorToken(ctx.tryPeek(), "=")) throw new ParseError("Rest argument can not have default value", ctx.peekSourceCodeInfo());
				if (rest) {
					const restTarget = withSourceCodeInfo([
						bindingTargetTypes.rest,
						[key[1], parseOptionalDefaulValue(ctx)],
						0
					], keyTokenDebug, ctx);
					ctx.setNodeEnd(restTarget[2]);
					elements.push({
						key: key[1],
						keyNodeId,
						target: restTarget
					});
				} else {
					const symTarget = withSourceCodeInfo([
						bindingTargetTypes.symbol,
						[key, parseOptionalDefaulValue(ctx)],
						0
					], keyTokenDebug, ctx);
					ctx.setNodeEnd(symTarget[2]);
					elements.push({
						key: key[1],
						keyNodeId,
						target: symTarget
					});
				}
			} else if (isOperatorToken(token, ":")) {
				ctx.advance();
				token = ctx.peek();
				if (allowLiteralPatterns) {
					if (!isLBraceToken(token) && !isLBracketToken(token) && !isLiteralToken(token)) throw new ParseError("Expected literal, object or array pattern", ctx.resolveTokenDebugInfo(token[2]));
				} else if (!isLBraceToken(token) && !isLBracketToken(token)) throw new ParseError("Expected object or array", ctx.resolveTokenDebugInfo(token[2]));
				assertUniqueKey(keyTokenDebug);
				elements.push({
					key: keyName,
					keyNodeId,
					target: parseBindingTarget(ctx, { allowLiteralPatterns })
				});
			}
			if (!isRBraceToken(ctx.peek())) {
				assertOperatorToken(ctx.peek(), ",");
				ctx.advance();
			}
			token = ctx.peek();
		}
		ctx.advance();
		const target = withSourceCodeInfo([
			bindingTargetTypes.object,
			[elements, void 0],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		token = ctx.peek();
		const defaultValue = parseOptionalDefaulValue(ctx);
		if (requireDefaultValue && !defaultValue) throw new ParseError("Expected assignment", ctx.resolveTokenDebugInfo(token[2]));
		target[1][1] = defaultValue;
		return target;
	}
	throw new ParseError("Expected symbol", ctx.peekSourceCodeInfo());
}
function parseOptionalDefaulValue(ctx) {
	if (isOperatorToken(ctx.tryPeek(), "=")) {
		ctx.advance();
		return ctx.parseExpression();
	}
}
/**
* Parse a binding-scoped type-parameter list `<T, U: Bound, ...>` after
* the symbol in a `let f<T> = ...` binding. Shares the shape with the
* type-alias generic-param parser in `parseTypeDeclaration.ts` — both
* emit `AliasParam[]` where each entry has a name and optional bound
* source text. The bound source is parsed lazily by the typechecker
* (same rationale as for alias bounds: avoid circular-parse dependencies
* when a bound references a name defined later in the same file).
*
* Duplicates (`<T, T>` or `<T: A, T: B>`) are rejected here so the
* typechecker downstream never sees an ambiguous type-var map.
*/
function parseBindingTypeParams(ctx) {
	ctx.advance();
	const params = [];
	const seen = /* @__PURE__ */ new Set();
	while (!ctx.isAtEnd()) {
		const paramToken = ctx.peek();
		if (!isSymbolToken(paramToken) && !isReservedSymbolToken(paramToken)) throw new ParseError("Expected type parameter name", ctx.peekSourceCodeInfo());
		const paramName = paramToken[1];
		if (seen.has(paramName)) throw new ParseError(`Duplicate type parameter '${paramName}' in generic parameter list`, ctx.peekSourceCodeInfo());
		seen.add(paramName);
		ctx.advance();
		let bound;
		if (isOperatorToken(ctx.tryPeek(), ":")) {
			ctx.advance();
			const boundExpr = collectTypeAnnotation(ctx, { stopAtGt: true });
			if (!boundExpr) throw new ParseError(`Expected bound type after ":" for parameter "${paramName}"`, ctx.peekSourceCodeInfo());
			bound = boundExpr;
		}
		params.push(bound === void 0 ? { name: paramName } : {
			name: paramName,
			bound
		});
		if (isOperatorToken(ctx.tryPeek(), ",")) {
			ctx.advance();
			continue;
		}
		break;
	}
	if (!isOperatorToken(ctx.tryPeek(), ">")) throw new ParseError("Expected \">\" after type parameters", ctx.peekSourceCodeInfo());
	ctx.advance();
	return params;
}
function isLiteralToken(token) {
	return isNumberToken(token) || isBasePrefixedNumberToken(token) || isStringToken(token) || isTemplateStringToken(token) || isAtomToken(token) || isReservedSymbolToken(token, "true") || isReservedSymbolToken(token, "false") || isReservedSymbolToken(token, "null");
}
function parseLet(ctx, token) {
	ctx.builder?.startNode("Let");
	ctx.advance();
	const target = parseBindingTarget(ctx, {
		requireDefaultValue: true,
		noRest: true,
		allowTypeParams: true
	});
	const value = target[1][1];
	target[1][1] = void 0;
	const node = withSourceCodeInfo([
		NodeTypes.Let,
		[target, value],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseForOrDoseq(ctx, firstToken) {
	ctx.builder?.startNode("For");
	ctx.advance();
	assertLParenToken(ctx.tryPeek());
	ctx.advance();
	const forLoopBindings = [];
	while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
		const loopBinding = parseForLoopBinding(ctx);
		const existingBoundNames = forLoopBindings.flatMap((b) => Object.keys(getAllBindingTargetNames(b[0][0])));
		const newBoundNames = getAllBindingTargetNames(loopBinding[0][0]);
		if (Object.keys(newBoundNames).some((n) => existingBoundNames.includes(n))) throw new ParseError("Duplicate binding", void 0);
		forLoopBindings.push(loopBinding);
		if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
	}
	assertRParenToken(ctx.tryPeek());
	ctx.advance();
	assertOperatorToken(ctx.tryPeek(), "->");
	ctx.advance();
	const expression = ctx.parseExpression();
	const node = withSourceCodeInfo([
		NodeTypes.For,
		[forLoopBindings, expression],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseForLoopBinding(ctx) {
	const bindingNode = parseBinding(ctx);
	const modifiers = [];
	let token = ctx.peek();
	assertInternalLoopBindingDelimiter(token, [
		"let",
		"when",
		"while"
	], ctx.peekSourceCodeInfo());
	const letBindings = [];
	if (token[1] === "let") {
		modifiers.push("&let");
		while (isSymbolToken(token, "let")) {
			const letNode = parseLet(ctx, token);
			const existingBoundNames = letBindings.flatMap((b) => Object.keys(getAllBindingTargetNames(b[0])));
			const [letTarget, letValue] = letNode[1];
			if (Object.keys(getAllBindingTargetNames(letTarget)).some((n) => existingBoundNames.includes(n))) throw new ParseError("Duplicate binding", void 0);
			letBindings.push([letTarget, letValue]);
			token = ctx.peek();
			assertInternalLoopBindingDelimiter(token, [
				"let",
				"when",
				"while"
			], ctx.peekSourceCodeInfo());
			token = ctx.peek();
		}
	}
	let whenNode;
	let whileNode;
	while (isReservedSymbolToken(token, "when") || isReservedSymbolToken(token, "while")) {
		ctx.advance();
		if (token[1] === "when") {
			modifiers.push("&when");
			whenNode = ctx.parseExpression();
		} else {
			modifiers.push("&while");
			whileNode = ctx.parseExpression();
		}
		token = ctx.peek();
		const symbols = modifiers.includes("&when") && modifiers.includes("&while") ? [] : modifiers.includes("&when") ? ["while"] : ["when"];
		assertInternalLoopBindingDelimiter(token, symbols, ctx.peekSourceCodeInfo());
		token = ctx.peek();
	}
	assertInternalLoopBindingDelimiter(token, [], ctx.peekSourceCodeInfo());
	return [
		bindingNode,
		letBindings,
		whenNode,
		whileNode
	];
}
function parseBinding(ctx) {
	const firstToken = asSymbolToken(ctx.tryPeek());
	const name = asUserDefinedSymbolNode(parseSymbol(ctx));
	assertReservedSymbolToken(ctx.tryPeek(), "in");
	ctx.advance();
	const value = ctx.parseExpression();
	const target = withSourceCodeInfo([
		bindingTargetTypes.symbol,
		[name, void 0],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(target[2]);
	return [target, value];
}
function assertInternalLoopBindingDelimiter(token, symbols, sourceCodeInfo) {
	if (!isInternalLoopBindingDelimiter(token, symbols)) throw new ParseError(`Expected symbol ${`${[...symbols, ","].map((symbol) => `"${symbol}"`).join(", ")} or ")"`}`, sourceCodeInfo);
}
function isInternalLoopBindingDelimiter(token, symbols) {
	if (isOperatorToken(token, ",") || isRParenToken(token)) return true;
	for (const symbol of symbols) {
		if (symbol === "let" && isSymbolToken(token, "let")) return true;
		if (["when", "while"].includes(symbol) && isReservedSymbolToken(token, symbol)) return true;
	}
	return false;
}
/**
* Parse a type alias declaration: type Name = TypeExpr
* Or with type parameters: type Name<A, B> = TypeExpr
*
* Type declarations are type-only — they tell the typechecker about
* named type aliases. They produce a null AST node at runtime (erased).
*
* Syntax:
*   type Nullable = Number | Null
*   type Pair = [Number, Number]
*   type Result<T, E> = {tag: :ok, value: T} | {tag: :error, error: E}
*/
function skipWhitespace(ctx) {
	while (!ctx.isAtEnd()) {
		if (ctx.peek()[0] !== "Whitespace") break;
		ctx.advance();
	}
}
function parseTypeDeclaration(ctx) {
	ctx.builder?.startNode("TypeAlias");
	try {
		return parseTypeDeclarationBody(ctx);
	} finally {
		ctx.builder?.endNode();
	}
}
function parseTypeDeclarationBody(ctx) {
	const token = ctx.peek();
	ctx.advance();
	const nameToken = ctx.peek();
	if (!isSymbolToken(nameToken)) throw new ParseError("Expected type name after \"type\"", ctx.peekSourceCodeInfo());
	const name = nameToken[1];
	ctx.advance();
	const params = [];
	const seenNames = /* @__PURE__ */ new Set();
	skipWhitespace(ctx);
	if (isOperatorToken(ctx.tryPeek(), "<")) {
		ctx.advance();
		while (!ctx.isAtEnd()) {
			skipWhitespace(ctx);
			const paramToken = ctx.peek();
			if (!isSymbolToken(paramToken) && !isReservedSymbolToken(paramToken)) throw new ParseError("Expected type parameter name", ctx.peekSourceCodeInfo());
			const paramName = paramToken[1];
			if (seenNames.has(paramName)) throw new ParseError(`Duplicate type parameter '${paramName}' in generic parameter list`, ctx.peekSourceCodeInfo());
			seenNames.add(paramName);
			ctx.advance();
			skipWhitespace(ctx);
			let bound;
			if (isOperatorToken(ctx.tryPeek(), ":")) {
				ctx.advance();
				const boundExpr = collectTypeAnnotation(ctx, { stopAtGt: true });
				if (!boundExpr) throw new ParseError(`Expected bound type after ":" for parameter "${paramName}"`, ctx.peekSourceCodeInfo());
				bound = boundExpr;
				skipWhitespace(ctx);
			}
			params.push(bound === void 0 ? { name: paramName } : {
				name: paramName,
				bound
			});
			if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
			else break;
		}
		skipWhitespace(ctx);
		if (!isOperatorToken(ctx.tryPeek(), ">")) throw new ParseError("Expected \">\" after type parameters", ctx.peekSourceCodeInfo());
		ctx.advance();
	}
	skipWhitespace(ctx);
	if (!isOperatorToken(ctx.tryPeek(), "=")) throw new ParseError("Expected \"=\" after type name", ctx.peekSourceCodeInfo());
	ctx.advance();
	const typeExpr = collectTypeAnnotation(ctx);
	if (!typeExpr) throw new ParseError("Expected type expression after \"=\"", ctx.peekSourceCodeInfo());
	ctx.typeAliases.set(name, {
		params,
		body: typeExpr
	});
	return withSourceCodeInfo([
		NodeTypes.Reserved,
		"null",
		0
	], token[2], ctx);
}
/**
* Parse `handler <clauses> [transform x -> expr] end`.
*
* `handler` and `transform` are contextual keywords (regular symbols),
* not reserved words — they can be used as variable names elsewhere.
* `end` is a reserved keyword as usual.
*
* Clauses: `@effect(params...) -> expr` — the `@effect` token starts each clause.
* No separators between clauses (like match cases).
* Duplicate effect names are a parse error.
* Transform: optional, `transform x -> expr` (or `transform x -> do...end`).
*/
function parseHandler(ctx, shallow = false, linear = false) {
	if (!shallow && !linear) ctx.builder?.startNode("Handler");
	const token = ctx.tryPeek();
	ctx.advance();
	const clauses = [];
	const seenEffects = /* @__PURE__ */ new Set();
	while (!ctx.isAtEnd() && !isSymbolToken(ctx.tryPeek(), "transform") && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		const clauseToken = ctx.tryPeek();
		if (!isEffectNameToken(clauseToken)) throw new ParseError("Expected effect clause (@effect) or \"transform\" or \"end\"", ctx.peekSourceCodeInfo());
		const effectName = clauseToken[1];
		ctx.advance();
		if (seenEffects.has(effectName)) throw new ParseError(`Duplicate handler clause for effect '@${effectName}'`, ctx.peekSourceCodeInfo());
		seenEffects.add(effectName);
		const params = [];
		if (isLParenToken(ctx.tryPeek())) {
			ctx.advance();
			while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
				params.push(parseBindingTarget(ctx, { stopTypeAnnotationAtRParen: true }));
				if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
			}
			if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected \")\"", ctx.peekSourceCodeInfo());
			ctx.advance();
		}
		if (!isOperatorToken(ctx.tryPeek(), "->")) throw new ParseError("Expected \"->\" after handler clause parameters", ctx.peekSourceCodeInfo());
		ctx.advance();
		let body;
		if (isReservedSymbolToken(ctx.tryPeek(), "do")) {
			const doNode = parseDo(ctx);
			body = doNode[1];
			ctx.markStructuralLeaf(doNode[2]);
		} else body = [ctx.parseExpression()];
		if (linear && containsResumeOutsideNestedHandler(body)) throw new ParseError("`resume` is not available in linear handler clauses — return the value to resume, or `perform(@dvala.error, msg)` to fail.", ctx.peekSourceCodeInfo());
		if (linear && body.length > 0) {
			const lastExpr = body[body.length - 1];
			body[body.length - 1] = withSourceCodeInfo([
				NodeTypes.Resume,
				lastExpr,
				0
			], token[2], ctx);
		}
		clauses.push({
			effectName,
			params,
			body
		});
	}
	let transform = null;
	if (isSymbolToken(ctx.tryPeek(), "transform")) {
		ctx.advance();
		const transformParam = parseBindingTarget(ctx);
		if (!isOperatorToken(ctx.tryPeek(), "->")) throw new ParseError("Expected \"->\" after transform parameter", ctx.peekSourceCodeInfo());
		ctx.advance();
		let transformBody;
		if (isReservedSymbolToken(ctx.tryPeek(), "do")) {
			const doNode = parseDo(ctx);
			transformBody = doNode[1];
			ctx.markStructuralLeaf(doNode[2]);
		} else transformBody = [ctx.parseExpression()];
		transform = [transformParam, transformBody];
	}
	if (!isReservedSymbolToken(ctx.tryPeek(), "end")) throw new ParseError("Expected \"end\" to close handler", ctx.peekSourceCodeInfo());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Handler,
		[
			clauses,
			transform,
			shallow,
			linear
		],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* True iff the parsed clause body uses the `resume` keyword anywhere that's
* still bound to *this* handler (rather than a nested one). The walk
* descends into all child AST nodes except nested `Handler` and `Macro`
* nodes, both of which rebind `resume` to themselves: a `resume` inside
* those bodies belongs to the inner handler/macro, not the outer linear
* one. Used by `parseHandler` to enforce the linear-handler contract from
* `design/active/2026-04-29_linear-handler.md` (Q6 / static check (a)).
*/
function containsResumeOutsideNestedHandler(body) {
	for (const node of body) if (containsResume(node)) return true;
	return false;
}
function containsResume(value) {
	if (Array.isArray(value)) {
		if (value.length === 3 && typeof value[0] === "string") {
			const type = value[0];
			if (type === NodeTypes.Resume) return true;
			if (type === NodeTypes.Handler || type === NodeTypes.Macro) return false;
			return containsResume(value[1]);
		}
		for (const child of value) if (containsResume(child)) return true;
		return false;
	}
	if (value && typeof value === "object") {
		for (const v of Object.values(value)) if (containsResume(v)) return true;
		return false;
	}
	return false;
}
function parseImplicitBlock(ctx, ends) {
	const nodes = [];
	while (!ctx.isAtEnd() && !isImplicitBlockEnd(ctx, ends)) if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
	else nodes.push(ctx.parseExpression());
	assertImplicitBlockEnd(ctx, ends);
	if (nodes.length === 0) throw new ParseError("Expected expression", ctx.peekSourceCodeInfo());
	if (nodes.length === 1) return nodes[0];
	const node = withSourceCodeInfo([
		NodeTypes.Block,
		nodes,
		0
	], ctx.peekDebugInfo(), ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
function assertImplicitBlockEnd(ctx, ends) {
	if (!isImplicitBlockEnd(ctx, ends)) throw new ParseError(`Expected ${ends.map((e) => e[1]).join(" or ")}`, ctx.peekSourceCodeInfo());
}
function isImplicitBlockEnd(ctx, ends) {
	for (const end of ends) if (isReservedSymbolToken(ctx.tryPeek(), end)) return true;
	return false;
}
function parseIf(ctx, token) {
	ctx.builder?.startNode("If");
	ctx.advance();
	const condition = ctx.parseExpression();
	assertReservedSymbolToken(ctx.tryPeek(), "then");
	ctx.advance();
	const thenExpression = parseImplicitBlock(ctx, ["else", "end"]);
	let elseExpression;
	if (isReservedSymbolToken(ctx.tryPeek(), "else")) {
		ctx.advance();
		if (isSymbolToken(ctx.tryPeek()) && ctx.tryPeek()[1] === "if") elseExpression = parseElseIf(ctx);
		else elseExpression = parseImplicitBlock(ctx, ["end"]);
	} else throw new ParseError("`if` without `else` is not allowed — use `when condition do expr end` for side effects, or add an `else` clause", ctx.peekSourceCodeInfo());
	ctx.advance();
	ctx.clearStructuralLeaf(thenExpression[2]);
	ctx.clearStructuralLeaf(elseExpression[2]);
	const node = withSourceCodeInfo([
		NodeTypes.If,
		[
			condition,
			thenExpression,
			elseExpression
		],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* Parse `if condition then body [else if ... | else ...]` without consuming `end`.
* Used by `else if` chains — the outermost `if` consumes the single shared `end`.
*/
function parseElseIf(ctx) {
	const token = ctx.tryPeek();
	ctx.advance();
	const condition = ctx.parseExpression();
	assertReservedSymbolToken(ctx.tryPeek(), "then");
	ctx.advance();
	const thenExpression = parseImplicitBlock(ctx, ["else", "end"]);
	let elseExpression;
	if (isReservedSymbolToken(ctx.tryPeek(), "else")) {
		ctx.advance();
		if (isSymbolToken(ctx.tryPeek()) && ctx.tryPeek()[1] === "if") elseExpression = parseElseIf(ctx);
		else elseExpression = parseImplicitBlock(ctx, ["end"]);
	} else throw new ParseError("`if` without `else` is not allowed — use `when condition do expr end` for side effects, or add an `else` clause", ctx.peekSourceCodeInfo());
	ctx.clearStructuralLeaf(thenExpression[2]);
	ctx.clearStructuralLeaf(elseExpression[2]);
	const node = withSourceCodeInfo([
		NodeTypes.If,
		[
			condition,
			thenExpression,
			elseExpression
		],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
function parseLoop(ctx, firstToken) {
	ctx.builder?.startNode("Loop");
	ctx.advance();
	assertLParenToken(ctx.tryPeek());
	ctx.advance();
	const bindings = [];
	let token = ctx.tryPeek();
	while (!ctx.isAtEnd() && !isRParenToken(token)) {
		const target = parseBindingTarget(ctx, {
			requireDefaultValue: true,
			noRest: true
		});
		const value = target[1][1];
		target[1][1] = void 0;
		bindings.push([target, value]);
		if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
		token = ctx.tryPeek();
	}
	if (bindings.length === 0) throw new ParseError("Expected binding", ctx.peekSourceCodeInfo());
	assertRParenToken(token);
	ctx.advance();
	assertOperatorToken(ctx.tryPeek(), "->");
	ctx.advance();
	const expression = ctx.parseExpression();
	const node = withSourceCodeInfo([
		NodeTypes.Loop,
		[bindings, expression],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
const placeholderRegexp = /^\$([1-9]\d?)?$/;
const maxShorthandLambdaArity = 20;
/**
* Reserved symbols that can NEVER start an expression — they are
* purely syntactic block-terminators or continuation keywords
* (`end` closes do/if/match/handler, `else`/`then` continue an `if`,
* `case` opens a match arm, etc.).
*
* Any of these appearing immediately after `->` means the user wrote
* an empty function body (the most common scenario is forgetting
* to supply a body before closing the enclosing construct). Runtime
* strictness Step 0a: reject this at parse time with a clear message
* rather than a later TypeError about "Reserved symbol cannot be
* evaluated." See `design/active/2026-04-12_type-system.md` § Step 0.
*/
const nonExpressionReservedSymbols = new Set([
	"end",
	"else",
	"then",
	"case",
	"in",
	"as",
	"_"
]);
function assertNonEmptyFunctionBody(ctx) {
	const token = ctx.peek();
	if (isReservedSymbolToken(token) && nonExpressionReservedSymbols.has(token[1])) throw new ParseError(`Empty function body — '${token[1]}' cannot start an expression. Provide a body expression or use \`do ... end\` for an explicit block.`, ctx.peekSourceCodeInfo());
}
function parseLambdaFunction(ctx) {
	ctx.builder?.startNode("Function");
	const firstToken = ctx.peek();
	const functionArguments = parseFunctionArguments(ctx);
	let returnTypeAnnotation;
	if (isOperatorToken(ctx.peek(), ":")) {
		ctx.advance();
		returnTypeAnnotation = collectTypeAnnotation(ctx, {
			stopAtArrow: true,
			stopAtRParen: false
		});
	}
	if (!isOperatorToken(ctx.peek(), "->")) throw new ParseError("Expected ->", ctx.peekSourceCodeInfo());
	ctx.advance();
	assertNonEmptyFunctionBody(ctx);
	let nodes;
	if (isReservedSymbolToken(ctx.peek(), "do")) {
		const doNode = parseDo(ctx);
		nodes = doNode[1];
		ctx.markStructuralLeaf(doNode[2]);
	} else nodes = [ctx.parseExpression()];
	const node = withSourceCodeInfo([
		NodeTypes.Function,
		[functionArguments, nodes],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	if (returnTypeAnnotation) ctx.typeAnnotations.set(node[2], `return:${returnTypeAnnotation}`);
	ctx.builder?.endNode();
	return node;
}
function parseFunctionArguments(ctx) {
	const firstToken = ctx.peek();
	if (isSymbolToken(firstToken)) return [withSourceCodeInfo([
		bindingTargetTypes.symbol,
		[parseSymbol(ctx), void 0],
		0
	], firstToken[2], ctx)];
	assertLParenToken(firstToken);
	ctx.advance();
	let rest = false;
	let defaults = false;
	const functionArguments = [];
	while (!ctx.isAtEnd() && !isRParenToken(ctx.peek()) && !isSymbolToken(ctx.peek(), "let")) {
		if (rest) throw new ParseError("Rest argument must be last", ctx.peekSourceCodeInfo());
		const bindingTarget = parseBindingTarget(ctx, { stopTypeAnnotationAtRParen: true });
		if (bindingTarget[1][1] !== void 0) defaults = true;
		if (bindingTarget[0] === bindingTargetTypes.rest) rest = true;
		if (defaults && !bindingTarget[1][1]) throw new ParseError("Default arguments must be last", ctx.peekSourceCodeInfo());
		functionArguments.push(bindingTarget);
		if (!isOperatorToken(ctx.peek(), ",") && !isRParenToken(ctx.peek()) && !isSymbolToken(ctx.peek(), "let")) throw new ParseError("Expected comma or closing parenthesis", ctx.peekSourceCodeInfo());
		if (isOperatorToken(ctx.peek(), ",")) ctx.advance();
	}
	if (!isRParenToken(ctx.peek())) throw new ParseError("Expected closing parenthesis", ctx.peekSourceCodeInfo());
	ctx.advance();
	return functionArguments;
}
function parseShorthandLambdaFunction(ctx) {
	ctx.builder?.startNode("Function");
	const firstToken = ctx.peek();
	ctx.advance();
	const startPos = ctx.getPosition();
	assertNonEmptyFunctionBody(ctx);
	let nodes;
	if (isReservedSymbolToken(ctx.peek(), "do")) {
		const doNode = parseDo(ctx);
		nodes = doNode[1];
		ctx.markStructuralLeaf(doNode[2]);
	} else nodes = [ctx.parseExpression()];
	const endPos = ctx.getPosition() - 1;
	let arity = 0;
	for (let pos = startPos; pos <= endPos; pos += 1) {
		const token = ctx.getTokenAt(pos);
		if (isSymbolToken(token)) {
			const match = placeholderRegexp.exec(token[1]);
			if (match) {
				const number = match[1] ?? "1";
				if (match[1] === "1") throw new ParseError("Use $ instead of $1 for the first argument", ctx.resolveTokenDebugInfo(firstToken[2]));
				arity = Math.max(arity, Number(number));
				if (arity > maxShorthandLambdaArity) throw new ParseError("Can't specify more than 20 arguments", ctx.resolveTokenDebugInfo(firstToken[2]));
			}
		}
	}
	const functionArguments = [];
	for (let i = 1; i <= arity; i += 1) {
		const name = i === 1 ? "$" : `$${i}`;
		functionArguments.push(withSourceCodeInfo([
			bindingTargetTypes.symbol,
			[[
				NodeTypes.Sym,
				name,
				0
			], void 0],
			0
		], firstToken[2], ctx));
	}
	const node = withSourceCodeInfo([
		NodeTypes.Function,
		[
			functionArguments,
			nodes,
			{ isShorthand: true }
		],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseMacro(ctx) {
	ctx.builder?.startNode("Macro");
	const token = ctx.peek();
	ctx.advance();
	let functionArguments;
	try {
		functionArguments = parseFunctionArguments(ctx);
	} catch {
		throw new ParseError("Expected function parameters after \"macro\"", ctx.peekSourceCodeInfo());
	}
	assertOperatorToken(ctx.peek(), "->");
	ctx.advance();
	let bodyNodes;
	if (isReservedSymbolToken(ctx.peek(), "do")) {
		const doNode = parseDo(ctx);
		bodyNodes = doNode[1];
		ctx.markStructuralLeaf(doNode[2]);
	} else bodyNodes = [ctx.parseExpression()];
	const node = withSourceCodeInfo([
		NodeTypes.Macro,
		[functionArguments, bodyNodes],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseMatch(ctx, token) {
	ctx.builder?.startNode("Match");
	ctx.advance();
	const valueExpression = ctx.parseExpression();
	const params = [];
	while (!ctx.isAtEnd() && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		assertReservedSymbolToken(ctx.tryPeek(), "case");
		ctx.advance();
		const pattern = parseBindingTarget(ctx, { allowLiteralPatterns: true });
		let guard;
		if (isReservedSymbolToken(ctx.tryPeek(), "when")) {
			ctx.advance();
			guard = ctx.parseExpression();
		}
		assertReservedSymbolToken(ctx.tryPeek(), "then");
		ctx.advance();
		const thenExpression = parseImplicitBlock(ctx, ["case", "end"]);
		ctx.clearStructuralLeaf(thenExpression[2]);
		params.push([
			pattern,
			thenExpression,
			guard
		]);
		if (isReservedSymbolToken(ctx.tryPeek(), "end")) break;
	}
	assertReservedSymbolToken(ctx.tryPeek(), "end");
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Match,
		[valueExpression, params],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseRegexpShorthand(ctx) {
	ctx.builder?.startNode("RegexpShorthand");
	const token = ctx.peek();
	ctx.advance();
	const endStringPosition = token[1].lastIndexOf("\"");
	const regexpString = token[1].substring(2, endStringPosition);
	const optionsString = token[1].substring(endStringPosition + 1);
	const stringNode = withSourceCodeInfo([
		NodeTypes.Str,
		regexpString,
		0
	], token[2], ctx);
	const optionsNode = withSourceCodeInfo([
		NodeTypes.Str,
		optionsString,
		0
	], token[2], ctx);
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[withSourceCodeInfo([
			NodeTypes.Builtin,
			"regexp",
			0
		], token[2], ctx), [stringNode, optionsNode]],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseReservedSymbol(ctx) {
	ctx.builder?.startNode("ReservedSymbol");
	const token = asReservedSymbolToken(ctx.tryPeek());
	ctx.advance();
	const symbol = token[1];
	if (isNumberReservedSymbol(symbol)) {
		const node = withSourceCodeInfo([
			NodeTypes.Num,
			numberReservedSymbolRecord[symbol],
			0
		], token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	const node = withSourceCodeInfo([
		NodeTypes.Reserved,
		token[1],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseArray(ctx) {
	ctx.builder?.startNode("Array");
	const firstToken = asLBracketToken(ctx.tryPeek());
	ctx.advance();
	const params = [];
	while (!ctx.isAtEnd() && !isRBracketToken(ctx.tryPeek())) {
		if (isOperatorToken(ctx.tryPeek(), "...")) {
			ctx.builder?.startNode("Spread");
			ctx.advance();
			params.push(withSourceCodeInfo([
				NodeTypes.Spread,
				ctx.parseExpression(),
				0
			], ctx.peekDebugInfo(), ctx));
			ctx.builder?.endNode();
		} else params.push(ctx.parseExpression());
		const nextToken = ctx.tryPeek();
		if (!isOperatorToken(nextToken, ",") && !isRBracketToken(nextToken)) throw new ParseError("Expected comma or closing parenthesis", ctx.peekSourceCodeInfo());
		if (isOperatorToken(nextToken, ",")) ctx.advance();
	}
	assertRBracketToken(ctx.tryPeek());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Array,
		params,
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseFunctionCall(ctx, symbol) {
	ctx.advance();
	const symbolSci = resolveSourceCodeInfo(symbol[2], ctx.sourceMap);
	const symbolDebugInfo = sourceCodeInfoToDebugInfo(symbolSci);
	if (isSpecialSymbolNode(symbol) && symbol[1] === specialExpressionTypes.effect) {
		const node = parseEffectArgs(ctx, symbolDebugInfo);
		ctx.setNodeEnd(node[2]);
		return node;
	}
	const params = [];
	while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
		if (isOperatorToken(ctx.tryPeek(), "...")) {
			ctx.builder?.startNode("Spread");
			ctx.advance();
			params.push(withSourceCodeInfo([
				NodeTypes.Spread,
				ctx.parseExpression(),
				0
			], ctx.peekDebugInfo(), ctx));
			ctx.builder?.endNode();
		} else params.push(ctx.parseExpression());
		const nextToken = ctx.tryPeek();
		if (!isOperatorToken(nextToken, ",") && !isRParenToken(nextToken)) throw new ParseError("Expected comma or closing parenthesis", ctx.peekSourceCodeInfo());
		if (isOperatorToken(nextToken, ",")) ctx.advance();
	}
	if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected closing parenthesis", ctx.peekSourceCodeInfo());
	ctx.advance();
	if (isSpecialSymbolNode(symbol)) {
		const specialExpressionType = symbol[1];
		if (specialExpressionType === specialExpressionTypes.import) {
			if (params.length !== 1) throw new ParseError(`import expects exactly 1 argument, got ${params.length}`, symbolSci);
			const param = params[0];
			if (param[0] !== NodeTypes.Str) throw new ParseError("import expects a string argument, e.g. import(\"math\")", resolveSourceCodeInfo(param[2], ctx.sourceMap) ?? symbolSci);
			const moduleName = param[1];
			ctx.markStructuralLeaf(param[2]);
			const node = withSourceCodeInfo([
				NodeTypes.Import,
				moduleName,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.recur) {
			const node = withSourceCodeInfo([
				NodeTypes.Recur,
				params,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.array) {
			const node = withSourceCodeInfo([
				NodeTypes.Array,
				params,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.parallel) {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.length, symbolSci);
			const node = withSourceCodeInfo([
				NodeTypes.Parallel,
				params[0],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.race) {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.length, symbolSci);
			const node = withSourceCodeInfo([
				NodeTypes.Race,
				params[0],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.settled) {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.length, symbolSci);
			const node = withSourceCodeInfo([
				NodeTypes.Settled,
				params[0],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.perform) {
			assertNumberOfParams({
				min: 1,
				max: 2
			}, params.length, symbolSci);
			const [effectExpr, payloadExpr] = params;
			const node = withSourceCodeInfo([
				NodeTypes.Perform,
				[effectExpr, payloadExpr],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.object) {
			const entries = [];
			let i = 0;
			while (i < params.length) {
				const param = params[i];
				if (isSpreadNode(param)) {
					entries.push(param);
					i += 1;
				} else {
					const valueParam = params[i + 1];
					if (valueParam === void 0) throw new ParseError("object() requires an even number of non-spread arguments (key-value pairs)", symbolSci);
					entries.push([param, valueParam]);
					i += 2;
				}
			}
			const node = withSourceCodeInfo([
				NodeTypes.Object,
				entries,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		const type = specialExpressionType;
		const specialExpression = builtin.specialExpressions[type];
		assertNumberOfParams(specialExpression.arity, params.length, symbolSci);
		switch (type) {
			case specialExpressionTypes["||"]: {
				const node = withSourceCodeInfo([
					NodeTypes.Or,
					params,
					0
				], symbolDebugInfo, ctx);
				for (let i = 1; i < params.length; i++) ctx.clearStructuralLeaf(params[i][2]);
				ctx.setNodeEnd(node[2]);
				return node;
			}
			case specialExpressionTypes["&&"]: {
				const node = withSourceCodeInfo([
					NodeTypes.And,
					params,
					0
				], symbolDebugInfo, ctx);
				for (let i = 1; i < params.length; i++) ctx.clearStructuralLeaf(params[i][2]);
				ctx.setNodeEnd(node[2]);
				return node;
			}
			case specialExpressionTypes["??"]: {
				const node = withSourceCodeInfo([
					NodeTypes.Qq,
					params,
					0
				], symbolDebugInfo, ctx);
				for (let i = 1; i < params.length; i++) ctx.clearStructuralLeaf(params[i][2]);
				ctx.setNodeEnd(node[2]);
				return node;
			}
			case specialExpressionTypes["function"]: throw new ParseError(`${type} is not allowed`, symbolSci);
			/* v8 ignore next 2 */
			default: throw new ParseError(`Unknown special expression: ${type}`, symbolSci);
		}
	} else if (isBuiltinSymbolNode(symbol) || isUserDefinedSymbolNode(symbol)) {
		const node = createNamedNormalExpressionNode(symbol, params, symbolDebugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	} else {
		const node = withSourceCodeInfo([
			NodeTypes.Call,
			[symbol, params],
			0
		], symbolDebugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	}
}
/**
* Parse the argument to `effect(...)` — a dotted identifier like `llm.complete`
* or `com.myco.human.approve`. Consumes symbol tokens separated by `.` and
* builds the full name string.
*/
function parseEffectArgs(ctx, symbolDebugInfo) {
	const firstToken = ctx.peek();
	if (!isSymbolToken(firstToken)) throw new ParseError("effect expects a dotted name identifier", ctx.resolveTokenDebugInfo(firstToken[2]));
	let name = firstToken[1];
	ctx.advance();
	while (isOperatorToken(ctx.tryPeek(), ".")) {
		ctx.advance();
		const nextToken = ctx.peek();
		if (!isSymbolToken(nextToken)) throw new ParseError("Expected identifier after dot in effect name", ctx.resolveTokenDebugInfo(nextToken[2]));
		name += `.${nextToken[1]}`;
		ctx.advance();
	}
	if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected closing parenthesis after effect name", ctx.peekSourceCodeInfo());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Effect,
		name,
		0
	], symbolDebugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
function parseObject(ctx) {
	ctx.builder?.startNode("Object");
	const firstToken = asLBraceToken(ctx.tryPeek());
	ctx.advance();
	const entries = [];
	while (!ctx.isAtEnd() && !isRBraceToken(ctx.tryPeek())) {
		if (isOperatorToken(ctx.tryPeek(), "...")) {
			ctx.builder?.startNode("Spread");
			ctx.advance();
			entries.push(withSourceCodeInfo([
				NodeTypes.Spread,
				ctx.parseExpression(),
				0
			], ctx.peekDebugInfo(), ctx));
			ctx.builder?.endNode();
		} else {
			const token = ctx.tryPeek();
			let keyNode;
			if (isTemplateStringToken(token)) keyNode = parseTemplateString(ctx, token);
			else if (isStringToken(token)) keyNode = parseString(ctx, token);
			else if (isSymbolToken(token)) {
				const isQuoted = token[1].startsWith("'");
				const value = isQuoted ? stringFromQuotedSymbol(token[1]) : token[1];
				keyNode = withSourceCodeInfo([
					NodeTypes.Str,
					value,
					0
				], token[2], ctx);
				ctx.advance();
				ctx.setNodeEnd(keyNode[2]);
				if (!isQuoted && !isOperatorToken(ctx.tryPeek(), ":")) {
					const valueNode = withSourceCodeInfo([
						NodeTypes.Sym,
						value,
						0
					], token[2], ctx);
					ctx.setNodeEnd(valueNode[2]);
					entries.push([keyNode, valueNode]);
					const nextToken = ctx.tryPeek();
					if (!isOperatorToken(nextToken, ",") && !isRBraceToken(nextToken)) throw new ParseError("Expected comma or closing brace", ctx.peekSourceCodeInfo());
					if (isOperatorToken(nextToken, ",")) ctx.advance();
					continue;
				}
			} else if (isLBracketToken(token)) {
				ctx.advance();
				keyNode = ctx.parseExpression();
				assertRBracketToken(ctx.tryPeek());
				ctx.advance();
			} else throw new ParseError("Expected key to be a symbol or a string", ctx.peekSourceCodeInfo());
			assertOperatorToken(ctx.tryPeek(), ":");
			ctx.advance();
			const valueNode = ctx.parseExpression();
			entries.push([keyNode, valueNode]);
		}
		const nextToken = ctx.tryPeek();
		if (!isOperatorToken(nextToken, ",") && !isRBraceToken(nextToken)) throw new ParseError("Expected comma or closing brace", ctx.peekSourceCodeInfo());
		if (isOperatorToken(nextToken, ",")) ctx.advance();
	}
	assertRBraceToken(ctx.tryPeek());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Object,
		entries,
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
const validDvalaEffects = new Set([
	"dvala.io.print",
	"dvala.io.error",
	"dvala.io.read",
	"dvala.io.readStdin",
	"dvala.io.pick",
	"dvala.io.confirm",
	"dvala.random",
	"dvala.random.uuid",
	"dvala.random.int",
	"dvala.random.item",
	"dvala.random.shuffle",
	"dvala.time.now",
	"dvala.time.zone",
	"dvala.sleep",
	"dvala.error",
	"dvala.checkpoint",
	"dvala.host",
	"dvala.env",
	"dvala.args"
]);
function parseOperand(ctx) {
	const checkpoint = ctx.builder?.checkpoint();
	let operand = parseOperandPart(ctx);
	let token = ctx.tryPeek();
	while (isOperatorToken(token, ".") || isOperatorToken(token, "?.") || isLBracketToken(token) || isLParenToken(token)) if (token[1] === "." || token[1] === "?.") {
		const safe = token[1] === "?.";
		ctx.builder?.startNodeAt(checkpoint, "PropertyAccess");
		ctx.advance();
		const symbolToken = ctx.tryPeek();
		if (!isSymbolToken(symbolToken)) throw new ParseError("Expected symbol", ctx.peekSourceCodeInfo());
		const stringNode = withSourceCodeInfo([
			NodeTypes.Str,
			symbolToken[1],
			0
		], symbolToken[2], ctx);
		if (safe) operand = createAccessorNode(ctx, operand, stringNode, token[2]);
		else operand = createStrictAccessorNode(ctx, operand, stringNode, token[2]);
		ctx.advance();
		ctx.builder?.endNode();
		token = ctx.tryPeek();
	} else if (isLBracketToken(token)) {
		ctx.builder?.startNodeAt(checkpoint, "IndexAccess");
		ctx.advance();
		const expression = ctx.parseExpression();
		if (!isRBracketToken(ctx.tryPeek())) throw new ParseError("Expected closing bracket", ctx.peekSourceCodeInfo());
		operand = createAccessorNode(ctx, operand, expression, token[2]);
		ctx.advance();
		ctx.builder?.endNode();
		token = ctx.tryPeek();
	} else if (isLParenToken(token)) {
		ctx.builder?.startNodeAt(checkpoint, "Call");
		operand = parseFunctionCall(ctx, operand);
		ctx.builder?.endNode();
		token = ctx.tryPeek();
	}
	ctx.setNodeEnd(operand[2]);
	return operand;
}
function parseOperandPart(ctx) {
	const token = ctx.peek();
	if (isLParenToken(token)) {
		if (looksLikeLambda(ctx)) return parseLambdaFunction(ctx);
		ctx.builder?.startNode("Parenthesized");
		ctx.advance();
		const expression = ctx.parseExpression();
		if (!isRParenToken(ctx.peek())) throw new ParseError("Expected closing parenthesis", ctx.peekSourceCodeInfo());
		ctx.advance();
		ctx.setNodeEnd(expression[2]);
		ctx.builder?.endNode();
		return expression;
	} else if (isOperatorToken(token)) {
		const operatorName = token[1];
		if (operatorName === "-") {
			const nextToken = ctx.peekAhead(1);
			const nextType = nextToken?.[0];
			if (nextType === "Number" || nextType === "Symbol" || nextType === "ReservedSymbol" && isNumberReservedSymbol(nextToken[1]) || nextType === "LBracket" || nextType === "LBrace" || nextType === "string" || nextType === "EffectName" || nextType === "BasePrefixedNumber") {
				ctx.builder?.startNode("PrefixOp");
				ctx.advance();
				const operand = parseOperand(ctx);
				const zeroNode = withSourceCodeInfo([
					NodeTypes.Num,
					0,
					0
				], token[2], ctx);
				const minusSymbol = withSourceCodeInfo([
					NodeTypes.Builtin,
					"-",
					0
				], token[2], ctx);
				const node = withSourceCodeInfo([
					NodeTypes.Call,
					[minusSymbol, [zeroNode, operand]],
					0
				], token[2], ctx);
				ctx.setNodeEnd(node[2]);
				ctx.builder?.endNode();
				return node;
			}
		}
		if (operatorName === "!") {
			const nextToken = ctx.peekAhead(1);
			const nextType = nextToken?.[0];
			if (nextType === "Number" || nextType === "BasePrefixedNumber" || nextType === "Symbol" || nextType === "ReservedSymbol" || nextType === "string" || nextType === "TemplateString" || nextType === "LBracket" || nextType === "LBrace" || nextType === "LParen" || nextType === "Atom" || nextType === "EffectName" || nextType === "RegexpShorthand" || nextType === "MacroPrefix" || nextType === "Operator" && (nextToken[1] === "-" || nextToken[1] === "!")) {
				ctx.builder?.startNode("PrefixOp");
				ctx.advance();
				const operand = parseOperand(ctx);
				const bangSymbol = withSourceCodeInfo([
					NodeTypes.Builtin,
					"!",
					0
				], token[2], ctx);
				const node = withSourceCodeInfo([
					NodeTypes.Call,
					[bangSymbol, [operand]],
					0
				], token[2], ctx);
				ctx.setNodeEnd(node[2]);
				ctx.builder?.endNode();
				return node;
			}
			ctx.builder?.startNode("Symbol");
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Builtin,
				"!",
				0
			], token[2], ctx);
			ctx.setNodeEnd(node[2]);
			ctx.builder?.endNode();
			return node;
		}
		if (isBinaryOperator(operatorName)) {
			ctx.builder?.startNode("Symbol");
			ctx.advance();
			if (specialExpressionTypes[operatorName] !== void 0) {
				const node = withSourceCodeInfo([
					NodeTypes.Special,
					specialExpressionTypes[operatorName],
					0
				], token[2], ctx);
				ctx.setNodeEnd(node[2]);
				ctx.builder?.endNode();
				return node;
			}
			const node = withSourceCodeInfo([
				NodeTypes.Builtin,
				operatorName,
				0
			], token[2], ctx);
			ctx.setNodeEnd(node[2]);
			ctx.builder?.endNode();
			return node;
		}
		if (operatorName === "->") return parseShorthandLambdaFunction(ctx);
		else throw new ParseError(`Illegal operator: ${operatorName}`, ctx.resolveTokenDebugInfo(token[2]));
	}
	if (isLBraceToken(token)) return parseObject(ctx);
	if (isLBracketToken(token)) return parseArray(ctx);
	if (token[0] === "MacroPrefix") {
		ctx.builder?.startNode("MacroCall");
		const debugInfo = token[2];
		const nodeId = ctx.allocateNodeId(debugInfo);
		ctx.advance();
		const operand = ctx.parseExpression();
		const symNode = withSourceCodeInfo([
			NodeTypes.Sym,
			token[1],
			0
		], debugInfo, ctx);
		const node = [
			NodeTypes.MacroCall,
			[symNode, [operand]],
			nodeId
		];
		ctx.setNodeEnd(nodeId);
		ctx.builder?.endNode();
		return node;
	}
	const tokenType = token[0];
	switch (tokenType) {
		case "Atom": {
			ctx.builder?.startNode("AtomLiteral");
			ctx.advance();
			const atomNode = withSourceCodeInfo([
				NodeTypes.Atom,
				token[1],
				0
			], token[2], ctx);
			ctx.setNodeEnd(atomNode[2]);
			ctx.builder?.endNode();
			return atomNode;
		}
		case "Number":
		case "BasePrefixedNumber": return parseNumber(ctx);
		case "string": return parseString(ctx, token);
		case "TemplateString": return parseTemplateString(ctx, token);
		case "Symbol":
			if (looksLikeLambda(ctx)) return parseLambdaFunction(ctx);
			return parseSymbol(ctx);
		case "ReservedSymbol": return parseReservedSymbol(ctx);
		case "RegexpShorthand": return parseRegexpShorthand(ctx);
		case "EffectName": {
			ctx.builder?.startNode("EffectName");
			const effectName = token[1];
			if (effectName.startsWith("dvala.") && !effectName.includes("*") && !validDvalaEffects.has(effectName)) throw new ParseError(`Unknown dvala effect: '${effectName}'`, ctx.resolveTokenDebugInfo(token[2]));
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Effect,
				effectName,
				0
			], token[2], ctx);
			ctx.setNodeEnd(node[2]);
			ctx.builder?.endNode();
			return node;
		}
		default: throw new ParseError(`Unknown token type: ${tokenType}`, ctx.resolveTokenDebugInfo(token[2]));
	}
}
function looksLikeLambda(ctx) {
	const token = ctx.tryPeek();
	if (!token) return false;
	if (isSymbolToken(token)) return isOperatorToken(ctx.peekAhead(1), "->");
	if (isLParenToken(token)) {
		let depth = 1;
		let offset = 1;
		while (depth > 0) {
			const t = ctx.peekAhead(offset);
			if (!t) return false;
			if (isLParenToken(t)) depth++;
			else if (isRParenToken(t)) depth--;
			offset++;
		}
		if (isOperatorToken(ctx.peekAhead(offset), "->")) return true;
		if (isOperatorToken(ctx.peekAhead(offset), ":")) {
			offset++;
			let typeDepth = 0;
			while (ctx.peekAhead(offset)) {
				const t = ctx.peekAhead(offset);
				if (typeDepth === 0 && isOperatorToken(t, "->")) return true;
				if (isLParenToken(t) || isLBracketToken(t)) typeDepth++;
				else if (isRParenToken(t) || isRBracketToken(t)) typeDepth--;
				if (typeDepth === 0 && (isOperatorToken(t, "=") || isOperatorToken(t, ";") || isReservedSymbolToken(t, "end") || isReservedSymbolToken(t, "do"))) break;
				offset++;
			}
		}
		return false;
	}
	return false;
}
function createAccessorNode(ctx, left, right, debugInfo) {
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[withSourceCodeInfo([
			NodeTypes.Builtin,
			"get",
			0
		], debugInfo, ctx), [left, right]],
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
/**
* Creates a strict property access node: `obj("key")` — a function call
* where the object is called as a function with the key as argument.
* Throws KeyError for missing keys (unlike get which returns null).
*/
function createStrictAccessorNode(ctx, left, right, debugInfo) {
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[left, [right]],
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
/**
* Parse a quote...end block.
*
* quote <body> end
*
* Body is Dvala code captured as AST data. $^{expr} splices escape one level
* back to runtime evaluation. $^^{expr} escapes two levels, etc.
*
* Returns the same CodeTmpl AST node as the old triple-backtick syntax.
*/
let quoteTemplateCounter = 0;
function parseQuote(ctx) {
	ctx.builder?.startNode("Quote");
	const debugInfo = ctx.peek()[2];
	ctx.advance();
	const bodyStartRawPos = ctx.getPosition();
	const bodyStartCstCheckpoint = ctx.builder?.checkpoint();
	const bodyTokens = [];
	let depth = 1;
	let bodyEndRawPos = -1;
	while (!ctx.isAtEnd() && depth > 0) {
		const t = ctx.peek();
		if (bodyTokens.length > 0 && isOperatorToken(bodyTokens[bodyTokens.length - 1], ".") && isSymbolToken(t)) {
			bodyTokens.push(t);
			ctx.advance();
			continue;
		}
		if (t[0] === "QuoteSplice") {
			ctx.builder?.startNode("Splice");
			bodyTokens.push(t);
			ctx.advance();
			let braceDepth = 1;
			while (!ctx.isAtEnd() && braceDepth > 0) {
				const inner = ctx.peek();
				if (isLBraceToken(inner)) braceDepth++;
				else if (isRBraceToken(inner)) braceDepth--;
				bodyTokens.push(inner);
				ctx.advance();
			}
			ctx.builder?.endNode();
			continue;
		}
		if (isBlockOpener(t)) {
			depth++;
			bodyTokens.push(t);
			ctx.advance();
		} else if (isReservedSymbolToken(t, "end")) {
			depth--;
			if (depth > 0) bodyTokens.push(t);
			else bodyEndRawPos = ctx.getPosition();
			ctx.advance();
		} else {
			bodyTokens.push(t);
			ctx.advance();
		}
	}
	if (depth > 0) throw new ParseError("Unterminated quote block — expected `end`", ctx.resolveTokenDebugInfo(debugInfo));
	const resolvedSci = ctx.resolveTokenDebugInfo(debugInfo);
	const templateId = quoteTemplateCounter++;
	const spliceExprs = [];
	const processedTokens = [];
	const blockStack = [];
	let i = 0;
	while (i < bodyTokens.length) {
		const bt = bodyTokens[i];
		if (processedTokens.length > 0 && isOperatorToken(processedTokens[processedTokens.length - 1], ".") && isSymbolToken(bt)) {
			processedTokens.push(bt);
			i++;
			continue;
		}
		if (isBlockOpener(bt)) {
			blockStack.push(isReservedSymbolToken(bt, "quote") ? "quote" : "other");
			processedTokens.push(bt);
			i++;
			continue;
		}
		if (isReservedSymbolToken(bt, "end")) {
			blockStack.pop();
			processedTokens.push(bt);
			i++;
			continue;
		}
		if (bt[0] === "QuoteSplice") {
			const level = countCarets(bt[1]);
			const innerQuoteDepth = blockStack.filter((b) => b === "quote").length;
			const effectiveLevel = level - innerQuoteDepth;
			if (effectiveLevel === 1) {
				i = extractSplice(bodyTokens, i, spliceExprs, processedTokens, templateId, resolvedSci, ctx.allocateId);
				continue;
			} else if (effectiveLevel > 1) throw new ParseError(`Splice level ${level} but only ${innerQuoteDepth + 1} quote level(s) deep`, resolvedSci);
			processedTokens.push(bt);
			i++;
			let braceDepth = 1;
			while (i < bodyTokens.length && braceDepth > 0) {
				const inner = bodyTokens[i];
				if (isLBraceToken(inner)) braceDepth++;
				else if (isRBraceToken(inner)) braceDepth--;
				processedTokens.push(inner);
				i++;
			}
			continue;
		}
		processedTokens.push(bt);
		i++;
	}
	const bodyCtx = createParserContext({ tokens: processedTokens }, ctx.allocateId);
	const bodyAst = [];
	while (!bodyCtx.isAtEnd()) {
		bodyAst.push(parseExpression(bodyCtx, 0));
		if (isOperatorToken(bodyCtx.tryPeek(), ";")) bodyCtx.advance();
	}
	const processedBody = bodyAst.map((node) => replacePlaceholders(node, spliceExprs, 0, templateId));
	if (ctx.builder && bodyEndRawPos >= 0 && bodyStartCstCheckpoint !== void 0) rebuildQuoteBodyCst(ctx, bodyStartRawPos, bodyEndRawPos, bodyStartCstCheckpoint, templateId);
	const resultNode = withSourceCodeInfo([
		NodeTypes.CodeTmpl,
		[processedBody, spliceExprs],
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(resultNode[2]);
	ctx.builder?.endNode();
	return resultNode;
}
/**
* Rebuild the main Quote CST node's body children as a properly structured
* sub-tree (Function/Block/If/etc. sub-nodes) instead of the flat tokens
* emitted by pass 1.
*
* Approach:
*  1. Walk ctx.tokens over the body range, producing a trivia-preserving
*     token stream with this-level splices replaced by placeholder Symbol
*     tokens. Record, for each placeholder, which Splice CST sub-node from
*     the main Quote's body it should be swapped back to.
*  2. Sub-parse the stream with a fresh CstBuilder. The sub-CST has full
*     structure (Function, Block, ...) built by the normal parseExpression
*     path — same machinery that formats any non-quoted expression.
*  3. Walk the sub-CST, replacing placeholder Symbol sub-nodes with the
*     originals captured in step 1. This preserves their leading/trailing
*     trivia (comments around splices survive formatting).
*  4. Truncate the main Quote's children back to where the body started and
*     append the rebuilt children. The Quote node now contains structured
*     body nodes the formatter can recursively wrap.
*
* Splice placeholders use the template-id from pass 2 so this post-processing
* only touches this parseQuote call's splices; nested Quote CST nodes that
* pass through have their own templateId and get rebuilt on their own call.
*/
function rebuildQuoteBodyCst(ctx, bodyStartPos, bodyEndPos, bodyStartCheckpoint, templateId) {
	const builder = ctx.builder;
	const rawBodyChildren = builder.peekCurrent().children.slice(bodyStartCheckpoint);
	const mainSpliceSubNodes = rawBodyChildren.filter(isSpliceCstNode);
	const endToken = rawBodyChildren[rawBodyChildren.length - 1];
	if (!isCstToken(endToken) || endToken.text !== "end") throw new Error("parseQuote.rebuildQuoteBodyCst: expected last body child to be the outer `end` token");
	const { subTokens, placeholderToSplice } = buildSubParseStream(ctx, bodyStartPos, bodyEndPos, templateId, mainSpliceSubNodes);
	const subBuilder = new CstBuilder();
	subBuilder.startNode("QuoteBody");
	const subCtx = createCstParserContext({ tokens: subTokens }, ctx.allocateId, subBuilder);
	while (!subCtx.isAtEnd()) {
		parseExpression(subCtx, 0);
		if (isOperatorToken(subCtx.tryPeek(), ";")) subCtx.advance();
	}
	subBuilder.endNode();
	const subTree = subBuilder.finish();
	replacePlaceholderSymbolsInCst(subTree, templateId, placeholderToSplice);
	const pass1FirstLeaf = rawBodyChildren[0] !== void 0 && !isCstToken(rawBodyChildren[0]) ? findFirstLeafToken(rawBodyChildren[0]) : rawBodyChildren[0];
	if (pass1FirstLeaf && pass1FirstLeaf.leadingTrivia && pass1FirstLeaf.leadingTrivia.length > 0 && subTree.children.length > 0) {
		const subFirst = subTree.children[0];
		const subFirstLeaf = "kind" in subFirst ? findFirstLeafToken(subFirst) : subFirst;
		if (subFirstLeaf && subFirstLeaf.leadingTrivia.length === 0) subFirstLeaf.leadingTrivia = pass1FirstLeaf.leadingTrivia;
	}
	builder.truncateCurrent(bodyStartCheckpoint);
	for (const child of subTree.children) builder.appendChild(child);
	builder.appendChild(endToken);
}
/**
* True when a CST child is a leaf token rather than a sub-node. Tokens carry
* `text` + trivia arrays; sub-nodes carry `kind` + `children`.
*/
function isCstToken(v) {
	return typeof v === "object" && v !== null && "text" in v && !("kind" in v);
}
/** Find the first leaf CstToken in a CST sub-tree (pre-order). */
function findFirstLeafToken(node) {
	for (const child of node.children) {
		if (isCstToken(child)) return child;
		const nested = findFirstLeafToken(child);
		if (nested) return nested;
	}
}
function isSpliceCstNode(child) {
	return typeof child === "object" && child !== null && "kind" in child && child.kind === "Splice";
}
/**
* Walk the body range building a trivia-preserving token stream for the sub-parse.
* This-level splices (effectiveLevel === 1) become placeholder Symbol tokens so
* parseExpression treats them as plain identifiers; nested-level splices pass
* through so the recursive parseQuote call on the inner quote handles them.
*
* The returned placeholderToSplice array is indexed by this-level splice count
* and holds the original main-Quote Splice CST sub-node for each placeholder,
* so the caller can swap them back after sub-parsing.
*/
function buildSubParseStream(ctx, bodyStartPos, bodyEndPos, templateId, mainSpliceSubNodes) {
	const subTokens = [];
	const placeholderToSplice = [];
	const blockStack = [];
	let overallSpliceCount = 0;
	let i = bodyStartPos;
	while (i < bodyEndPos) {
		const t = ctx.getTokenAt(i);
		if (isTrivia(t)) {
			subTokens.push(t);
			i++;
			continue;
		}
		if (subTokens.length > 0 && isOperatorToken(subTokens[subTokens.length - 1], ".") && isSymbolToken(t)) {
			subTokens.push(t);
			i++;
			continue;
		}
		if (isBlockOpener(t)) {
			blockStack.push(isReservedSymbolToken(t, "quote") ? "quote" : "other");
			subTokens.push(t);
			i++;
			continue;
		}
		if (isReservedSymbolToken(t, "end")) {
			blockStack.pop();
			subTokens.push(t);
			i++;
			continue;
		}
		if (t[0] === "QuoteSplice") {
			const effectiveLevel = countCarets(t[1]) - blockStack.filter((b) => b === "quote").length;
			const spliceSubNode = mainSpliceSubNodes[overallSpliceCount];
			overallSpliceCount++;
			if (effectiveLevel === 1) {
				const placeholderName = `__splice_${templateId}_${placeholderToSplice.length}__`;
				const placeholderToken = t[2] ? [
					"Symbol",
					placeholderName,
					t[2]
				] : ["Symbol", placeholderName];
				subTokens.push(placeholderToken);
				if (spliceSubNode) placeholderToSplice.push(spliceSubNode);
				else placeholderToSplice.push({
					kind: "Symbol",
					children: []
				});
				i++;
				let braceDepth = 1;
				while (i < bodyEndPos && braceDepth > 0) {
					const inner = ctx.getTokenAt(i);
					if (isLBraceToken(inner)) braceDepth++;
					else if (isRBraceToken(inner)) braceDepth--;
					i++;
				}
				continue;
			}
			subTokens.push(t);
			i++;
			let braceDepth = 1;
			while (i < bodyEndPos && braceDepth > 0) {
				const inner = ctx.getTokenAt(i);
				if (isLBraceToken(inner)) braceDepth++;
				else if (isRBraceToken(inner)) braceDepth--;
				subTokens.push(inner);
				i++;
			}
			continue;
		}
		subTokens.push(t);
		i++;
	}
	return {
		subTokens,
		placeholderToSplice
	};
}
/**
* Walk a CST sub-tree and, wherever a Symbol node wraps a placeholder token
* named `__splice_${templateId}_N__`, replace the Symbol node with the
* corresponding Splice sub-node supplied by placeholderToSplice[N].
*
* Uses templateId so this only touches placeholders from this parseQuote call —
* nested Quote sub-nodes with their own templateId are left alone.
*/
function replacePlaceholderSymbolsInCst(node, templateId, placeholderToSplice) {
	for (let i = 0; i < node.children.length; i++) {
		const child = node.children[i];
		if (!("kind" in child)) continue;
		if (child.kind === "Symbol" && child.children.length === 1) {
			const inner = child.children[0];
			if (inner && "text" in inner) {
				const match = inner.text.match(/^__splice_(\d+)_(\d+)__$/);
				if (match && Number(match[1]) === templateId) {
					const splice = placeholderToSplice[Number(match[2])];
					if (splice) node.children[i] = splice;
					continue;
				}
			}
		}
		replacePlaceholderSymbolsInCst(child, templateId, placeholderToSplice);
	}
}
/** Count the number of ^ characters in a QuoteSplice value like "$^^{" */
function countCarets(value) {
	let count = 0;
	for (let j = 1; j < value.length; j++) if (value[j] === "^") count++;
	else break;
	return count;
}
/**
* Extract a splice expression from bodyTokens starting at position i (a QuoteSplice token).
* Parses the expression tokens between the splice opener and matching RBrace.
* Pushes a placeholder Symbol token into processedTokens.
* Returns the new position in bodyTokens (after the closing RBrace).
*/
function extractSplice(bodyTokens, i, spliceExprs, processedTokens, templateId, sci, allocateId) {
	const spliceDebugInfo = bodyTokens[i][2];
	i++;
	const exprTokens = [];
	let braceDepth = 1;
	while (i < bodyTokens.length && braceDepth > 0) {
		const t = bodyTokens[i];
		if (isLBraceToken(t)) braceDepth++;
		else if (isRBraceToken(t)) {
			braceDepth--;
			if (braceDepth === 0) {
				i++;
				break;
			}
		}
		exprTokens.push(t);
		i++;
	}
	if (braceDepth > 0) throw new ParseError("Unclosed splice expression in quote block", sci);
	if (exprTokens.length === 0) throw new ParseError("Empty splice expression in quote block", sci);
	const expr = parseExpression(createParserContext({ tokens: exprTokens }, allocateId), 0);
	const index = spliceExprs.length;
	spliceExprs.push(expr);
	const placeholder = spliceDebugInfo ? [
		"Symbol",
		`__splice_${templateId}_${index}__`,
		spliceDebugInfo
	] : ["Symbol", `__splice_${templateId}_${index}__`];
	processedTokens.push(placeholder);
	return i;
}
/** Check if a token opens a block that ends with 'end'. */
function isBlockOpener(t) {
	if (isReservedSymbolToken(t, "do")) return true;
	if (isSymbolToken(t, "handler")) return true;
	if (isReservedSymbolToken(t, "quote")) return true;
	if (isSymbolToken(t, "if")) return true;
	if (isSymbolToken(t, "match")) return true;
	if (isSymbolToken(t, "for")) return true;
	if (isSymbolToken(t, "loop")) return true;
	return false;
}
/**
* Recursively walk an AST node and replace UserDefinedSymbol nodes
* named `__splice_N_M__` with Splice nodes referencing the Mth splice expression.
*/
function replacePlaceholders(node, spliceExprs, indexOffset = 0, templateId) {
	const [type, payload, nodeId] = node;
	if (type === NodeTypes.Sym && typeof payload === "string") {
		const match = payload.match(/^__splice_(\d+)_(\d+)__$/);
		if (match && (templateId === void 0 || parseInt(match[1], 10) === templateId)) {
			const index = parseInt(match[2], 10) + indexOffset;
			return [
				NodeTypes.Splice,
				index,
				nodeId
			];
		}
	}
	if (type === NodeTypes.CodeTmpl && Array.isArray(payload)) {
		const [bodyAst, innerSpliceExprs] = payload;
		const innerCount = innerSpliceExprs.length;
		return [
			type,
			[bodyAst.map((n) => replacePlaceholders(n, spliceExprs, innerCount, templateId)), innerSpliceExprs],
			nodeId
		];
	}
	if (Array.isArray(payload)) return [
		type,
		payload.map((item) => Array.isArray(item) ? replacePlaceholdersInValue(item, spliceExprs, indexOffset, templateId) : item),
		nodeId
	];
	return node;
}
function replacePlaceholdersInValue(value, spliceExprs, indexOffset = 0, templateId) {
	if (value.length >= 2 && typeof value[0] === "string") return replacePlaceholders(value, spliceExprs, indexOffset, templateId);
	return value.map((item) => Array.isArray(item) ? replacePlaceholdersInValue(item, spliceExprs, indexOffset, templateId) : item);
}
function createParserContext(tokenStream, allocateId) {
	const ctx = new ParserContext(tokenStream, allocateId);
	ctx.parseExpression = (precedence = 0) => parseExpression(ctx, precedence);
	return ctx;
}
function createCstParserContext(tokenStream, allocateId, builder) {
	const ctx = new ParserContext(tokenStream, allocateId, builder);
	ctx.parseExpression = (precedence = 0) => parseExpression(ctx, precedence);
	return ctx;
}
function parseExpression(ctx, precedence = 0) {
	const checkpoint = ctx.builder?.checkpoint();
	const token = ctx.tryPeek();
	let left;
	if (isSymbolToken(token)) switch (token[1]) {
		case "let": return parseLet(ctx, token);
		case "if":
			left = parseIf(ctx, token);
			break;
		case "match":
			left = parseMatch(ctx, token);
			break;
		case "for":
			left = parseForOrDoseq(ctx, token);
			break;
		case "loop":
			left = parseLoop(ctx, token);
			break;
		case "macro":
			left = parseMacro(ctx);
			break;
		case "handler":
			if (isHandlerStart(ctx)) left = parseHandler(ctx);
			break;
		case "shallow":
			if (isShallowHandlerStart(ctx)) {
				ctx.builder?.startNode("Handler");
				ctx.advance();
				let linear = false;
				if (isSymbolToken(ctx.tryPeek(), "linear")) {
					ctx.advance();
					linear = true;
				}
				left = parseHandler(ctx, true, linear);
			}
			break;
		case "linear":
			if (isLinearHandlerStart(ctx)) {
				ctx.builder?.startNode("Handler");
				ctx.advance();
				let shallow = false;
				if (isSymbolToken(ctx.tryPeek(), "shallow")) {
					ctx.advance();
					shallow = true;
				}
				left = parseHandler(ctx, shallow, true);
			}
			break;
		case "resume":
			if (isResumeStart(ctx)) left = parseResume(ctx);
			break;
		case "effect":
			if (isEffectNameToken(ctx.peekAhead(1))) left = parseEffectDeclaration(ctx);
			break;
		case "type":
			if (isSymbolToken(ctx.peekAhead(1)) && /^[A-Z]/.test(ctx.peekAhead(1)[1])) left = parseTypeDeclaration(ctx);
			break;
	}
	else if (isReservedSymbolToken(token, "do")) left = parseDo(ctx);
	else if (isReservedSymbolToken(token, "quote")) left = parseQuote(ctx);
	left ||= parseOperand(ctx);
	let operator = ctx.tryPeek();
	while (!isAtExpressionEnd(ctx)) {
		if (isA_BinaryOperatorToken(operator)) {
			const name = operator[1];
			const newPrecedece = getPrecedence(name, ctx.resolveTokenDebugInfo(operator[2]));
			if (newPrecedece <= precedence && !(newPrecedece === 12 && precedence === 12)) break;
			ctx.builder?.startNodeAt(checkpoint, "BinaryOp");
			const symbol = specialExpressionTypes[name] ? withSourceCodeInfo([
				NodeTypes.Special,
				specialExpressionTypes[name],
				0
			], operator[2], ctx) : withSourceCodeInfo([
				NodeTypes.Builtin,
				name,
				0
			], operator[2], ctx);
			ctx.advance();
			const right = parseExpression(ctx, newPrecedece);
			const callRangeStart = nodeStartDebugInfo(left, ctx) ?? operator[2];
			left = fromBinaryOperatorToNode(operator, symbol, left, right, callRangeStart, ctx);
			ctx.builder?.endNode();
		} else if (isSymbolToken(operator)) {
			if (!isFunctionOperator(operator[1])) break;
			const newPrecedence = 3;
			if (newPrecedence <= precedence) break;
			ctx.builder?.startNodeAt(checkpoint, "BinaryOp");
			const operatorSymbol = parseSymbol(ctx);
			const right = parseExpression(ctx, newPrecedence);
			if (isSpecialSymbolNode(operatorSymbol)) throw new ParseError("Special expressions are not allowed in binary functional operators", ctx.resolveTokenDebugInfo(operator[2]));
			left = createNamedNormalExpressionNode(operatorSymbol, [left, right], operator[2], ctx, { isInfix: true });
			ctx.builder?.endNode();
		} else break;
		operator = ctx.tryPeek();
	}
	ctx.setNodeEnd(left[2]);
	return left;
}
/**
* Check if `handler` token starts a handler expression (not a variable reference).
* Handler expression: `handler @effect... end` or `handler transform... end` or `handler end`.
* The lookahead checks the token after `handler`.
*/
/**
* Check if `shallow handler` starts a shallow handler expression. Also
* accepts `shallow linear handler` (modifiers in either order).
* Requires: current token = `shallow`, then `[linear?] handler @effect|transform`.
*/
function isShallowHandlerStart(ctx) {
	let handlerOffset = 1;
	if (isSymbolToken(ctx.peekAhead(1), "linear")) handlerOffset = 2;
	if (!isSymbolToken(ctx.peekAhead(handlerOffset), "handler")) return false;
	const afterHandler = ctx.peekAhead(handlerOffset + 1);
	if (!afterHandler) return false;
	if (isEffectNameToken(afterHandler)) return true;
	if (isSymbolToken(afterHandler, "transform")) return true;
	return false;
}
/**
* Check if `linear handler` starts a linear handler expression. Also
* accepts `linear shallow handler` (modifiers in either order).
* Requires: current token = `linear`, then `[shallow?] handler @effect|transform`.
*/
function isLinearHandlerStart(ctx) {
	let handlerOffset = 1;
	if (isSymbolToken(ctx.peekAhead(1), "shallow")) handlerOffset = 2;
	if (!isSymbolToken(ctx.peekAhead(handlerOffset), "handler")) return false;
	const afterHandler = ctx.peekAhead(handlerOffset + 1);
	if (!afterHandler) return false;
	if (isEffectNameToken(afterHandler)) return true;
	if (isSymbolToken(afterHandler, "transform")) return true;
	return false;
}
function isHandlerStart(ctx) {
	const next = ctx.peekAhead(1);
	if (!next) return false;
	if (isEffectNameToken(next)) return true;
	if (isSymbolToken(next, "transform")) return true;
	return false;
}
/**
* Check if `resume` token starts a resume expression (not a variable reference).
* Resume expression: `resume(...)` or bare `resume` at expression end.
* When `resume` is followed by `(`, it's always a resume call (not a variable call).
* When at expression end, it's a bare resume reference.
*/
function isResumeStart(ctx) {
	const next = ctx.peekAhead(1);
	if (isLParenToken(next)) return true;
	if (!next) return true;
	if (isReservedSymbolToken(next)) return true;
	if (isA_BinaryOperatorToken(next)) return true;
	return false;
}
/**
* Parse `resume`, `resume()`, or `resume(value)`.
*
* ResumeNode payload encoding:
* - `resume(value)`: the argument AstNode
* - `resume()`: a Null literal node (call with no arg → resume with null)
* - bare `resume`: special string 'ref' (reference to resume function)
*/
function parseResume(ctx) {
	ctx.builder?.startNode("Resume");
	const token = ctx.tryPeek();
	ctx.advance();
	if (isLParenToken(ctx.tryPeek())) {
		ctx.advance();
		let argNode;
		if (!isRParenToken(ctx.tryPeek())) argNode = ctx.parseExpression();
		else argNode = withSourceCodeInfo([
			NodeTypes.Reserved,
			"null",
			0
		], token[2], ctx);
		if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected \")\" after resume argument", ctx.peekSourceCodeInfo());
		ctx.advance();
		const node = withSourceCodeInfo([
			NodeTypes.Resume,
			argNode,
			0
		], token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	const node = withSourceCodeInfo([
		NodeTypes.Resume,
		"ref",
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* Parse the full token stream into an untyped CST tree.
*
* The token stream must include whitespace and comment tokens (debug mode).
* The parser runs in CST mode: every consumed token is wrapped in a CstToken
* with attached trivia and fed to the CstBuilder.
*
* Throws on parse error — no error recovery (CstErrorNode is a future concern).
*/
function parseToCst(fullTokenStream) {
	for (const token of fullTokenStream.tokens) if (token[0] === "Error") throw new ParseError(token[3], debugInfoToSourceCodeInfo(token[2], fullTokenStream.source, fullTokenStream.filePath));
	const builder = new CstBuilder();
	let localCounter = 0;
	const ctx = createCstParserContext(fullTokenStream, () => localCounter++, builder);
	builder.startNode("Program");
	while (!ctx.isAtEnd()) {
		parseExpression(ctx, 0);
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!ctx.isAtEnd()) throw new ParseError("Expected ;", ctx.peekSourceCodeInfo());
	}
	builder.endNode();
	return {
		tree: builder.finish(),
		trailingTrivia: ctx.getRemainingTrivia()
	};
}
/** Literal text — never broken. */
function text(s) {
	return {
		type: "text",
		text: s
	};
}
/** In flat mode: space. In break mode: newline + indent. */
const line = { type: "line" };
/** In flat mode: nothing. In break mode: newline + indent. */
const softLine = { type: "softline" };
/** Always a newline + indent. Forces enclosing Group to break. */
const hardLine = { type: "hardline" };
/** A line comment — text followed by a mandatory hard break. */
function lineComment(s) {
	return {
		type: "lineComment",
		text: s
	};
}
/** Concatenate multiple docs. Flattens nested concats. */
function concat(...parts) {
	const flat = [];
	for (const part of parts) if (part.type === "concat") flat.push(...part.parts);
	else if (part.type === "text" && part.text === "") continue;
	else flat.push(part);
	if (flat.length === 0) return text("");
	if (flat.length === 1) return flat[0];
	return {
		type: "concat",
		parts: flat
	};
}
/** Increase indent by `n` for the inner doc. */
function nest(n, doc) {
	return {
		type: "nest",
		indent: n,
		doc
	};
}
/** Try to render `doc` flat (on one line). If it doesn't fit, break. */
function group(doc) {
	return {
		type: "group",
		doc
	};
}
/** Render `broken` when the enclosing Group breaks, `flat` when it fits. */
function ifBreak(broken, flat) {
	return {
		type: "ifBreak",
		broken,
		flat
	};
}
/** Join docs with a separator between each pair. */
function join(sep, docs) {
	if (docs.length === 0) return text("");
	const parts = [docs[0]];
	for (let i = 1; i < docs.length; i++) parts.push(sep, docs[i]);
	return concat(...parts);
}
/** Mode of a command on the rendering stack. */
const FLAT = 0;
const BREAK = 1;
/**
* Render a Doc tree to a string.
*
* Uses the Wadler-Lindig "best fit" algorithm: a stack-based traversal
* that decides, for each Group, whether to print it flat or broken.
*
* @param doc The document tree to render.
* @param width Target line width (default 80).
*/
function render(doc, width = 80) {
	const output = [];
	let pos = 0;
	const stack = [[
		0,
		BREAK,
		doc
	]];
	while (stack.length > 0) {
		const [indent, mode, d] = stack.pop();
		switch (d.type) {
			case "text":
				output.push(d.text);
				pos += d.text.length;
				break;
			case "line":
				if (mode === FLAT) {
					output.push(" ");
					pos += 1;
				} else {
					output.push(`\n${" ".repeat(indent)}`);
					pos = indent;
				}
				break;
			case "softline":
				if (mode === FLAT) {} else {
					output.push(`\n${" ".repeat(indent)}`);
					pos = indent;
				}
				break;
			case "hardline":
				output.push(`\n${" ".repeat(indent)}`);
				pos = indent;
				break;
			case "lineComment":
				output.push(d.text);
				output.push(`\n${" ".repeat(indent)}`);
				pos = indent;
				break;
			case "concat":
				for (let i = d.parts.length - 1; i >= 0; i--) stack.push([
					indent,
					mode,
					d.parts[i]
				]);
				break;
			case "nest":
				stack.push([
					indent + d.indent,
					mode,
					d.doc
				]);
				break;
			case "group":
				if (fits(width - pos, [[
					indent,
					FLAT,
					d.doc
				]])) stack.push([
					indent,
					FLAT,
					d.doc
				]);
				else stack.push([
					indent,
					BREAK,
					d.doc
				]);
				break;
			case "ifBreak":
				if (mode === BREAK) stack.push([
					indent,
					mode,
					d.broken
				]);
				else stack.push([
					indent,
					mode,
					d.flat
				]);
				break;
		}
	}
	return output.join("");
}
/**
* Check if a document fits within `remaining` columns when rendered flat.
* Stops early on encountering a HardLine or LineComment (never fits flat).
*/
function fits(remaining, stack) {
	let rem = remaining;
	while (stack.length > 0 && rem >= 0) {
		const [indent, mode, d] = stack.pop();
		switch (d.type) {
			case "text":
				rem -= d.text.length;
				break;
			case "line":
				if (mode === FLAT) rem -= 1;
				else return true;
				break;
			case "softline":
				if (mode === BREAK) return true;
				break;
			case "hardline": return false;
			case "lineComment": return false;
			case "concat":
				for (let i = d.parts.length - 1; i >= 0; i--) stack.push([
					indent,
					mode,
					d.parts[i]
				]);
				break;
			case "nest":
				stack.push([
					indent + d.indent,
					mode,
					d.doc
				]);
				break;
			case "group":
				stack.push([
					indent,
					FLAT,
					d.doc
				]);
				break;
			case "ifBreak":
				if (mode === BREAK) stack.push([
					indent,
					mode,
					d.broken
				]);
				else stack.push([
					indent,
					mode,
					d.flat
				]);
				break;
		}
	}
	return rem >= 0;
}
const INDENT = 2;
function isToken(child) {
	return "text" in child && !("kind" in child);
}
function isNode(child) {
	return "kind" in child;
}
/** Get the Nth token from a node's children. */
function nthToken(node, n) {
	let count = 0;
	for (const child of node.children) if (isToken(child)) {
		if (count === n) return child;
		count++;
	}
	throw new Error(`No token at index ${n} in ${node.kind}`);
}
/** Get the Nth child node from a node's children. */
function nthChild(node, n) {
	let count = 0;
	for (const child of node.children) if (isNode(child)) {
		if (count === n) return child;
		count++;
	}
	throw new Error(`No child node at index ${n} in ${node.kind}`);
}
/** Get all child nodes. */
function childNodes(node) {
	return node.children.filter(isNode);
}
/** Get all tokens. */
function tokens(node) {
	return node.children.filter(isToken);
}
/** Extract comment Docs from trivia, ignoring whitespace. */
function triviaComments(trivia) {
	const docs = [];
	for (const t of trivia) if (t.kind === "lineComment") docs.push(lineComment(t.text));
	else if (t.kind === "blockComment") docs.push(text(t.text));
	return docs;
}
/** Emit trailing comment trivia of a token as Docs. */
function trailingComments(token) {
	return triviaComments(token.trailingTrivia);
}
/** Collect leading block comment Docs from a trivia array. */
function leadingBlockComments(trivia) {
	const docs = [];
	for (const t of trivia) if (t.kind === "blockComment") docs.push(text(t.text));
	return docs;
}
/** Prepend leading block comments to a Doc. Returns the doc unchanged if none. */
function withLeadingBlockComments(trivia, doc) {
	const cmts = leadingBlockComments(trivia);
	if (cmts.length === 0) return doc;
	return concat(...cmts.flatMap((c) => [c, text(" ")]), doc);
}
/**
* Format a token with trailing block comments preserved.
*
* Only emits TRAILING block comments. Leading block comments are NOT emitted
* here — at statement boundaries they're handled by containers (program, body),
* and elsewhere by `formatClosingToken` or `formatExprChild`.
*
* Trailing line comments are NOT emitted — they force line breaks that conflict
* with the enclosing layout. Containers handle trailing line comments.
*/
function formatTokenWithTrivia(token) {
	const tokenDoc = text(token.text);
	const trailingBlocks = [];
	for (const t of token.trailingTrivia) if (t.kind === "blockComment") trailingBlocks.push(text(t.text));
	if (trailingBlocks.length === 0) return tokenDoc;
	const parts = [tokenDoc];
	for (const c of trailingBlocks) parts.push(text(" "), c);
	return concat(...parts);
}
/**
* Format a token with both leading AND trailing block comments.
*
* Use for tokens that are NOT at statement boundaries — closing delimiters
* (`end`, `else`, `)`, `}`), tokens inside binding patterns, and other
* expression-internal tokens where no container handles leading trivia.
*/
function formatClosingToken(token) {
	return withLeadingBlockComments(token.leadingTrivia, formatTokenWithTrivia(token));
}
/**
* Get the trailing line comment from the last token of a subtree, if any.
* Used by containers to emit trailing line comments after semicolons.
*/
function getTrailingLineComment(node) {
	const tok = lastToken(node);
	for (const t of tok.trailingTrivia) if (t.kind === "lineComment") return t.text;
}
/**
* Format a child node inside an expression with leading block comments.
*
* Use for expression-internal child nodes (call args, array elements, binary
* operands, object values) where no container handles leading block comments
* on the node's first token.
*/
function formatExprChild(node) {
	return withLeadingBlockComments(firstToken(node).leadingTrivia, formatNode(node));
}
/**
* Count the number of blank lines in a trivia array.
* A blank line is an empty line (two consecutive newlines in a whitespace node).
* Returns 0 if no blank lines are found.
*/
function countBlankLines(trivia) {
	let total = 0;
	for (const t of trivia) if (t.kind === "whitespace") {
		let newlines = 0;
		for (const ch of t.text) if (ch === "\n") newlines++;
		if (newlines >= 2) total += newlines - 1;
	}
	return total;
}
/**
* Count blank lines in the gap between two tokens.
*
* Trivia split puts some newlines on the trailing side and the rest on the
* leading side. To get the true blank-line count we must count all newlines
* across both sides and subtract 1 (the statement-separating newline).
*/
function countBlankLinesBetweenTokens(prevTrailing, nextLeading) {
	let totalNewlines = 0;
	const lastTrailing = prevTrailing[prevTrailing.length - 1];
	if (lastTrailing?.kind === "whitespace") {
		for (const ch of lastTrailing.text) if (ch === "\n") totalNewlines++;
	}
	const firstLeading = nextLeading[0];
	if (firstLeading?.kind === "whitespace") {
		for (const ch of firstLeading.text) if (ch === "\n") totalNewlines++;
	}
	return Math.max(0, totalNewlines - 1);
}
/** Push up to MAX_BLANK_LINES extra hardLines into `parts` to represent blank lines. */
function pushBlankLines(parts, count) {
	const clamped = Math.min(count, 1);
	for (let i = 0; i < clamped; i++) parts.push(hardLine);
}
function firstToken(node) {
	for (const child of node.children) {
		if (isToken(child)) return child;
		if (isNode(child)) return firstToken(child);
	}
	throw new Error(`No tokens in ${node.kind}`);
}
function lastToken(node) {
	for (let i = node.children.length - 1; i >= 0; i--) {
		const child = node.children[i];
		if (isToken(child)) return child;
		if (isNode(child)) return lastToken(child);
	}
	throw new Error(`No tokens in ${node.kind}`);
}
/**
* Iterator over an UntypedCstNode's children. Provides helpers to consume
* tokens by text and collect child nodes, making it easy to destructure
* untyped tree children positionally.
*/
var ChildIterator = class {
	children;
	pos = 0;
	constructor(children) {
		this.children = children;
	}
	/** True if all children have been consumed. */
	done() {
		return this.pos >= this.children.length;
	}
	/** Peek at the current child without consuming it. */
	peek() {
		return this.children[this.pos];
	}
	/** Consume and return the next child (token or node). */
	next() {
		return this.children[this.pos++];
	}
	/** Consume the next child, expecting it to be a token with the given text. */
	expectToken(tokenText) {
		const child = this.next();
		if (!isToken(child) || child.text !== tokenText) throw new Error(`Expected token '${tokenText}', got ${isToken(child) ? `'${child.text}'` : `node '${child.kind}'`}`);
		return child;
	}
	/** Consume a token with known text and return a Doc with trailing block comments. */
	emitToken(tokenText) {
		return formatTokenWithTrivia(this.expectToken(tokenText));
	}
	/**
	* Consume a token with known text and return a Doc with both leading
	* AND trailing block comments. Use for closing delimiters and tokens
	* that are NOT at statement boundaries (end, else, case, ), }, etc.).
	*/
	emitClosing(tokenText) {
		return formatClosingToken(this.expectToken(tokenText));
	}
	/** Consume the next token (unknown text) and return a Doc with both leading and trailing block comments. */
	emitAnyClosing() {
		return formatClosingToken(this.nextToken());
	}
	/** Consume the next child, expecting it to be a token. Returns the token. */
	nextToken() {
		const child = this.next();
		if (!isToken(child)) throw new Error(`Expected token, got node '${child.kind}'`);
		return child;
	}
	/** Consume the next child, expecting it to be a node. Returns the node. */
	nextNode() {
		const child = this.next();
		if (!isNode(child)) throw new Error(`Expected node, got token '${child.text}'`);
		return child;
	}
	/** Check if the current child is a token with the given text. */
	isToken(tokenText) {
		const child = this.peek();
		return child !== void 0 && isToken(child) && child.text === tokenText;
	}
	/** Save the current position for later restore. */
	save() {
		return this.pos;
	}
	/** Restore to a previously saved position. */
	restore(savedPos) {
		this.pos = savedPos;
	}
	/** Check if the current child is a node. */
	isNode() {
		const child = this.peek();
		return child !== void 0 && isNode(child);
	}
	/**
	* Collect expression nodes and semicolons from a body until a stop token.
	* Returns the formatted body as a Doc[] (one per statement).
	* Does NOT consume the stop token.
	*/
	collectBody(...stopTokens) {
		const stmts = [];
		while (!this.done()) {
			const child = this.peek();
			if (isToken(child) && stopTokens.includes(child.text)) break;
			if (isToken(child) && child.text === ";") {
				this.next();
				continue;
			}
			if (isNode(child)) stmts.push(formatNode(this.nextNode()));
			else this.next();
		}
		return stmts;
	}
};
/** Format a body (list of statements) with semicolon separators. */
function formatBody(stmts) {
	if (stmts.length === 0) return text("");
	if (stmts.length === 1) return stmts[0];
	const parts = [stmts[0]];
	for (let i = 1; i < stmts.length; i++) parts.push(text(";"), hardLine, stmts[i]);
	return concat(...parts);
}
function formatBodyFromIter(iter, ...stopTokens) {
	return formatBodyFromIterInternal(iter, true, stopTokens);
}
function formatBodyFromIterNoTrailingSemi(iter, ...stopTokens) {
	return formatBodyFromIterInternal(iter, false, stopTokens);
}
function formatBodyFromIterInternal(iter, trailingSemi, stopTokens) {
	const stmts = [];
	const semiTokens = [];
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child) && stopTokens.includes(child.text)) break;
		if (isToken(child) && child.text === ";") {
			semiTokens.push(child);
			iter.next();
			continue;
		}
		if (isToken(child) && child.text === "with") {
			iter.next();
			if (iter.isNode()) {
				const withStmt = {
					kind: "WithStatement",
					children: [child, iter.nextNode()]
				};
				stmts.push(withStmt);
			}
		} else if (isNode(child)) stmts.push(iter.nextNode());
		else iter.next();
	}
	if (stmts.length === 0) return { doc: text("") };
	const parts = [];
	const firstTrivia = firstToken(stmts[0]).leadingTrivia;
	emitTriviaWithBlankLines(firstTrivia, parts);
	for (let i = 0; i < stmts.length; i++) {
		const stmt = stmts[i];
		parts.push(formatNode(stmt));
		if (i < stmts.length - 1 || trailingSemi) parts.push(text(";"));
		const stmtTrailingComment = getTrailingLineComment(stmt);
		const semiTrailingComment = i < semiTokens.length ? semiTokens[i].trailingTrivia.find((t) => t.kind === "lineComment")?.text : void 0;
		const trailingLc = stmtTrailingComment ?? semiTrailingComment;
		if (trailingLc && i < stmts.length - 1) parts.push(text(" "), lineComment(trailingLc));
		if (i < stmts.length - 1) {
			const nextFirst = firstToken(stmts[i + 1]);
			const blankCount = countBlankLinesBetweenTokens(((i < semiTokens.length ? semiTokens[i] : void 0) ?? lastToken(stmt)).trailingTrivia, nextFirst.leadingTrivia);
			if (trailingLc) {
				if (blankCount > 0) pushBlankLines(parts, blankCount);
			} else if (blankCount > 0) {
				parts.push(hardLine);
				pushBlankLines(parts, blankCount);
			} else parts.push(hardLine);
			const nextTrivia = nextFirst.leadingTrivia;
			for (let t = 0; t < nextTrivia.length; t++) {
				const tv = nextTrivia[t];
				if (tv.kind === "lineComment") {
					parts.push(lineComment(tv.text));
					const nextWs = nextTrivia[t + 1];
					if (nextWs?.kind === "whitespace") {
						const bc = countBlankLines([nextWs]);
						if (bc > 0) pushBlankLines(parts, bc);
					}
				} else if (tv.kind === "blockComment") parts.push(text(tv.text), hardLine);
				else if (tv.kind === "whitespace") {
					const bc = countBlankLines([tv]);
					const nextTv = nextTrivia[t + 1];
					if (bc > 0 && nextTv && (nextTv.kind === "lineComment" || nextTv.kind === "blockComment")) pushBlankLines(parts, bc);
				}
			}
		}
	}
	const lastStmt = stmts[stmts.length - 1];
	const lastSemi = semiTokens[stmts.length - 1];
	const lastTrailingLc = getTrailingLineComment(lastStmt) ?? lastSemi?.trailingTrivia.find((t) => t.kind === "lineComment")?.text;
	return {
		doc: concat(...parts),
		lastTrailingComment: lastTrailingLc
	};
}
function formatCst(tree, trailingTrivia) {
	let result = render(formatProgram(tree, trailingTrivia), 80);
	result = result.replace(/[ \t]+$/gm, "").replace(/^\n+/, "");
	if (result.length > 0 && !result.endsWith("\n")) result += "\n";
	return result;
}
function formatProgram(program, trailingTrivia) {
	const statements = childNodes(program);
	if (statements.length === 0) {
		const parts = [];
		for (let i = 0; i < trailingTrivia.length; i++) {
			const t = trailingTrivia[i];
			if (t.kind === "shebang") parts.push(text(t.text), hardLine);
			else if (t.kind === "lineComment") parts.push(lineComment(t.text));
			else if (t.kind === "blockComment") parts.push(text(t.text), hardLine);
			else if (t.kind === "whitespace") {
				const bc = countBlankLines([t]);
				const nextT = trailingTrivia[i + 1];
				if (bc > 0 && nextT && (nextT.kind === "lineComment" || nextT.kind === "blockComment")) pushBlankLines(parts, bc);
			}
		}
		if (parts.length === 0) return text("");
		return concat(...parts);
	}
	const first = firstToken(program);
	const shebangDocs = [];
	for (const t of first.leadingTrivia) if (t.kind === "shebang") shebangDocs.push(text(t.text), hardLine);
	const parts = [...shebangDocs];
	const firstTrivia = firstToken(statements[0]).leadingTrivia;
	emitTriviaWithBlankLines(firstTrivia, parts);
	const semicolonTokens = tokens(program);
	let lastTrailingComment;
	for (let i = 0; i < statements.length; i++) {
		const stmt = statements[i];
		parts.push(formatNode(stmt));
		parts.push(text(";"));
		const stmtTrailingComment = getTrailingLineComment(stmt);
		const semiToken = i < semicolonTokens.length ? semicolonTokens[i] : void 0;
		const semiTrailingLine = semiToken?.trailingTrivia.find((t) => t.kind === "lineComment")?.text;
		const semiTrailingBlocks = semiToken?.trailingTrivia.filter((t) => t.kind === "blockComment") ?? [];
		const trailingComment = stmtTrailingComment ?? semiTrailingLine;
		lastTrailingComment = trailingComment;
		for (const bc of semiTrailingBlocks) parts.push(text(" "), text(bc.text));
		if (trailingComment) parts.push(text(" "), lineComment(trailingComment));
		if (i < statements.length - 1) {
			const lastTok = lastToken(stmt);
			const nextFirst = firstToken(statements[i + 1]);
			const blankCount = countBlankLinesBetweenTokens((semiToken ?? lastTok).trailingTrivia, nextFirst.leadingTrivia);
			if (trailingComment) {
				if (blankCount > 0) pushBlankLines(parts, blankCount);
			} else if (blankCount > 0) {
				parts.push(hardLine);
				pushBlankLines(parts, blankCount);
			} else parts.push(hardLine);
			const nextTrivia = nextFirst.leadingTrivia;
			for (let t = 0; t < nextTrivia.length; t++) {
				const trivia = nextTrivia[t];
				if (trivia.kind === "lineComment") {
					parts.push(lineComment(trivia.text));
					const nextWs = nextTrivia[t + 1];
					if (nextWs && nextWs.kind === "whitespace") {
						const bc = countBlankLines([nextWs]);
						if (bc > 0) pushBlankLines(parts, bc);
					}
				} else if (trivia.kind === "blockComment") parts.push(text(trivia.text), hardLine);
				if (trivia.kind === "whitespace") {
					const bc = countBlankLines([trivia]);
					const nextTriv = nextTrivia[t + 1];
					if (bc > 0 && nextTriv && (nextTriv.kind === "lineComment" || nextTriv.kind === "blockComment")) pushBlankLines(parts, bc);
				}
			}
		}
	}
	if (trailingTrivia.some((t) => t.kind === "lineComment" || t.kind === "blockComment")) {
		if (!lastTrailingComment) parts.push(hardLine);
		emitTriviaWithBlankLines(trailingTrivia, parts);
	}
	return concat(...parts);
}
function formatNode(node) {
	switch (node.kind) {
		case "NumberLiteral":
		case "StringLiteral":
		case "AtomLiteral":
		case "TemplateString":
		case "RegexpShorthand":
		case "Symbol":
		case "EffectName":
		case "ReservedSymbol": return formatLeaf(node);
		case "Array": return formatArray(node);
		case "Object": return formatObject(node);
		case "BinaryOp": return formatBinaryOp(node);
		case "PrefixOp": return formatPrefixOp(node);
		case "PropertyAccess": return formatPropertyAccess(node);
		case "IndexAccess": return formatIndexAccess(node);
		case "Call": return formatCall(node);
		case "Parenthesized": return formatParenthesized(node);
		case "Spread": return formatSpread(node);
		case "Let": return formatLet(node);
		case "TypeAlias": return formatTypeAlias(node);
		case "If": return formatIf(node);
		case "Block": return formatBlock(node);
		case "Loop": return formatLoop(node);
		case "For": return formatFor(node);
		case "Match": return formatMatch(node);
		case "Function": return formatFunction(node);
		case "Handler": return formatHandler(node);
		case "Resume": return formatResume(node);
		case "Macro": return formatMacro(node);
		case "MacroCall": return formatMacroCall(node);
		case "Quote": return formatQuote(node);
		case "Splice": return formatSplice(node);
		case "WithStatement": return formatFromChildren(node);
		default: return formatFallback(node);
	}
}
function formatLeaf(node) {
	return formatTokenWithTrivia(nthToken(node, 0));
}
function formatArray(node) {
	const children = childNodes(node);
	if (children.length === 0) {
		const allToks = tokens(node);
		const openTok = allToks.find((t) => t.text === "[");
		const closeTok = allToks.find((t) => t.text === "]");
		const innerComments = [...openTok ? leadingBlockComments(openTok.trailingTrivia) : [], ...closeTok ? leadingBlockComments(closeTok.leadingTrivia) : []];
		if (innerComments.length > 0) return concat(text("["), ...innerComments.flatMap((c) => [text(" "), c]), text(" ]"));
		return text("[]");
	}
	const items = [];
	const toks = tokens(node);
	const commaTokens = toks.filter((t) => t.text === ",");
	for (let i = 0; i < children.length; i++) {
		const itemDoc = formatExprChild(children[i]);
		if (i > 0 && i - 1 < commaTokens.length) {
			const comma = commaTokens[i - 1];
			const trailingCmts = trailingComments(comma);
			if (trailingCmts.length > 0) {
				items.push(concat(...trailingCmts, text(" "), itemDoc));
				continue;
			}
		}
		items.push(itemDoc);
	}
	const separators = [];
	for (let i = 0; i < children.length - 1; i++) {
		const comma = i < commaTokens.length ? commaTokens[i] : void 0;
		const prevLast = lastToken(children[i]);
		const nextFirst = firstToken(children[i + 1]);
		const blankCount = comma ? countBlankLinesBetweenTokens(comma.trailingTrivia, nextFirst.leadingTrivia) : countBlankLinesBetweenTokens(prevLast.trailingTrivia, nextFirst.leadingTrivia);
		if (blankCount > 0) {
			const blanks = [text(","), hardLine];
			pushBlankLines(blanks, blankCount);
			separators.push(concat(...blanks));
		} else separators.push(concat(text(","), line));
	}
	const innerParts = [items[0]];
	for (let i = 1; i < items.length; i++) innerParts.push(separators[i - 1], items[i]);
	const trailingComma = ifBreak(text(","), text(""));
	const openBracketToken = toks.find((t) => t.text === "[");
	const closeBracketToken = toks.find((t) => t.text === "]");
	const openDoc = openBracketToken ? formatTokenWithTrivia(openBracketToken) : text("[");
	const closeDoc = closeBracketToken ? formatClosingToken(closeBracketToken) : text("]");
	return group(concat(openDoc, nest(INDENT, concat(softLine, concat(...innerParts), trailingComma)), softLine, closeDoc));
}
function formatObject(node) {
	const iter = new ChildIterator(node.children);
	const openBraceTrivia = trailingComments(iter.expectToken("{"));
	const entries = [];
	let pendingCommentDocs = [...openBraceTrivia.map((c) => concat(c, text(" ")))];
	while (!iter.done() && !iter.isToken("}")) {
		if (iter.isToken(",")) {
			const comma = iter.nextToken();
			for (const t of comma.trailingTrivia) if (t.kind === "blockComment") pendingCommentDocs.push(text(t.text), text(" "));
			continue;
		}
		if (iter.isNode() && iter.peek().kind === "Spread") {
			entries.push(formatExprChild(iter.nextNode()));
			continue;
		}
		const entryParts = [];
		if (iter.isNode()) entryParts.push(formatExprChild(iter.nextNode()));
		else entryParts.push(iter.emitAnyClosing());
		if (iter.isToken("[")) {}
		if (iter.isToken(":")) {
			const colonDoc = formatTokenWithTrivia(iter.nextToken());
			entryParts.push(colonDoc, text(" "));
			if (iter.isNode()) entryParts.push(formatExprChild(iter.nextNode()));
		}
		if (pendingCommentDocs.length > 0) {
			entries.push(concat(...pendingCommentDocs, ...entryParts));
			pendingCommentDocs = [];
		} else entries.push(concat(...entryParts));
	}
	const closeBraceDoc = iter.emitClosing("}");
	if (entries.length === 0) {
		if (openBraceTrivia.length > 0) return concat(text("{"), ...openBraceTrivia.flatMap((c) => [text(" "), c]), text(" "), closeBraceDoc);
		return concat(text("{"), closeBraceDoc);
	}
	const trailingComma = ifBreak(text(","), text(""));
	return group(concat(text("{"), nest(INDENT, concat(line, join(concat(text(","), line), entries), trailingComma)), line, closeBraceDoc));
}
function formatBinaryOp(node) {
	const children = childNodes(node);
	const toks = tokens(node);
	if (toks.length > 0) {
		const left = children[0];
		const right = children[1];
		const opDoc = formatTokenWithTrivia(toks[0]);
		return group(concat(formatNode(left), text(" "), opDoc, nest(INDENT, concat(line, formatExprChild(right)))));
	}
	if (children.length >= 3) {
		const left = children[0];
		const op = children[1];
		const right = children[2];
		return group(concat(formatNode(left), text(" "), formatExprChild(op), nest(INDENT, concat(line, formatExprChild(right)))));
	}
	return formatFromChildren(node);
}
function formatPrefixOp(node) {
	const operand = nthChild(node, 0);
	return concat(formatTokenWithTrivia(nthToken(node, 0)), formatExprChild(operand));
}
function formatPropertyAccess(node) {
	const object = nthChild(node, 0);
	const toks = tokens(node);
	const dotToken = toks[0];
	const propToken = toks.length >= 2 ? toks[1] : toks[0];
	return concat(formatNode(object), formatTokenWithTrivia(dotToken), toks.length >= 2 ? formatTokenWithTrivia(propToken) : text(""));
}
function formatIndexAccess(node) {
	const object = nthChild(node, 0);
	const index = nthChild(node, 1);
	const toks = tokens(node);
	const openBracket = toks.find((t) => t.text === "[");
	const closeBracket = toks.find((t) => t.text === "]");
	return concat(formatNode(object), openBracket ? formatTokenWithTrivia(openBracket) : text("["), formatExprChild(index), closeBracket ? formatClosingToken(closeBracket) : text("]"));
}
function formatCall(node) {
	const children = childNodes(node);
	const fn = children[0];
	const args = children.slice(1);
	const toks = tokens(node);
	const openParen = toks.find((t) => t.text === "(");
	const closeParen = toks.find((t) => t.text === ")");
	if (args.length === 0) return concat(formatNode(fn), openParen ? formatTokenWithTrivia(openParen) : text("("), closeParen ? formatClosingToken(closeParen) : text(")"));
	const commaTokens = toks.filter((t) => t.text === ",");
	const argDocs = [];
	for (let i = 0; i < args.length; i++) {
		const argDoc = formatExprChild(args[i]);
		if (i > 0 && i - 1 < commaTokens.length) {
			const trailingBlocks = commaTokens[i - 1].trailingTrivia.filter((t) => t.kind === "blockComment");
			if (trailingBlocks.length > 0) {
				argDocs.push(concat(...trailingBlocks.map((t) => concat(text(t.text), text(" "))), argDoc));
				continue;
			}
		}
		argDocs.push(argDoc);
	}
	const openDoc = openParen ? formatTokenWithTrivia(openParen) : text("(");
	const lastArg = args[args.length - 1];
	if (args.length >= 2 && lastArg.kind === "Function" && childNodes(lastArg).some((c) => c.kind === "Block")) {
		const leadingArgDocs = argDocs.slice(0, -1);
		const trailingArgDoc = argDocs[argDocs.length - 1];
		return group(concat(formatNode(fn), openDoc, join(concat(text(","), text(" ")), leadingArgDocs), text(", "), trailingArgDoc, closeParen ? formatClosingToken(closeParen) : text(")")));
	}
	return concat(formatNode(fn), group(concat(openDoc, nest(INDENT, concat(softLine, join(concat(text(","), line), argDocs))), softLine, closeParen ? formatClosingToken(closeParen) : text(")"))));
}
function formatParenthesized(node) {
	const inner = nthChild(node, 0);
	const toks = tokens(node);
	const openParen = toks.find((t) => t.text === "(");
	const closeParen = toks.find((t) => t.text === ")");
	return concat(openParen ? formatTokenWithTrivia(openParen) : text("("), formatExprChild(inner), closeParen ? formatClosingToken(closeParen) : text(")"));
}
function formatSpread(node) {
	const expr = nthChild(node, 0);
	return concat(formatTokenWithTrivia(nthToken(node, 0)), formatExprChild(expr));
}
function formatLet(node) {
	const iter = new ChildIterator(node.children);
	const letDoc = iter.emitToken("let");
	const targetParts = [];
	let lastTargetText = "";
	let bracketDepth = 0;
	while (!iter.done() && !(iter.isToken("=") && bracketDepth === 0)) {
		const peeked = iter.peek();
		if (isToken(peeked) && (peeked.text === "[" || peeked.text === "{")) bracketDepth++;
		if (isToken(peeked) && (peeked.text === "]" || peeked.text === "}")) bracketDepth--;
		const child = iter.next();
		if (isToken(child)) {
			const t = child.text;
			if (t === "{") {
				if (targetParts.length > 0) targetParts.push(text(" "));
				targetParts.push(formatTokenWithTrivia(child), text(" "));
				lastTargetText = "{ ";
				continue;
			}
			if (t === "}") {
				targetParts.push(text(" "), formatClosingToken(child));
				lastTargetText = "}";
				continue;
			}
			if (t === ",") {
				targetParts.push(formatTokenWithTrivia(child), text(" "));
				lastTargetText = ", ";
				continue;
			}
			const postfixBracket = t === "[" && lastTargetText.length > 0 && /[)\]A-Za-z_0-9]$/.test(lastTargetText);
			if (targetParts.length > 0 && !isPunctuation(t) && !isOpenBracket(lastTargetText) && lastTargetText !== "..." && !lastTargetText.endsWith(" ") && !postfixBracket) targetParts.push(text(" "));
			targetParts.push(formatClosingToken(child));
			lastTargetText = t;
		} else {
			if (targetParts.length > 0 && !isOpenBracket(lastTargetText) && lastTargetText !== "..." && !lastTargetText.endsWith(" ")) targetParts.push(text(" "));
			targetParts.push(formatExprChild(child));
			lastTargetText = "";
		}
	}
	const eqDoc = iter.emitToken("=");
	const value = iter.nextNode();
	const header = concat(letDoc, text(" "), concat(...targetParts), text(" "), eqDoc, text(" "));
	const valueDoc = formatExprChild(value);
	if (value.kind === "Function" || value.kind === "Block") return concat(header, valueDoc);
	if (value.kind === "Array" || value.kind === "Object") return concat(header, valueDoc);
	return group(concat(letDoc, text(" "), concat(...targetParts), text(" "), eqDoc, nest(INDENT, concat(line, valueDoc))));
}
function formatIf(node) {
	const iter = new ChildIterator(node.children);
	const branches = [];
	let elseBranchDoc;
	let elseBranchCount = 0;
	let elseLastComment;
	let elseDoc;
	let pendingElseDoc;
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child) && child.text === "end") break;
		if (isToken(child) && child.text === "else") {
			const elseKwDoc = iter.emitClosing("else");
			if (iter.isToken("if")) pendingElseDoc = elseKwDoc;
			else {
				elseDoc = elseKwDoc;
				elseBranchCount = countNodesUntil(iter, "end");
				const elseResult = formatBodyFromIterNoTrailingSemi(iter, "end");
				elseBranchDoc = elseResult.doc;
				elseLastComment = elseResult.lastTrailingComment;
				break;
			}
		}
		if (isToken(iter.peek()) && iter.peek().text === "if") {
			const ifDoc = branches.length === 0 ? iter.emitToken("if") : iter.emitClosing("if");
			const condition = iter.nextNode();
			const thenDoc = iter.emitClosing("then");
			const bodyCount = countNodesUntil(iter, "else", "end");
			const bodyResult = formatBodyFromIterNoTrailingSemi(iter, "else", "end");
			branches.push({
				isElseIf: branches.length > 0,
				elseDoc: pendingElseDoc,
				ifDoc,
				condition: formatExprChild(condition),
				thenDoc,
				bodyDoc: bodyResult.doc,
				bodyCount,
				lastTrailingComment: bodyResult.lastTrailingComment
			});
			pendingElseDoc = void 0;
		}
	}
	const endDoc = iter.emitClosing("end");
	const allTrailingComments = [branches[0]?.lastTrailingComment, elseLastComment].filter(Boolean);
	const trailingCmtDoc = allTrailingComments.length > 0 ? concat(text(" "), text(allTrailingComments.join(" "))) : text("");
	if (branches.length === 1 && branches[0].bodyCount === 1 && (elseBranchDoc === void 0 || elseBranchCount === 1)) {
		const b = branches[0];
		const flatParts = [
			b.ifDoc,
			text(" "),
			b.condition,
			text(" "),
			b.thenDoc,
			text(" "),
			b.bodyDoc
		];
		if (elseBranchDoc && elseDoc) flatParts.push(text(" "), elseDoc, text(" "), elseBranchDoc);
		flatParts.push(text(" "), endDoc, trailingCmtDoc);
		const expandedParts = [
			b.ifDoc,
			text(" "),
			b.condition,
			text(" "),
			b.thenDoc
		];
		expandedParts.push(nest(INDENT, concat(hardLine, b.bodyDoc)));
		if (b.lastTrailingComment) expandedParts.push(text(" "), text(b.lastTrailingComment));
		if (elseBranchDoc && elseDoc) {
			expandedParts.push(hardLine, elseDoc);
			expandedParts.push(nest(INDENT, concat(hardLine, elseBranchDoc)));
			if (elseLastComment) expandedParts.push(text(" "), text(elseLastComment));
		}
		expandedParts.push(hardLine, endDoc);
		return group(concat(ifBreak(concat(...expandedParts), concat(...flatParts))));
	}
	const parts = [];
	for (const b of branches) {
		if (b.isElseIf) parts.push(hardLine, b.elseDoc ?? text("else"), text(" "), b.ifDoc, text(" "), b.condition, text(" "), b.thenDoc);
		else parts.push(b.ifDoc, text(" "), b.condition, text(" "), b.thenDoc);
		parts.push(nest(INDENT, concat(hardLine, b.bodyDoc)));
		if (b.lastTrailingComment) parts.push(text(" "), text(b.lastTrailingComment));
	}
	if (elseBranchDoc) {
		parts.push(hardLine, elseDoc ?? text("else"));
		parts.push(nest(INDENT, concat(hardLine, elseBranchDoc)));
		if (elseLastComment) parts.push(text(" "), text(elseLastComment));
	}
	parts.push(hardLine, endDoc);
	return concat(...parts);
}
function formatBlock(node) {
	const iter = new ChildIterator(node.children);
	const doDoc = iter.emitToken("do");
	let withClause;
	if (iter.isToken("with")) {
		const withDoc = iter.emitClosing("with");
		const handler = iter.nextNode();
		const semiDoc = iter.emitToken(";");
		withClause = concat(withDoc, text(" "), formatExprChild(handler), semiDoc);
	}
	const bodyStmtCount = countNodesUntil(iter, "end");
	const bodyResult = formatBodyFromIter(iter, "end");
	const bodyDoc = bodyResult.doc;
	const endDoc = iter.emitClosing("end");
	if (bodyDoc.type === "text" && bodyDoc.text === "") {
		if (withClause) return concat(doDoc, nest(INDENT, concat(hardLine, withClause)), hardLine, endDoc);
		return concat(doDoc, text(" "), endDoc);
	}
	if (bodyStmtCount === 1 && !withClause) {
		const bodyNodes = node.children.filter(isNode).filter((c) => c.kind !== "Block");
		if (!(bodyNodes.length > 0 ? firstToken(bodyNodes[bodyNodes.length - 1]) : void 0)?.leadingTrivia.some((t) => t.kind === "lineComment" || t.kind === "blockComment")) return group(concat(doDoc, nest(INDENT, concat(line, ifBreak(bodyDoc, bodyNodes.length > 0 ? formatExprChild(bodyNodes[bodyNodes.length - 1]) : bodyDoc))), bodyResult.lastTrailingComment ? concat(text(" "), text(bodyResult.lastTrailingComment), hardLine) : line, endDoc));
	}
	const inner = withClause ? concat(hardLine, withClause, hardLine, bodyDoc) : concat(hardLine, bodyDoc);
	const trailingCmt = bodyResult.lastTrailingComment ? concat(text(" "), text(bodyResult.lastTrailingComment)) : text("");
	return concat(doDoc, nest(INDENT, inner), trailingCmt, hardLine, endDoc);
}
function formatLoop(node) {
	return formatFromChildren(node);
}
function formatFor(node) {
	return formatFromChildren(node);
}
function formatMatch(node) {
	const iter = new ChildIterator(node.children);
	const matchDoc = iter.emitToken("match");
	const expr = iter.nextNode();
	const parts = [
		matchDoc,
		text(" "),
		formatExprChild(expr)
	];
	while (!iter.done() && !iter.isToken("end")) if (iter.isToken("case")) {
		const caseDoc = iter.emitClosing("case");
		const patternParts = [];
		let lastPatternText = "";
		while (!iter.done() && !iter.isToken("then") && !iter.isToken("when")) {
			const child = iter.next();
			if (isToken(child)) {
				if (patternParts.length > 0 && !isPunctuation(child.text) && lastPatternText !== "...") patternParts.push(text(" "));
				patternParts.push(formatClosingToken(child));
				lastPatternText = child.text;
			} else {
				if (patternParts.length > 0 && lastPatternText !== "...") patternParts.push(text(" "));
				patternParts.push(formatExprChild(child));
				lastPatternText = "";
			}
		}
		let guardDoc;
		if (iter.isToken("when")) {
			const whenDoc = iter.emitClosing("when");
			const guard = iter.nextNode();
			guardDoc = concat(text(" "), whenDoc, text(" "), formatExprChild(guard));
		}
		const thenDoc = iter.emitClosing("then");
		const body = iter.collectBody("case", "end");
		parts.push(nest(INDENT, concat(hardLine, caseDoc, text(" "), concat(...patternParts), guardDoc ?? text(""), text(" "), thenDoc, body.length === 1 ? concat(text(" "), body[0]) : nest(INDENT, concat(hardLine, formatBody(body))))));
	} else iter.next();
	const endDoc = iter.emitClosing("end");
	parts.push(hardLine, endDoc);
	return concat(...parts);
}
function formatFunction(node) {
	const iter = new ChildIterator(node.children);
	const paramParts = [];
	while (!iter.done() && !iter.isToken("->")) {
		const child = iter.next();
		if (isToken(child)) if (child.text === "(" || child.text === ")") paramParts.push(formatTokenWithTrivia(child));
		else if (child.text === ",") {
			paramParts.push(formatTokenWithTrivia(child));
			paramParts.push(text(" "));
		} else if (child.text === ":") paramParts.push(formatTokenWithTrivia(child));
		else if (child.text === "...") paramParts.push(formatTokenWithTrivia(child));
		else if (child.text === "=") paramParts.push(text(" "), formatTokenWithTrivia(child), text(" "));
		else {
			if (paramParts.length > 0 && !isPunctuation(child.text)) paramParts.push(text(" "));
			paramParts.push(formatTokenWithTrivia(child));
		}
		else paramParts.push(formatExprChild(child));
	}
	const arrowDoc = iter.emitToken("->");
	const body = iter.nextNode();
	return group(concat(...paramParts, paramParts.length > 0 ? text(" ") : text(""), arrowDoc, text(" "), formatExprChild(body)));
}
function formatHandler(node) {
	const iter = new ChildIterator(node.children);
	const parts = [];
	while (iter.isToken("shallow") || iter.isToken("linear")) if (iter.isToken("shallow")) parts.push(iter.emitClosing("shallow"), text(" "));
	else parts.push(iter.emitClosing("linear"), text(" "));
	parts.push(iter.emitToken("handler"));
	const clauseParts = [];
	while (!iter.done() && !iter.isToken("end")) if (iter.isToken("transform")) {
		const transformParts = [iter.emitClosing("transform")];
		while (!iter.done() && !iter.isToken("end")) {
			const child = iter.next();
			if (isToken(child)) if (child.text === "->") transformParts.push(text(" "), formatTokenWithTrivia(child), text(" "));
			else transformParts.push(text(" "), formatTokenWithTrivia(child));
			else transformParts.push(text(" "), formatExprChild(child));
		}
		clauseParts.push(concat(...transformParts));
	} else {
		const clauseTokens = [];
		while (!iter.done() && !iter.isToken("end") && !iter.isToken("transform")) {
			const child = iter.peek();
			if (isToken(child) && child.text.startsWith("@") && clauseTokens.length > 0) break;
			iter.next();
			if (isToken(child)) if (child.text === "->") clauseTokens.push(text(" "), formatTokenWithTrivia(child), text(" "));
			else if (child.text === ":") clauseTokens.push(formatTokenWithTrivia(child));
			else if (child.text === "," || child.text === "(" || child.text === ")") clauseTokens.push(formatTokenWithTrivia(child));
			else {
				if (clauseTokens.length > 0) clauseTokens.push(text(" "));
				clauseTokens.push(formatTokenWithTrivia(child));
			}
			else clauseTokens.push(formatExprChild(child));
		}
		if (clauseTokens.length > 0) clauseParts.push(concat(...clauseTokens));
	}
	const endDoc = iter.emitClosing("end");
	if (clauseParts.length === 0) return concat(...parts, text(" "), endDoc);
	return concat(...parts, nest(INDENT, concat(...clauseParts.map((c) => concat(hardLine, c)))), hardLine, endDoc);
}
function formatResume(node) {
	const iter = new ChildIterator(node.children);
	const resumeDoc = iter.emitToken("resume");
	if (iter.isToken("(")) {
		const openDoc = iter.emitToken("(");
		if (iter.isNode()) {
			const arg = iter.nextNode();
			const closeDoc = iter.emitClosing(")");
			return concat(resumeDoc, openDoc, formatExprChild(arg), closeDoc);
		}
		return concat(resumeDoc, openDoc, iter.emitClosing(")"));
	}
	return resumeDoc;
}
function formatMacro(node) {
	const iter = new ChildIterator(node.children);
	const paramParts = [iter.emitToken("macro")];
	let firstParamToken = true;
	while (!iter.done() && !iter.isToken("->")) {
		const child = iter.next();
		if (isToken(child)) {
			if (child.text === "(" || child.text === ")") paramParts.push(formatTokenWithTrivia(child));
			else if (child.text === ",") {
				paramParts.push(formatTokenWithTrivia(child));
				paramParts.push(text(" "));
			} else if (child.text === ":") paramParts.push(formatTokenWithTrivia(child));
			else if (child.text === "...") paramParts.push(formatTokenWithTrivia(child));
			else if (child.text === "=") paramParts.push(text(" "), formatTokenWithTrivia(child), text(" "));
			else {
				if (!firstParamToken && !isPunctuation(child.text)) paramParts.push(text(" "));
				paramParts.push(formatTokenWithTrivia(child));
			}
			firstParamToken = false;
		} else {
			paramParts.push(formatExprChild(child));
			firstParamToken = false;
		}
	}
	const arrowDoc = iter.emitToken("->");
	const body = iter.nextNode();
	return group(concat(...paramParts, text(" "), arrowDoc, text(" "), formatExprChild(body)));
}
function formatMacroCall(node) {
	const children = childNodes(node);
	const prefixDoc = formatTokenWithTrivia(nthToken(node, 0));
	if (children.length > 0) return concat(prefixDoc, text(" "), formatExprChild(children[0]));
	return prefixDoc;
}
function formatQuote(node) {
	const iter = new ChildIterator(node.children);
	const quoteDoc = iter.emitToken("quote");
	const statements = [[]];
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child)) {
			if (child.text === "end") break;
			if (child.text === ";") {
				iter.next();
				statements.push([]);
				continue;
			}
		}
		statements[statements.length - 1].push(iter.next());
	}
	if (statements.length > 1 && statements[statements.length - 1].length === 0) statements.pop();
	const endDoc = iter.emitClosing("end");
	if (statements.length === 1 && statements[0].length === 0) return concat(quoteDoc, text(" "), endDoc);
	if (statements.length === 1) return group(concat(quoteDoc, nest(INDENT, concat(line, formatTokenSequence(statements[0]), ifBreak(text(";"), text("")))), line, endDoc));
	return concat(quoteDoc, nest(INDENT, concat(...statements.map((stmt) => concat(formatTokenSequence(stmt), text(";"))).map((d) => concat(hardLine, d)))), hardLine, endDoc);
}
/**
* Format an arbitrary slice of CST children using the same spacing rules as
* formatFromChildren. Used by formatQuote to render each split statement
* independently while keeping splice/identifier/operator spacing consistent.
*
* The isFirst handling mirrors formatFromChildren: the first child uses
* formatTokenWithTrivia (trailing trivia only), subsequent children use
* formatClosingToken (both leading and trailing). This is asymmetric on
* purpose — formatFromChildren assumes its container handles leading trivia
* at a statement boundary. Here, each split statement IS a statement
* boundary, so the first child matches that contract.
*
* Known limitation: line comments that land between tokens of the same
* statement are dropped, because the body was re-parsed from a trivia
* stream that only carries block comments and whitespace through to the
* rebuilt CstTokens. Mid-body comment preservation would require walking
* splice sub-nodes' own trivia or attaching line-comment trivia to
* adjacent non-trivia tokens — out of scope for the quote-body rebuild.
*/
function formatTokenSequence(children) {
	const parts = [];
	let prevText = "";
	let isFirst = true;
	for (const child of children) {
		if (isToken(child)) {
			if (prevText && needsSpaceBetween(prevText, child.text)) parts.push(text(" "));
			parts.push(isFirst ? formatTokenWithTrivia(child) : formatClosingToken(child));
			prevText = child.text;
		} else {
			if (prevText && !isOpenBracket(prevText) && prevText !== ".") parts.push(text(" "));
			parts.push(isFirst ? formatNode(child) : formatExprChild(child));
			prevText = "_";
		}
		isFirst = false;
	}
	return concat(...parts);
}
function formatSplice(node) {
	return formatFromChildren(node);
}
/**
* Generic formatter that reconstructs output from children in order.
* Tokens are emitted with proper spacing, child nodes are recursively formatted.
* This is used for complex constructs where specialized formatting hasn't been
* implemented yet — it produces correct (lossless-ish) but unformatted output.
*/
/**
* Format a `type X[<T...>] = TypeExpr` declaration. Type expressions
* have their own grammar and aren't fully formatted here — the
* implementation walks the raw token stream and uses the standard
* `needsSpaceBetween` rules with a small override for generic angle
* brackets, which the regular fallback would space-pad incorrectly
* (turning `Foo<T>` into `Foo < T >`).
*
* Tokens flow into the CST in source order via `ctx.advance()`
* during type-decl parsing, so the children list contains every
* token of the declaration body in sequence. No nested CstNodes
* appear here today — type expressions don't go through the
* expression parser.
*/
function formatTypeAlias(node) {
	const parts = [];
	let prevText = "";
	let isFirst = true;
	let angleDepth = 0;
	let braceDepth = 0;
	for (const child of node.children) {
		if (!isToken(child)) {
			parts.push(isFirst ? formatNode(child) : formatExprChild(child));
			prevText = "_";
			isFirst = false;
			continue;
		}
		const t = child.text;
		const inGenerics = angleDepth > 0 && braceDepth === 0;
		let space = "";
		if (parts.length > 0) {
			if (t === "<" && /^[A-Z_a-z]/.test(prevText) && braceDepth === 0) space = "";
			else if (t === ">" && inGenerics) space = "";
			else if (prevText === "<" && braceDepth === 0) space = "";
			else if (prevText === ":" && inGenerics) space = " ";
			else if (t === ":" && inGenerics) space = "";
			else if (t === "," && inGenerics) space = "";
			else if (prevText === "," && inGenerics) space = " ";
			else if (needsSpaceBetween(prevText, t)) space = " ";
		}
		if (space) parts.push(text(space));
		parts.push(isFirst ? formatTokenWithTrivia(child) : formatClosingToken(child));
		if (t === "<" && /^[A-Z_a-z]/.test(prevText) && braceDepth === 0) angleDepth++;
		else if (t === ">" && inGenerics) angleDepth--;
		else if (t === "{") braceDepth++;
		else if (t === "}") braceDepth--;
		prevText = t;
		isFirst = false;
	}
	return concat(...parts);
}
function formatFromChildren(node) {
	const parts = [];
	let prevText = "";
	let isFirst = true;
	for (const child of node.children) {
		if (isToken(child)) {
			if (prevText && needsSpaceBetween(prevText, child.text)) parts.push(text(" "));
			parts.push(isFirst ? formatTokenWithTrivia(child) : formatClosingToken(child));
			prevText = child.text;
		} else {
			if (prevText && !isOpenBracket(prevText) && prevText !== ".") parts.push(text(" "));
			parts.push(isFirst ? formatNode(child) : formatExprChild(child));
			prevText = "_";
		}
		isFirst = false;
	}
	return concat(...parts);
}
function formatFallback(node) {
	return formatFromChildren(node);
}
/**
* Emit trivia (comments with blank lines preserved) into a Doc parts array.
* Used for preamble, epilogue, and inter-statement trivia.
*/
function emitTriviaWithBlankLines(trivia, parts) {
	for (let i = 0; i < trivia.length; i++) {
		const t = trivia[i];
		if (t.kind === "lineComment") parts.push(lineComment(t.text));
		else if (t.kind === "blockComment") parts.push(text(t.text), hardLine);
		else if (t.kind === "whitespace") {
			const bc = countBlankLines([t]);
			const nextT = trivia[i + 1];
			if (bc > 0 && (!nextT || nextT.kind === "lineComment" || nextT.kind === "blockComment")) pushBlankLines(parts, bc);
		}
	}
}
/** Count child nodes in an iterator until a stop token (without consuming). */
function countNodesUntil(iter, ...stopTokens) {
	let count = 0;
	const saved = iter.save();
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child) && stopTokens.includes(child.text)) break;
		if (isToken(child) && child.text === ";") {
			iter.next();
			continue;
		}
		if (isNode(child)) {
			count++;
			iter.next();
			continue;
		}
		iter.next();
	}
	iter.restore(saved);
	return count;
}
/** Check if a token text is punctuation that shouldn't have a space before it. */
function isPunctuation(s) {
	return s === "," || s === ";" || s === ")" || s === "]" || s === "}" || s === "." || s === ":";
}
/** Check if a token ends with an opening bracket — no space should follow it. */
function isOpenBracket(s) {
	const last = s[s.length - 1];
	return last === "(" || last === "[" || last === "{";
}
/**
* Determine whether a space is needed before the current token, given the
* previous token text. Handles function calls (no space before `(`),
* property access (no space after `.`), and brackets (no space after open,
* no space before close).
*/
function needsSpaceBetween(prev, cur) {
	if (isPunctuation(cur)) return false;
	if (isOpenBracket(prev)) return false;
	if (prev === ".") return false;
	if (cur === "(" && !isOperatorText(prev)) return false;
	if (cur === "[" && !isOperatorText(prev)) return false;
	return true;
}
/** Check if text looks like an operator (not an identifier or literal). */
function isOperatorText(s) {
	if (s.length === 0) return false;
	const ch = s[0];
	return "+-*/%<>=!&|^~?".includes(ch);
}
/**
* Dvala source formatter — CST-based.
*
* Parses source into a concrete syntax tree (CST) that preserves all tokens
* including comments and whitespace, then formats it using a Wadler-Lindig
* document algebra. Comments are preserved at their logical positions.
* Structural whitespace is normalized to canonical form.
*
* On parse failure the original source is returned unchanged so format-on-save
* never destroys partially-written code.
*/
function format(source) {
	if (source.trim() === "") return "";
	const fullTokenStream = tokenize(source, true, void 0);
	try {
		const { tree, trailingTrivia } = parseToCst(fullTokenStream);
		return formatCst(tree, trailingTrivia);
	} catch {
		return source;
	}
}
/**
* Format a single-expression Dvala snippet, stripping the trailing semicolon
* that the formatter adds. Use this for short inline examples in datatype.ts
* and shorthand.ts where the reference tests require no trailing semicolon.
*/
function snippet(code) {
	return format(code.trim()).trimEnd().replace(/;$/, "");
}
//#endregion
//#region ../dvala-core-tooling/dist/reference/datatype.js
const datatype = {
	"-type-number": {
		datatype: true,
		title: "number",
		category: "datatype",
		description: "A `number`",
		examples: [snippet("42"), snippet("3.14")]
	},
	"-type-string": {
		datatype: true,
		title: "string",
		category: "datatype",
		description: "A `string`. Strings are written with double quotes. Template strings use backticks and support `${...}` interpolation — any expression can appear inside the braces.",
		examples: [
			snippet("\"hello\""),
			snippet("\"\""),
			snippet("`Hello, World!`"),
			snippet("`${2} * ${3} = ${2 * 3}`")
		]
	},
	"-type-object": {
		datatype: true,
		title: "object",
		category: "datatype",
		description: "An `object`, a collection of key-value pairs where keys are `strings`",
		examples: [snippet("{}"), snippet("{ a: 1, b: 2}")]
	},
	"-type-array": {
		datatype: true,
		title: "array",
		category: "datatype",
		description: "An `array`",
		examples: [
			snippet("[]"),
			snippet("[1, 2, 3]"),
			snippet("[\"a\", null, true]")
		]
	},
	"-type-vector": {
		datatype: true,
		title: "vector",
		category: "datatype",
		description: "An `array` of `numbers`",
		examples: [snippet("[]"), snippet("[1, 2, 3]")]
	},
	"-type-matrix": {
		datatype: true,
		title: "matrix",
		category: "datatype",
		description: "A `matrix`, a two-dimensional `array` with `numbers` where each row has the same number of columns. A `matrix` is also a `grid`.",
		examples: [
			snippet("[[42]]"),
			snippet("[[1, 2], [3, 4]]"),
			snippet("[[1, 2], [3, 4], [5, 6]]")
		]
	},
	"-type-grid": {
		datatype: true,
		title: "grid",
		category: "datatype",
		description: "A `grid`, a two-dimensional `array` where each row has the same number of columns",
		examples: [
			snippet("[[]]"),
			snippet("[[1, 2], [3, 4]]"),
			snippet("[[\"a\", \"b\"], [3, 4], [5, 6]]")
		]
	},
	"-type-boolean": {
		datatype: true,
		title: "boolean",
		category: "datatype",
		description: "A `boolean`",
		examples: [snippet("true"), snippet("false")]
	},
	"-type-atom": {
		datatype: true,
		title: "atom",
		category: "datatype",
		description: "An atom — a self-evaluating named constant used as a tag or label. Atoms start with `:` followed by a name. Two atoms with the same name are always equal. Common atoms include `:ok` and `:error`.",
		examples: [
			snippet(":ok"),
			snippet(":error"),
			snippet("[:ok, 42]"),
			snippet(":ok == :ok")
		]
	},
	"-type-function": {
		datatype: true,
		title: "function",
		category: "datatype",
		description: "A `function`",
		examples: [
			snippet("x -> x + 1"),
			snippet("(a, b, c) -> (a + b) * c"),
			snippet("() -> 42"),
			snippet("-> $ + $2")
		]
	},
	"-type-integer": {
		datatype: true,
		title: "integer",
		category: "datatype",
		description: "An `integer`",
		examples: [snippet("42"), snippet("-42")]
	},
	"-type-any": {
		datatype: true,
		title: "any",
		category: "datatype",
		description: "`Any` value",
		examples: [
			snippet("42"),
			snippet("\"hello\""),
			snippet("true"),
			snippet("null")
		]
	},
	"-type-null": {
		datatype: true,
		title: "null",
		category: "datatype",
		description: "The value `null`",
		examples: [snippet("null")]
	},
	"-type-collection": {
		datatype: true,
		title: "collection",
		category: "datatype",
		description: "A collection, an `object`, an `array` or a `string`",
		examples: [
			snippet("{ foo: 42 }"),
			snippet("[1, 2, 3]"),
			snippet("\"hello\"")
		]
	},
	"-type-sequence": {
		datatype: true,
		title: "sequence",
		category: "datatype",
		description: "A sequence, an `array` or a `string`",
		examples: [snippet("[1, 2, 3]"), snippet("\"hello\"")]
	},
	"-type-regexp": {
		datatype: true,
		title: "regexp",
		category: "datatype",
		description: "A regular expression",
		examples: [
			snippet("regexp(\"^\\\\s*(.*)$\")"),
			snippet("#\"^\\s*(.*)$\""),
			snippet("#\"albert\"ig")
		]
	},
	"-type-effect": {
		datatype: true,
		title: "effect",
		category: "datatype",
		description: "An effect, created with the `effect` special expression. Effects are used with `perform` to trigger algebraic effects.",
		examples: [snippet("@dvala.io.print"), snippet("@dvala.time.now")]
	},
	"-type-never": {
		datatype: true,
		title: "never",
		category: "datatype",
		description: "A value that can never be created",
		examples: [`
// perform(@dvala.error, { message: "error" }) will never return a value
do with handler @dvala.error(arg) -> resume("never") end; perform(@dvala.error, { message: "error" }) end`]
	}
};
//#endregion
//#region ../dvala-core-tooling/dist/reference/_chunks/reference.js
/**
* Reference entries for the standard prelude type aliases declared in
* `src/prelude.dvala`. Keep `definition` in sync with the alias body
* there — these are documentation surfaces and the prelude file is the
* source of truth for typechecker semantics.
*/
const prelude = {
	"-prelude-Positive": {
		prelude: true,
		title: "Positive",
		category: "prelude",
		definition: "Number & {n | n > 0}",
		description: "Strictly positive numbers (n > 0). Use as a precondition where zero and negatives are both invalid — e.g. divisor in a count-style formula, quantities, lengths.",
		examples: [snippet("let x: Positive = 5"), snippet("let f = (n: Positive) -> 100 / n")],
		seeAlso: ["-prelude-NonNegative", "-prelude-NonZero"]
	},
	"-prelude-NonNegative": {
		prelude: true,
		title: "NonNegative",
		category: "prelude",
		definition: "Number & {n | n >= 0}",
		description: "Non-negative numbers (n >= 0). The natural-number sense from CS where 0 is included; common precondition for array indices and counts.",
		examples: [snippet("let i: NonNegative = 0"), snippet("let f = (idx: NonNegative) -> idx + 1")],
		seeAlso: ["-prelude-Positive", "-prelude-NonZero"]
	},
	"-prelude-NonZero": {
		prelude: true,
		title: "NonZero",
		category: "prelude",
		definition: "Number & {n | n != 0}",
		description: "Non-zero numbers (n != 0). The minimal precondition for division — `Positive` is stricter than needed when negative divisors are also fine.",
		examples: [snippet("let d: NonZero = -3"), snippet("let safeDivide = (a: Number, b: NonZero) -> a / b")],
		seeAlso: ["-prelude-Positive", "-prelude-NonNegative"]
	},
	"-prelude-NonEmpty": {
		prelude: true,
		title: "NonEmpty",
		category: "prelude",
		definition: "T & {xs | count(xs) > 0} where T: Sequence",
		description: "Non-empty sequences (count(xs) > 0). Generic over the `Sequence` upper bound, so it works for both arrays and strings — `NonEmpty<Array<Number>>` and `NonEmpty<String>` are both valid.",
		examples: [snippet("let xs: NonEmpty<Number[]> = [1, 2, 3]"), snippet("let s: NonEmpty<String> = \"hi\"")]
	}
};
const shorthand = {
	"-short-regexp": {
		shorthand: true,
		title: "#\"pattern\"",
		category: "shorthand",
		description: "Shorthand for `regexp(pattern)`. Only difference is that escaping is not needed.",
		examples: [snippet("#\"^\\s*(.*)$\""), snippet("#\"albert\"ig")],
		seeAlso: [
			"regexp",
			"reMatch",
			"replace",
			"replaceAll"
		]
	},
	"-short-fn": {
		shorthand: true,
		title: "-> expression",
		category: "shorthand",
		description: `
Shorthand for \`(args, ...) -> expression\`.
\`$\` is the first argument, and \`$2, $3, ...\` are the second, third, ... argument.`,
		examples: [snippet("-> $ + $2"), snippet("(-> $ * $)(9)")]
	},
	"-short-object": {
		shorthand: true,
		title: "{ foo }",
		category: "shorthand",
		description: "Shorthand for `{ foo: foo }`. When a key and variable name are the same, you can omit the value.",
		examples: [snippet("let x = 1; let y = 2; { x, y }"), snippet("let name = \"Alice\"; let age = 30; { name, age }")]
	}
};
function docsToReference(expressions) {
	const result = {};
	for (const [key, expr] of Object.entries(expressions)) {
		const docs = expr.docs;
		if (!docs) throw new Error(`Missing docs for expression "${key}"`);
		result[key] = {
			title: key,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
	}
	return result;
}
function moduledDocsToReference(module) {
	const result = {};
	for (const [key, docs] of Object.entries(module.docs ?? {})) {
		const qualifiedKey = `${module.name}.${key}`;
		result[qualifiedKey] = {
			title: qualifiedKey,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
	}
	return result;
}
const assertionRef = docsToReference(assertionNormalExpression);
const bitwiseReference = docsToReference(bitwiseNormalExpression);
const arrayRef = docsToReference(arrayNormalExpression);
const collectionRef = docsToReference(collectionNormalExpression);
const functionalRef = docsToReference(functionalNormalExpression);
const mathRef = docsToReference(mathNormalExpression);
const metaRef = docsToReference(getMetaNormalExpression({}, {}));
const miscRef = docsToReference(miscNormalExpression);
const objectRef = docsToReference(objectNormalExpression);
const predicatesRef = docsToReference(predicatesNormalExpression);
const regexpRef = docsToReference(regexpNormalExpression);
const sequenceRef = docsToReference(sequenceNormalExpression);
const stringRef = docsToReference(stringNormalExpression);
function specialExpressionDocsToReference() {
	const result = {};
	for (const [name, type] of Object.entries(specialExpressionTypes)) {
		const docs = specialExpressions[type]?.docs;
		if (!docs) continue;
		if (isFunctionDocs(docs)) result[name] = {
			title: name,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
		else result[name] = {
			title: name,
			category: docs.category,
			description: docs.description,
			customVariants: docs.customVariants,
			...docs.details ? { details: docs.details } : {},
			...docs.returns ? { returns: docs.returns } : {},
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {}
		};
	}
	return result;
}
const specialExpressionsReference = specialExpressionDocsToReference();
const normalExpressionReference = {
	...assertionRef,
	...bitwiseReference,
	...collectionRef,
	...arrayRef,
	...sequenceRef,
	...mathRef,
	...functionalRef,
	...metaRef,
	...miscRef,
	...objectRef,
	...predicatesRef,
	...regexpRef,
	...stringRef
};
const moduleReference = {
	...moduledDocsToReference(assertModule),
	...moduledDocsToReference(gridModule),
	...moduledDocsToReference(vectorModule),
	...moduledDocsToReference(linearAlgebraModule),
	...moduledDocsToReference(matrixModule),
	...moduledDocsToReference(numberTheoryModule),
	...moduledDocsToReference(stringUtilsModule),
	...moduledDocsToReference(collectionUtilsModule),
	...moduledDocsToReference(sequenceUtilsModule),
	...moduledDocsToReference(mathUtilsModule),
	...moduledDocsToReference(functionalUtilsModule),
	...moduledDocsToReference(bitwiseUtilsModule),
	...moduledDocsToReference(convertModule),
	...moduledDocsToReference(jsonModule),
	...moduledDocsToReference(timeModule),
	...moduledDocsToReference(handlerModule),
	...moduledDocsToReference(astModule),
	...moduledDocsToReference(macrosModule)
};
Object.entries(normalExpressionReference).forEach(([key, obj]) => {
	if (!normalExpressions[key]) throw new Error(`Missing normal expression ${key} in normalExpressions`);
	const arity = normalExpressions[key].arity;
	if (!obj.noOperatorDocumentation && canBeOperator(arity)) {
		obj._isOperator = true;
		if (isSymbolicOperator(key)) obj._prefereOperator = true;
	}
});
Object.entries(specialExpressionsReference).forEach(([key, obj]) => {
	if (isFunctionReference(obj)) {
		const arity = specialExpressions[specialExpressionTypes[key]]?.arity;
		if (arity && canBeOperator(arity)) obj._isOperator = true;
	}
});
const apiReference = sortByCategory({
	...normalExpressionReference,
	...specialExpressionsReference,
	...shorthand,
	...datatype,
	...prelude
});
function deriveEffectReference() {
	const result = {};
	for (const [name, def] of Object.entries(allStandardEffectDefinitions)) {
		const key = `-effect-${name}`;
		result[key] = {
			effect: true,
			title: name,
			category: "effect",
			description: def.docs.description,
			args: def.docs.args,
			returns: def.docs.returns,
			variants: def.docs.variants,
			examples: def.docs.examples,
			...def.docs.seeAlso ? { seeAlso: def.docs.seeAlso } : {}
		};
	}
	return result;
}
const effectReference = deriveEffectReference();
const allReference = sortByCategory({
	...apiReference,
	...moduleReference,
	...effectReference
});
function sortByCategory(ref) {
	return Object.fromEntries(Object.entries(ref).sort(([keyA, refA], [keyB, refB]) => {
		const catA = refA.category === "special-expression" ? "" : refA.category;
		const catB = refB.category === "special-expression" ? "" : refB.category;
		if (catA !== catB) return catA.localeCompare(catB);
		return keyA.localeCompare(keyB);
	}));
}
//#endregion
//#region src/catchallQuickFix.ts
const CATCHALL_BODY = "case _ then perform(@dvala.error, \"unhandled match case\")";
/**
* Compute a text edit that inserts a catchall case immediately before the
* closing `end` of a match expression. The insertion point is the start
* of the line containing `end` (column 1, 1-based), and the inserted text
* carries the matching indent for that line + 2 spaces.
*
* Returns null if the source doesn't actually contain `end` at the
* expected position (defensive — the match node's sourceMap range and the
* source text are usually in sync, but a stale request might trip this).
*/
function computeCatchallEdit(source, matchRange) {
	const lines = source.split("\n");
	const endLineIndex = matchRange.endLine - 1;
	if (endLineIndex < 0 || endLineIndex >= lines.length) return null;
	const endLineText = lines[endLineIndex];
	const endKeywordStartColumn = matchRange.endColumn - 3;
	if (endKeywordStartColumn < 1) return null;
	const startIndex = endKeywordStartColumn - 1;
	if (endLineText.slice(startIndex, startIndex + 3) !== "end") return null;
	const endIndentMatch = /^\s*/.exec(endLineText);
	const caseIndent = `${endIndentMatch ? endIndentMatch[0] : ""}  `;
	return {
		startLine: matchRange.endLine,
		startColumn: 1,
		endLine: matchRange.endLine,
		endColumn: 1,
		newText: `${caseIndent}${CATCHALL_BODY}\n`
	};
}
//#endregion
//#region src/extractVariableEdit.ts
const DEFAULT_NAME$1 = "extracted";
/**
* Compute the two edits that move the selected expression into a new
* `let <name> = <expr>` binding above its enclosing statement.
*
* Returns null if:
*   - The selection range doesn't lie within a single line (multi-line
*     extraction is a future polish; the line-based indent sampling here
*     assumes the original expression occupies its line on one row).
*   - The statement's line is out of range.
*   - The selection is malformed (start after end).
*/
function computeExtractVariableEdit(inputs) {
	if (inputs.expressionStartLine !== inputs.expressionEndLine || inputs.expressionEndColumn <= inputs.expressionStartColumn) return null;
	const lines = inputs.source.split("\n");
	const statementLineIndex = inputs.statementStartLine - 1;
	if (statementLineIndex < 0 || statementLineIndex >= lines.length) return null;
	const statementLineText = lines[statementLineIndex];
	const indentMatch = /^\s*/.exec(statementLineText);
	const indent = indentMatch ? indentMatch[0] : "";
	const expressionText = lines[inputs.expressionStartLine - 1].slice(inputs.expressionStartColumn - 1, inputs.expressionEndColumn - 1);
	return {
		letInsertion: {
			startLine: inputs.statementStartLine,
			startColumn: 1,
			endLine: inputs.statementStartLine,
			endColumn: 1,
			newText: `${indent}let ${DEFAULT_NAME$1} = ${expressionText};\n`
		},
		expressionReplacement: {
			startLine: inputs.expressionStartLine,
			startColumn: inputs.expressionStartColumn,
			endLine: inputs.expressionEndLine,
			endColumn: inputs.expressionEndColumn,
			newText: DEFAULT_NAME$1
		},
		defaultName: DEFAULT_NAME$1
	};
}
//#endregion
//#region src/extractFunctionEdit.ts
const DEFAULT_NAME = "extracted";
/**
* Compute the two edits that move the selection into a new function.
* Returns null when the selection is malformed (zero or negative width).
*
* v1 limitations:
*   - Selection must have non-zero width — wrapping an empty range is
*     meaningless.
*   - Multi-line selections preserve the original line breaks verbatim
*     inside the `do … end` body. The caller is responsible for ensuring
*     the selection covers complete statements; partial-statement
*     selections produce syntactically broken extractions.
*   - The new function is inserted directly above the selection's first
*     line. For a selection nested deep inside a function body, the
*     extracted function ends up locally scoped — which is fine for v1
*     but may not match the user's mental model of "module-level helper."
*     Move-up refinement is a v2 polish.
*/
function computeExtractFunctionEdit(inputs) {
	if (inputs.selectionEndLine < inputs.selectionStartLine || inputs.selectionEndLine === inputs.selectionStartLine && inputs.selectionEndColumn <= inputs.selectionStartColumn) return null;
	const lines = inputs.source.split("\n");
	const selectionStartLineIndex = inputs.selectionStartLine - 1;
	if (selectionStartLineIndex < 0 || selectionStartLineIndex >= lines.length) return null;
	const selectionStartLineText = lines[selectionStartLineIndex];
	const indentMatch = /^\s*/.exec(selectionStartLineText);
	const indent = indentMatch ? indentMatch[0] : "";
	const selectionText = sliceSelection(lines, inputs.selectionStartLine, inputs.selectionStartColumn, inputs.selectionEndLine, inputs.selectionEndColumn);
	const paramList = inputs.freeVars.join(", ");
	const argList = inputs.freeVars.join(", ");
	const bodyText = indentBody(selectionText, `${indent}  `);
	return {
		letInsertion: {
			startLine: inputs.selectionStartLine,
			startColumn: 1,
			endLine: inputs.selectionStartLine,
			endColumn: 1,
			newText: `${indent}let ${DEFAULT_NAME} = (${paramList}) -> do\n${bodyText}\n${indent}end;\n`
		},
		selectionReplacement: {
			startLine: inputs.selectionStartLine,
			startColumn: inputs.selectionStartColumn,
			endLine: inputs.selectionEndLine,
			endColumn: inputs.selectionEndColumn,
			newText: `${DEFAULT_NAME}(${argList})`
		},
		defaultName: DEFAULT_NAME
	};
}
function sliceSelection(lines, startLine, startColumn, endLine, endColumn) {
	if (startLine === endLine) return lines[startLine - 1].slice(startColumn - 1, endColumn - 1);
	const parts = [];
	parts.push(lines[startLine - 1].slice(startColumn - 1));
	for (let i = startLine; i < endLine - 1; i++) parts.push(lines[i]);
	parts.push(lines[endLine - 1].slice(0, endColumn - 1));
	return parts.join("\n");
}
/**
* Re-indent multi-line body text. Strips the original leading whitespace
* on the first line (it's about to be prefixed with `bodyIndent`) and
* applies `bodyIndent` to every line of the body. Internal blank lines
* are preserved without forced indent.
*/
function indentBody(body, bodyIndent) {
	return body.split("\n").map((line) => {
		const trimmed = line.trimStart();
		if (trimmed === "") return "";
		return `${bodyIndent}${trimmed}`;
	}).join("\n");
}
//#endregion
//#region src/inlineVariableEdit.ts
/**
* Compute the edits to inline the variable. Returns the let-removal edit
* first, followed by each reference-replacement edit. The order doesn't
* matter for the editor (VS Code applies WorkspaceEdit edits
* atomically), but consistent ordering helps tests assert against the
* shape directly.
*
* Returns null if no references exist — there's nothing to inline if the
* binding isn't used anywhere; the user probably wants "remove unused
* variable" instead, which is a different refactor.
*/
function computeInlineVariableEdits(inputs) {
	if (inputs.references.length === 0) return null;
	const wrappedValue = isNonTrivialExpression(inputs.valueText) ? `(${inputs.valueText})` : inputs.valueText;
	const edits = [];
	edits.push({
		startLine: inputs.letRemoveStartLine,
		startColumn: inputs.letRemoveStartColumn,
		endLine: inputs.letRemoveEndLine,
		endColumn: inputs.letRemoveEndColumn,
		newText: ""
	});
	for (const ref of inputs.references) edits.push({
		startLine: ref.line,
		startColumn: ref.column,
		endLine: ref.line,
		endColumn: ref.column + ref.length,
		newText: wrappedValue
	});
	return edits;
}
/**
* Crude precedence heuristic — treat anything that isn't a plain
* identifier or numeric/string literal as "non-trivial" and wrap it in
* parens at each use site. False positives are harmless (extra parens);
* false negatives can corrupt precedence (e.g. `let x = a + b; x * 2`
* inlined without parens becomes `a + b * 2` which evaluates wrong). So
* we err on the side of wrapping.
*/
function isNonTrivialExpression(text) {
	const trimmed = text.trim();
	if (/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(trimmed)) return false;
	if (/^[0-9]+(?:\.[0-9]+)?$/.test(trimmed)) return false;
	if (/^"[^"\\$]*"$/.test(trimmed)) return false;
	return true;
}
//#endregion
//#region src/callHierarchy.ts
const CALLABLE_DEF_KINDS = new Set([
	"function",
	"macro",
	"handler"
]);
/**
* Walk the AST and collect every `let name = (params) -> body` shape
* (plus the macro / handler analogues). Each frame records both the
* outer Let range and the inner body range — the body range tests
* containment of call sites; the Let range is what VS Code highlights
* as the item's full range.
*/
function collectCallableFrames(ast, sourceMap, fileSymbols) {
	if (!sourceMap || !fileSymbols) return [];
	const map = sourceMap;
	const frames = [];
	const defByLocation = /* @__PURE__ */ new Map();
	for (const def of fileSymbols.definitions) {
		if (!CALLABLE_DEF_KINDS.has(def.kind)) continue;
		defByLocation.set(`${def.location.line}:${def.location.column}`, def);
	}
	function walk(node) {
		if (!Array.isArray(node) || typeof node[0] !== "string") {
			if (Array.isArray(node)) for (const child of node) walk(child);
			return;
		}
		if (node[0] === NodeTypes.Let && Array.isArray(node[1])) {
			const [bindingTarget, valueExpr] = node[1];
			if (bindingTarget[0] === "symbol" && Array.isArray(bindingTarget[1])) {
				const symNode = bindingTarget[1][0];
				const valueType = valueExpr[0];
				const isCallableValue = valueType === NodeTypes.Function || valueType === NodeTypes.Macro || valueType === NodeTypes.Handler;
				if (symNode[0] === NodeTypes.Sym && typeof symNode[1] === "string" && isCallableValue) {
					const symPos = map.get(symNode[symNode.length - 1]);
					const letPos = map.get(node[node.length - 1]);
					const bodyPos = map.get(valueExpr[valueExpr.length - 1]);
					if (symPos && letPos && bodyPos) {
						const defLocKey = `${symPos.start[0] + 1}:${symPos.start[1] + 1}`;
						const def = defByLocation.get(defLocKey);
						const astKind = valueType === NodeTypes.Function ? "function" : valueType === NodeTypes.Macro ? "macro" : "handler";
						const symbolKind = def?.kind;
						const kind = symbolKind === "function" || symbolKind === "macro" || symbolKind === "handler" ? symbolKind : astKind;
						frames.push({
							name: symNode[1],
							kind,
							defLine: symPos.start[0] + 1,
							defColumn: symPos.start[1] + 1,
							letStartLine: letPos.start[0] + 1,
							letStartColumn: letPos.start[1] + 1,
							letEndLine: letPos.end[0] + 1,
							letEndColumn: letPos.end[1] + 1,
							bodyStartLine: bodyPos.start[0] + 1,
							bodyStartColumn: bodyPos.start[1] + 1,
							bodyEndLine: bodyPos.end[0] + 1,
							bodyEndColumn: bodyPos.end[1] + 1
						});
					}
				}
			}
		}
		walk(node[1]);
	}
	walk(ast);
	return frames;
}
/**
* Smallest callable frame whose body contains the given (1-based)
* position. Returns null when the position is at top-level (no
* enclosing function — calls go on the file-level synthetic item
* in the backend).
*/
function findEnclosingCallableFrame(frames, line, column) {
	let best = null;
	let bestSize = Infinity;
	for (const frame of frames) {
		if (!((line > frame.bodyStartLine || line === frame.bodyStartLine && column >= frame.bodyStartColumn) && (line < frame.bodyEndLine || line === frame.bodyEndLine && column <= frame.bodyEndColumn))) continue;
		const size = (frame.bodyEndLine - frame.bodyStartLine) * 1e6 + (frame.bodyEndColumn - frame.bodyStartColumn);
		if (size < bestSize) {
			bestSize = size;
			best = frame;
		}
	}
	return best;
}
/**
* Walk an AST subtree and yield every call site whose callee is a
* known Sym. Caller filters by resolved-def kind. Used by
* outgoing-calls to list "what does this function call?".
*/
function* walkCallSites(node, sourceMap) {
	if (!Array.isArray(node) || typeof node[0] !== "string") {
		if (Array.isArray(node)) for (const child of node) yield* walkCallSites(child, sourceMap);
		return;
	}
	if (node[0] === NodeTypes.Call && Array.isArray(node[1])) {
		const callee = node[1][0];
		if (callee[0] === NodeTypes.Sym && typeof callee[1] === "string") {
			const callRange = sourceMap.get(node[node.length - 1]);
			if (callRange) yield {
				calleeNodeId: callee[callee.length - 1],
				calleeName: callee[1],
				callRange
			};
		}
	}
	yield* walkCallSites(node[1], sourceMap);
}
//#endregion
//#region src/documentStore.ts
const accepted = { ok: true };
function resyncRequired(path) {
	return {
		ok: false,
		error: {
			kind: "resync-required",
			message: `Backend document mirror missing or stale for ${path}`,
			path
		}
	};
}
function createInMemoryDocumentStore() {
	const openDocuments = /* @__PURE__ */ new Map();
	const workspaceSnapshot = /* @__PURE__ */ new Map();
	return {
		open(document) {
			const current = openDocuments.get(document.path);
			if (current && current.version > document.version) return;
			openDocuments.set(document.path, document);
		},
		update(document, previousVersion) {
			const current = openDocuments.get(document.path);
			if (!current || current.version !== previousVersion) return resyncRequired(document.path);
			if (document.version < current.version) return accepted;
			openDocuments.set(document.path, document);
			return accepted;
		},
		close(path) {
			openDocuments.delete(path);
		},
		persistFile(request) {
			workspaceSnapshot.set(request.file.path, request.file);
		},
		removeFile(request) {
			workspaceSnapshot.delete(request.path);
		},
		getOpenDocuments() {
			return [...openDocuments.values()];
		},
		getOpenDocument(path) {
			return openDocuments.get(path);
		},
		getWorkspaceDocument(path) {
			return workspaceSnapshot.get(path);
		},
		getWorkspaceSnapshot() {
			return [...workspaceSnapshot.values()];
		},
		getEffectiveSource(path) {
			const openDocument = openDocuments.get(path);
			if (openDocument) return openDocument.source;
			return workspaceSnapshot.get(path)?.code;
		}
	};
}
//#endregion
//#region src/runtime/runtimeAdapter.ts
const PLAYGROUND_FOLDER$1 = ".dvala-playground";
function isInPlaygroundFolder$1(path) {
	return path === PLAYGROUND_FOLDER$1 || path.startsWith(`${PLAYGROUND_FOLDER$1}/`);
}
function folderFromPath$1(path) {
	const index = path.lastIndexOf("/");
	return index === -1 ? "" : path.slice(0, index);
}
function resolvePlaygroundPath$1(fromDir, importPath) {
	const segments = importPath.startsWith("/") || fromDir === "" ? [] : fromDir.split("/").filter((seg) => seg !== "");
	for (const segment of importPath.split("/")) {
		if (segment === "" || segment === ".") continue;
		if (segment === "..") {
			if (segments.length === 0) throw new Error(`Import path escapes workspace root: '${importPath}' from '${fromDir}'`);
			segments.pop();
			continue;
		}
		segments.push(segment);
	}
	return segments.join("/");
}
function runtimeBaseDir$1(path) {
	if (!path || isInPlaygroundFolder$1(path)) return "";
	return folderFromPath$1(path);
}
function createRuntimeFileResolver$1(documents) {
	return (importPath, fromDir) => {
		const resolved = resolvePlaygroundPath$1(isInPlaygroundFolder$1(fromDir) ? "" : fromDir, importPath);
		if (isInPlaygroundFolder$1(resolved)) throw new Error(`Cannot import '${importPath}' from '${fromDir || "<root>"}': ${PLAYGROUND_FOLDER$1}/ is playground state, not part of the deployable project`);
		const exact = documents.getEffectiveSource(resolved);
		if (exact !== void 0) return exact;
		const withSuffix = documents.getEffectiveSource(`${resolved}.dvala`);
		if (withSuffix !== void 0) return withSuffix;
		throw new Error(`File not found: ${importPath} (resolved from '${fromDir}' to '${resolved}')`);
	};
}
function createRuntimeRunner(documents, path, debug) {
	return createDvala({
		...debug ? { debug: true } : {},
		modules: allBuiltinModules,
		fileResolver: createRuntimeFileResolver$1(documents),
		fileResolverBaseDir: runtimeBaseDir$1(path)
	});
}
function statusFromRunResult(result) {
	switch (result.type) {
		case "suspended": return "suspended";
		case "error": return "failed";
		case "completed":
		case "halted": return "completed";
	}
}
function hasEnv(value) {
	return typeof value === "object" && value !== null && "env" in value && value.env !== void 0;
}
function hasOuterEnv(value) {
	return typeof value === "object" && value !== null && "outerEnv" in value && value.outerEnv !== void 0;
}
function getEnvFromContinuation(k) {
	let node = k;
	while (typeof node === "object" && node !== null && "head" in node && "tail" in node) {
		const frame = node.head;
		if (hasEnv(frame)) return frame.env;
		if (hasOuterEnv(frame)) return frame.outerEnv;
		node = node.tail;
	}
	return null;
}
function extractSnapshotBindings(snapshot) {
	const deserialized = deserializeFromObject(snapshot.continuation);
	const env = getEnvFromContinuation(deserialized.k) ?? (hasEnv(deserialized.initialStep) ? deserialized.initialStep.env : null);
	if (!env) return {};
	return Debugger.extractBindings({
		env,
		k: deserialized.k,
		resume: () => {},
		getSnapshots: () => deserialized.snapshots
	});
}
function hasValidRuntimeContinuation(continuation) {
	try {
		deserializeFromObject(continuation);
		return true;
	} catch {
		return false;
	}
}
function asRuntimeSnapshot(value) {
	if (typeof value !== "object" || value === null) return null;
	if (!("id" in value) || typeof value.id !== "string") return null;
	if (!("continuation" in value)) return null;
	if (!hasValidRuntimeContinuation(value.continuation)) return null;
	if (!("timestamp" in value) || typeof value.timestamp !== "number") return null;
	if (!("index" in value) || typeof value.index !== "number") return null;
	if (!("executionId" in value) || typeof value.executionId !== "string") return null;
	if (!("message" in value) || typeof value.message !== "string") return null;
	if ("terminal" in value && value.terminal !== void 0 && typeof value.terminal !== "boolean") return null;
	if ("effectName" in value && value.effectName !== void 0 && typeof value.effectName !== "string") return null;
	const checkpointSnapshots = extractCheckpointSnapshots(value.continuation);
	for (const checkpointSnapshot of checkpointSnapshots) if (!asRuntimeSnapshot(checkpointSnapshot)) return null;
	return value;
}
function createBackendRuntimeAdapter(documents) {
	const sessions = /* @__PURE__ */ new Map();
	let sessionCounter = 0;
	function createSessionId() {
		sessionCounter += 1;
		return `backend-session-${sessionCounter}`;
	}
	function updateSession(sessionId, status) {
		sessions.set(sessionId, {
			status,
			lastUpdatedAt: Date.now()
		});
	}
	return {
		async start(request) {
			const sessionId = createSessionId();
			updateSession(sessionId, "running");
			const runOptions = request.pure ? {
				pure: true,
				...request.scope ? { scope: request.scope } : {},
				...request.disableAutoCheckpoint ? { disableAutoCheckpoint: true } : {},
				...request.terminalSnapshot ? { terminalSnapshot: true } : {},
				...request.path ? { filePath: request.path } : {}
			} : {
				...request.scope ? { scope: request.scope } : {},
				...request.effectHandlers ? { effectHandlers: request.effectHandlers } : {},
				...request.disableAutoCheckpoint ? { disableAutoCheckpoint: true } : {},
				...request.terminalSnapshot ? { terminalSnapshot: true } : {},
				...request.path ? { filePath: request.path } : {}
			};
			const runResult = await createRuntimeRunner(documents, request.path, request.debug).runAsync(request.source, runOptions);
			updateSession(sessionId, statusFromRunResult(runResult));
			return {
				sessionId,
				runResult
			};
		},
		async resume(request) {
			const sessionId = createSessionId();
			updateSession(sessionId, "running");
			const runResult = request.snapshot.effectName ? await retrigger(request.snapshot, {
				handlers: request.effectHandlers,
				modules: allBuiltinModules,
				...request.disableAutoCheckpoint ? { disableAutoCheckpoint: true } : {},
				...request.terminalSnapshot ? { terminalSnapshot: true } : {}
			}) : await resume(request.snapshot, request.value, {
				handlers: request.effectHandlers,
				modules: allBuiltinModules,
				...request.disableAutoCheckpoint ? { disableAutoCheckpoint: true } : {},
				...request.terminalSnapshot ? { terminalSnapshot: true } : {}
			});
			updateSession(sessionId, statusFromRunResult(runResult));
			return {
				sessionId,
				runResult
			};
		},
		async inspectSnapshot(request) {
			return extractCheckpointSnapshots(request.snapshot.continuation);
		},
		async inspectSnapshotBindings(request) {
			return extractSnapshotBindings(request.snapshot);
		},
		async validateSnapshot(request) {
			return asRuntimeSnapshot(request.value);
		},
		async inspect(sessionId) {
			const session = sessions.get(sessionId);
			if (session) return {
				ok: true,
				sessionId,
				status: session.status,
				lastUpdatedAt: session.lastUpdatedAt
			};
			return {
				ok: false,
				error: {
					kind: "session-not-found",
					message: `No runtime session for id ${sessionId}`,
					path: sessionId
				}
			};
		},
		async stop(sessionId) {
			sessions.delete(sessionId);
		}
	};
}
//#endregion
//#region src/createBackend.ts
const PLAYGROUND_FOLDER = ".dvala-playground";
const builtinCompletions = buildBuiltinCompletions();
const builtinModuleCompletions = allBuiltinModules.map((mod) => ({
	label: mod.name,
	kind: "module",
	detail: "module",
	sortText: `0_${mod.name}`
}));
function clearCancelledRequest(cancelledRequests, requestId) {
	cancelledRequests.delete(requestId);
}
function requestFailure(requestId, error, path) {
	return {
		ok: false,
		requestId,
		error: path && !error.path ? {
			...error,
			path
		} : error
	};
}
function isCancelled(cancelledRequests, requestId) {
	return cancelledRequests.get(requestId) === true;
}
function isInPlaygroundFolder(path) {
	return path === PLAYGROUND_FOLDER || path.startsWith(`${PLAYGROUND_FOLDER}/`);
}
function folderFromPath(path) {
	const index = path.lastIndexOf("/");
	return index === -1 ? "" : path.slice(0, index);
}
function stripDvalaSuffix(name) {
	return name.trim().replace(/\.dvala$/i, "");
}
function resolvePlaygroundPath(fromDir, importPath) {
	const segments = importPath.startsWith("/") || fromDir === "" ? [] : fromDir.split("/").filter((seg) => seg !== "");
	for (const segment of importPath.split("/")) {
		if (segment === "" || segment === ".") continue;
		if (segment === "..") {
			if (segments.length === 0) throw new Error(`Import path escapes workspace root: '${importPath}' from '${fromDir}'`);
			segments.pop();
			continue;
		}
		segments.push(segment);
	}
	return segments.join("/");
}
function matchesPrefix(label, prefix) {
	if (!prefix) return true;
	return label.toLowerCase().startsWith(prefix.toLowerCase());
}
function getScopedCompletionItems(prefix, visibleSymbols) {
	const items = [];
	const seen = /* @__PURE__ */ new Set();
	for (const def of visibleSymbols) {
		if (!matchesPrefix(def.name, prefix)) continue;
		if (seen.has(def.name)) continue;
		seen.add(def.name);
		items.push(symbolDefToCompletion(def));
	}
	for (const item of builtinCompletions) {
		if (!matchesPrefix(item.label, prefix)) continue;
		if (seen.has(item.label)) continue;
		seen.add(item.label);
		items.push(item);
	}
	return items;
}
function getImportedExportCompletionItems(prefix, currentFileSymbols, getFileSymbols) {
	if (!currentFileSymbols) return [];
	const items = [];
	const seen = /* @__PURE__ */ new Set();
	for (const importedPath of currentFileSymbols.imports.values()) {
		const importedSymbols = getFileSymbols(importedPath);
		if (!importedSymbols) continue;
		for (const exp of importedSymbols.exports) {
			if (!matchesPrefix(exp.name, prefix)) continue;
			if (seen.has(exp.name)) continue;
			seen.add(exp.name);
			items.push({
				...symbolDefToCompletion(exp),
				detail: "imported export",
				sortText: `2_${exp.name}`
			});
		}
	}
	return items;
}
function addImportCompletion(items, seen, label, detail) {
	if (seen.has(label)) return;
	seen.add(label);
	items.push({
		label,
		kind: "module",
		detail,
		sortText: detail === "folder" ? `1_${label}` : `2_${label}`
	});
}
function relativeImportPath(fromFilePath, targetPath) {
	const fromDir = fromFilePath ? folderFromPath(fromFilePath) : "";
	const fromSegments = fromDir === "" ? [] : fromDir.split("/");
	const toSegments = targetPath.split("/");
	const fileName = toSegments.pop();
	let shared = 0;
	while (shared < fromSegments.length && shared < toSegments.length && fromSegments[shared] === toSegments[shared]) shared++;
	const up = fromSegments.slice(shared).map(() => "..");
	const down = toSegments.slice(shared);
	const parts = [
		...up,
		...down,
		stripDvalaSuffix(fileName)
	];
	if (parts.length === 1 && !parts[0].startsWith(".")) return `./${parts[0]}`;
	if (parts[0]?.startsWith("..")) return parts.join("/");
	return `./${parts.join("/")}`;
}
function relativeFolderImportPath(fromFilePath, folderPath) {
	return relativeImportPath(fromFilePath, `${folderPath}/index.dvala`).replace(/\/index$/, "");
}
function importBasePath(currentFilePath) {
	if (!currentFilePath) return void 0;
	return isInPlaygroundFolder(currentFilePath) ? void 0 : currentFilePath;
}
function getImportFolderLabels(currentFilePath, workspaceFiles, importPrefix) {
	const labels = /* @__PURE__ */ new Set();
	for (const file of workspaceFiles) {
		if (isInPlaygroundFolder(file.path)) continue;
		const segments = file.path.split("/");
		segments.pop();
		let folderPath = "";
		for (const segment of segments) {
			folderPath = folderPath === "" ? segment : `${folderPath}/${segment}`;
			const label = importPrefix.startsWith("/") ? `/${folderPath}/` : `${relativeFolderImportPath(currentFilePath, folderPath)}/`;
			labels.add(label);
		}
	}
	return [...labels];
}
function getImportCompletionItems(importPrefix, currentFilePath, workspaceFiles) {
	const items = [];
	const seen = /* @__PURE__ */ new Set();
	const wantsPathCompletions = importPrefix.startsWith(".") || importPrefix.startsWith("/");
	const basePath = importBasePath(currentFilePath);
	if (!wantsPathCompletions) for (const item of builtinModuleCompletions) {
		if (!matchesPrefix(item.label, importPrefix)) continue;
		addImportCompletion(items, seen, item.label, "module");
	}
	for (const label of getImportFolderLabels(basePath, workspaceFiles, importPrefix)) {
		if (!matchesPrefix(label, importPrefix)) continue;
		addImportCompletion(items, seen, label, "folder");
	}
	for (const file of workspaceFiles) {
		if (isInPlaygroundFolder(file.path)) continue;
		if (file.path === currentFilePath) continue;
		const label = importPrefix.startsWith("/") ? `/${stripDvalaSuffix(file.path)}` : relativeImportPath(basePath, file.path);
		if (!matchesPrefix(label, importPrefix)) continue;
		addImportCompletion(items, seen, label, "workspace file");
	}
	return items;
}
function computeTypecheckResult(source, path, documents, createDvala) {
	const tokens = tokenizeSource(source, true, path);
	try {
		return typecheck(parseToAst(minifyTokenStream(tokens, { removeWhiteSpace: true })), {
			modules: allBuiltinModules,
			...documents ? {
				fileResolver: createRuntimeFileResolver(documents),
				fileResolverBaseDir: runtimeBaseDir(path)
			} : {},
			createDvala
		});
	} catch {
		return {
			diagnostics: [],
			typeMap: /* @__PURE__ */ new Map(),
			sourceMap: void 0
		};
	}
}
function computeHoverResult(request, typecheckResult) {
	if (request.source === void 0) return void 0;
	const wordRange = request.startColumn !== void 0 && request.endColumn !== void 0 ? {
		start: {
			line: request.line,
			column: request.startColumn
		},
		end: {
			line: request.line,
			column: request.endColumn
		}
	} : void 0;
	const type = findTypeAtPosition(typecheckResult.typeMap, typecheckResult.sourceMap, {
		line: request.line,
		column: request.column
	}, wordRange);
	return type ? formatHoverType(type) : void 0;
}
function resolveWorkspaceImportPathForSnapshot(snapshotFiles, rawPath, fromFile) {
	if (!(rawPath.startsWith(".") || rawPath.startsWith("/"))) return null;
	let resolved;
	try {
		resolved = resolvePlaygroundPath(isInPlaygroundFolder(fromFile) ? "" : folderFromPath(fromFile), rawPath);
	} catch {
		return null;
	}
	if (isInPlaygroundFolder(resolved)) return null;
	if (snapshotFiles.has(resolved)) return resolved;
	if (snapshotFiles.has(`${resolved}.dvala`)) return `${resolved}.dvala`;
	return null;
}
function indexWorkspaceSnapshot(path, source, snapshotFiles, index, seen = /* @__PURE__ */ new Set()) {
	if (seen.has(path)) return;
	seen.add(path);
	const resolveImport = (rawPath, fromFile) => resolveWorkspaceImportPathForSnapshot(snapshotFiles, rawPath, fromFile);
	const fileSymbols = index.updateFile(path, source, resolveImport);
	for (const importedPath of fileSymbols.imports.values()) {
		if (seen.has(importedPath)) continue;
		const importedSource = snapshotFiles.get(importedPath);
		if (importedSource === void 0) continue;
		indexWorkspaceSnapshot(importedPath, importedSource, snapshotFiles, index, seen);
	}
}
function runtimeBaseDir(path) {
	if (!path || isInPlaygroundFolder(path)) return "";
	return folderFromPath(path);
}
function createRuntimeFileResolver(documents) {
	return (importPath, fromDir) => {
		const resolved = resolvePlaygroundPath(isInPlaygroundFolder(fromDir) ? "" : fromDir, importPath);
		if (isInPlaygroundFolder(resolved)) throw new Error(`Cannot import '${importPath}' from '${fromDir || "<root>"}': ${PLAYGROUND_FOLDER}/ is playground state, not part of the deployable project`);
		const exact = documents.getEffectiveSource(resolved);
		if (exact !== void 0) return exact;
		const withSuffix = documents.getEffectiveSource(`${resolved}.dvala`);
		if (withSuffix !== void 0) return withSuffix;
		throw new Error(`File not found: ${importPath} (resolved from '${fromDir}' to '${resolved}')`);
	};
}
function resolveWorkspaceImportPathForDocuments(documents, rawPath, fromFile) {
	if (!(rawPath.startsWith(".") || rawPath.startsWith("/"))) return null;
	let resolved;
	try {
		resolved = resolvePlaygroundPath(isInPlaygroundFolder(fromFile) ? "" : folderFromPath(fromFile), rawPath);
	} catch {
		return null;
	}
	if (isInPlaygroundFolder(resolved)) return null;
	if (documents.getEffectiveSource(resolved) !== void 0) return resolved;
	if (documents.getEffectiveSource(`${resolved}.dvala`) !== void 0) return `${resolved}.dvala`;
	return null;
}
function indexBackendDocuments(path, documents, index, seen = /* @__PURE__ */ new Set()) {
	if (seen.has(path)) return;
	seen.add(path);
	const source = documents.getEffectiveSource(path);
	if (source === void 0) return;
	const resolveImport = (rawPath, fromFile) => resolveWorkspaceImportPathForDocuments(documents, rawPath, fromFile);
	const fileSymbols = index.updateFile(path, source, resolveImport);
	for (const importedPath of fileSymbols.imports.values()) {
		if (seen.has(importedPath)) continue;
		indexBackendDocuments(importedPath, documents, index, seen);
	}
}
function listAllKnownPaths(documents) {
	const paths = /* @__PURE__ */ new Set();
	for (const doc of documents.getOpenDocuments()) paths.add(doc.path);
	for (const file of documents.getWorkspaceSnapshot()) paths.add(file.path);
	return Array.from(paths);
}
function callableFrameToBackendItem(frame, file) {
	return {
		name: frame.name,
		kind: frame.kind,
		file,
		startLine: frame.letStartLine,
		startColumn: frame.letStartColumn,
		endLine: frame.letEndLine,
		endColumn: frame.letEndColumn,
		selectionStartLine: frame.defLine,
		selectionStartColumn: frame.defColumn,
		selectionEndLine: frame.defLine,
		selectionEndColumn: frame.defColumn + frame.name.length
	};
}
function fileScopeCallHierarchyItem(filePath) {
	return {
		name: `<${filePath.split("/").pop() ?? filePath}>`,
		kind: "function",
		file: filePath,
		startLine: 1,
		startColumn: 1,
		endLine: 1,
		endColumn: 1,
		selectionStartLine: 1,
		selectionStartColumn: 1,
		selectionEndLine: 1,
		selectionEndColumn: 1
	};
}
function computeCompletionResult(request, workspaceFiles) {
	if (request.source === void 0) return [];
	if (request.importPrefix !== null) return getImportCompletionItems(request.importPrefix, request.path, workspaceFiles);
	const snapshotFiles = new Map(workspaceFiles.map((file) => [file.path, file.code]));
	snapshotFiles.set(request.path, request.source);
	const index = new WorkspaceIndex();
	indexWorkspaceSnapshot(request.path, request.source, snapshotFiles, index);
	const currentFileSymbols = index.getFileSymbols(request.path);
	const seen = /* @__PURE__ */ new Set();
	const items = [];
	for (const item of getScopedCompletionItems(request.prefix, index.getSymbolsInScope(request.path, request.line, request.column))) {
		if (seen.has(item.label)) continue;
		seen.add(item.label);
		items.push(item);
	}
	for (const item of getImportedExportCompletionItems(request.prefix, currentFileSymbols, (filePath) => index.getFileSymbols(filePath))) {
		if (seen.has(item.label)) continue;
		seen.add(item.label);
		items.push(item);
	}
	return items;
}
function getImportPathAtSourcePosition(source, line, column) {
	const lineText = source.split("\n")[line - 1];
	if (lineText === void 0) return null;
	const beforeCursor = lineText.slice(0, Math.max(0, column - 1));
	const prefixMatch = /import\(\s*"([^"]*)$/.exec(beforeCursor);
	if (!prefixMatch) return null;
	const afterCursor = lineText.slice(Math.max(0, column - 1));
	const suffixMatch = /^([^"]*)"/.exec(afterCursor);
	const rawPath = `${prefixMatch[1] ?? ""}${suffixMatch?.[1] ?? ""}`;
	return rawPath.length > 0 ? rawPath : "";
}
function computeNavigationResult(request, workspaceFiles) {
	if (request.source === void 0) return request.kind === "rename" ? { edits: [] } : { locations: [] };
	const snapshotFiles = new Map(workspaceFiles.map((file) => [file.path, file.code]));
	snapshotFiles.set(request.path, request.source);
	const index = new WorkspaceIndex();
	indexWorkspaceSnapshot(request.path, request.source, snapshotFiles, index);
	if (request.kind === "definition") {
		const importPath = getImportPathAtSourcePosition(request.source, request.line, request.column);
		if (importPath !== null) {
			const resolved = resolveWorkspaceImportPathForSnapshot(snapshotFiles, importPath, request.path);
			if (resolved) return { locations: [{
				file: resolved,
				line: 1,
				column: 1,
				endColumn: 1
			}] };
		}
		const def = index.findDefinition(request.path, request.line, request.column);
		return { locations: def ? [{
			file: def.location.file,
			line: def.location.line,
			column: def.location.column,
			endColumn: def.location.column + def.name.length
		}] : [] };
	}
	const canonical = index.resolveCanonicalFile(request.path, request.line, request.column);
	if (!canonical) return request.kind === "rename" ? { edits: [] } : { locations: [] };
	const occurrences = index.findAllOccurrences(canonical.file, canonical.name);
	if (request.kind === "references") return { locations: occurrences.map((loc) => ({
		file: loc.file,
		line: loc.line,
		column: loc.column,
		endColumn: loc.column + loc.nameLength
	})) };
	return { edits: occurrences.map((loc) => ({
		file: loc.file,
		text: request.newName ?? canonical.name,
		range: {
			startLine: loc.line,
			startColumn: loc.column,
			endLine: loc.line,
			endColumn: loc.column + loc.nameLength
		}
	})) };
}
function indexAllBackendDocuments(documents, index) {
	const seen = /* @__PURE__ */ new Set();
	for (const file of documents.getWorkspaceSnapshot()) indexBackendDocuments(file.path, documents, index, seen);
	for (const document of documents.getOpenDocuments()) indexBackendDocuments(document.path, documents, index, seen);
}
function getEffectiveWorkspaceSnapshot(documents) {
	const snapshot = /* @__PURE__ */ new Map();
	for (const file of documents.getWorkspaceSnapshot()) snapshot.set(file.path, file);
	for (const document of documents.getOpenDocuments()) snapshot.set(document.path, {
		path: document.path,
		code: document.source
	});
	return [...snapshot.values()];
}
function toBackendDocumentSymbol(def) {
	return {
		name: def.name,
		kind: def.kind,
		line: def.location.line,
		column: def.location.column
	};
}
function isCallableType(type) {
	if (type.tag === "Function" || type.tag === "AnyFunction") return true;
	if (type.tag === "Inter" && type.members && type.members.length > 0) return type.members.every(isCallableType);
	if (type.tag === "Var" && type.lowerBounds && type.lowerBounds.length > 0) return type.lowerBounds.every(isCallableType);
	return false;
}
/**
* Find the Let AST node whose simple-symbol binding matches `def`'s
* location, and return the source ranges needed by the inline-variable
* refactor (the let's full range + the value expression's range).
*
* Returns `null` if no such Let exists, the binding isn't a simple-symbol
* shape, or any of the ranges are missing from the source map.
*
* Constraints:
*  - Simple-symbol bindings only — `['symbol', [SymNode, default?], nodeId]`.
*    Destructuring (array / object), rest, and literal patterns are out of
*    scope: "inline one element" / "inline one field" each need their own
*    refactor design.
*  - Match is on the SymNode's source POSITION, not its nodeId — the
*    symbol-table builder registers the def with the SymNode's nodeId,
*    but the bindingTarget itself has a different nodeId, and various
*    consumers pick differently. Position is the stable identifier.
*  - `bindingTarget[0]` is a BindingTargetType (`'symbol' | 'array' | …`),
*    not a NodeType — the `as string` cast is intentional.
*/
function findEnclosingLetRanges(ast, sourceMap, def) {
	if (!sourceMap) return null;
	let result = null;
	walkAst(ast, (node) => {
		if (result) return;
		if (node[0] !== NodeTypes.Let || !Array.isArray(node[1])) return;
		const [bindingTarget, valueExpr] = node[1];
		if (bindingTarget[0] !== "symbol" || !Array.isArray(bindingTarget[1])) return;
		const symNode = bindingTarget[1][0];
		if (symNode[0] !== NodeTypes.Sym || symNode[1] !== def.name) return;
		const symPos = sourceMap.get(symNode[symNode.length - 1]);
		if (!symPos) return;
		if (symPos.start[0] + 1 !== def.location.line || symPos.start[1] + 1 !== def.location.column) return;
		const letPos = sourceMap.get(node[node.length - 1]);
		const valuePos = sourceMap.get(valueExpr[valueExpr.length - 1]);
		if (!letPos || !valuePos) return;
		result = {
			letStartLine: letPos.start[0] + 1,
			letStartColumn: letPos.start[1] + 1,
			letEndLine: letPos.end[0] + 1,
			letEndColumn: letPos.end[1] + 1,
			valueStartLine: valuePos.start[0] + 1,
			valueStartColumn: valuePos.start[1] + 1,
			valueEndLine: valuePos.end[0] + 1,
			valueEndColumn: valuePos.end[1] + 1
		};
	});
	return result;
}
function findEnclosingMatchRange(ast, sourceMap, line, column) {
	if (!sourceMap) return null;
	const targetLine = line - 1;
	const targetCol = column - 1;
	let best;
	walkAst(ast, (node) => {
		if (node[0] !== NodeTypes.Match) return;
		const nodeId = node[node.length - 1];
		const pos = sourceMap.get(nodeId);
		if (!pos || !positionContains(pos, targetLine, targetCol)) return;
		const size = rangeSize(pos);
		if (!best || size < best.size) best = {
			pos,
			size
		};
	});
	if (!best) return null;
	return {
		endLine: best.pos.end[0] + 1,
		endColumn: best.pos.end[1] + 1
	};
}
function collectSelectionRanges(ast, sourceMap, position) {
	if (!sourceMap) return [];
	const targetLine = position.line - 1;
	const targetCol = position.column - 1;
	const containing = [];
	walkAst(ast, (node) => {
		const nodeId = node[node.length - 1];
		const pos = sourceMap.get(nodeId);
		if (pos && positionContains(pos, targetLine, targetCol)) containing.push(pos);
	});
	containing.sort((a, b) => rangeSize(a) - rangeSize(b));
	const result = [];
	let prev;
	for (const pos of containing) {
		const range = {
			startLine: pos.start[0] + 1,
			startColumn: pos.start[1] + 1,
			endLine: pos.end[0] + 1,
			endColumn: pos.end[1] + 1
		};
		if (!prev || prev.startLine !== range.startLine || prev.startColumn !== range.startColumn || prev.endLine !== range.endLine || prev.endColumn !== range.endColumn) {
			result.push(range);
			prev = range;
		}
	}
	return result;
}
function positionContains(pos, line, column) {
	const afterStart = line > pos.start[0] || line === pos.start[0] && column >= pos.start[1];
	const beforeEnd = line < pos.end[0] || line === pos.end[0] && column <= pos.end[1];
	return afterStart && beforeEnd;
}
function rangeSize(pos) {
	const lineSpan = pos.end[0] - pos.start[0];
	if (lineSpan === 0) return pos.end[1] - pos.start[1];
	return lineSpan * 1e6 + pos.end[1] - pos.start[1];
}
function collectCallInlayHints(ast, sourceMap, refByNodeId, out) {
	walkAst(ast, (node) => {
		if (node[0] !== NodeTypes.Call || !Array.isArray(node[1])) return;
		const [callee, args] = node[1];
		const params = paramNamesForCallee(callee, refByNodeId);
		if (params) emitArgHints(args, params, sourceMap, out);
	});
}
function isAstNode(value) {
	return Array.isArray(value) && typeof value[0] === "string" && typeof value[value.length - 1] === "number";
}
const NON_EXTRACTABLE_NODE_TYPES = new Set([
	NodeTypes.Let,
	NodeTypes.Block,
	NodeTypes.Binding,
	NodeTypes.Sym,
	NodeTypes.Builtin,
	NodeTypes.Special,
	NodeTypes.Reserved,
	NodeTypes.Effect,
	NodeTypes.Spread,
	NodeTypes.Splice,
	NodeTypes.InlinedData,
	"symbol",
	"array",
	"object",
	"rest",
	"literal",
	"wildcard"
]);
function findSmallestExtractableNode(ast, sourceMap, line, column) {
	if (!sourceMap) return null;
	let best = null;
	let bestSize = Infinity;
	walkAst(ast, (node) => {
		if (typeof node[0] !== "string" || NON_EXTRACTABLE_NODE_TYPES.has(node[0])) return;
		const pos = sourceMap.get(node[node.length - 1]);
		if (!pos) return;
		const startLine = pos.start[0] + 1;
		const startColumn = pos.start[1] + 1;
		const endLine = pos.end[0] + 1;
		const endColumn = pos.end[1] + 1;
		if (!((line > startLine || line === startLine && column >= startColumn) && (line < endLine || line === endLine && column <= endColumn))) return;
		const size = (endLine - startLine) * 1e6 + (endColumn - startColumn);
		if (size < bestSize) {
			bestSize = size;
			best = {
				startLine,
				startColumn,
				endLine,
				endColumn
			};
		}
	});
	return best;
}
function walkAst(nodes, visit) {
	for (const node of nodes) walkAstNode(node, visit);
}
function walkAstNode(node, visit) {
	visit(node);
	descendAstChildren(node[1], visit);
}
function descendAstChildren(value, visit) {
	if (isAstNode(value)) {
		walkAstNode(value, visit);
		return;
	}
	if (Array.isArray(value)) for (const item of value) descendAstChildren(item, visit);
}
function paramNamesForCallee(callee, refByNodeId) {
	if (callee[0] === NodeTypes.Sym) {
		const params = refByNodeId.get(callee[2])?.resolvedDef?.params;
		return params && params.length > 0 ? params : null;
	}
	if (callee[0] === NodeTypes.Builtin) {
		const name = callee[1];
		if (!isDvalaIdentifierName(name)) return null;
		const ref = allReference[name];
		if (ref && isFunctionReference$1(ref)) {
			const variant = ref.variants[0];
			return variant && variant.argumentNames.length > 0 ? variant.argumentNames : null;
		}
	}
	return null;
}
function emitArgHints(args, params, sourceMap, out) {
	if (!sourceMap) return;
	for (let i = 0; i < args.length && i < params.length; i++) {
		const arg = args[i];
		const paramName = params[i];
		if (arg[0] === NodeTypes.Sym && arg[1] === paramName) continue;
		const pos = sourceMap.get(arg[arg.length - 1]);
		if (!pos) continue;
		out.push({
			line: pos.start[0] + 1,
			column: pos.start[1] + 1,
			label: `${paramName}:`
		});
	}
}
function symbolKindToTokenType(kind) {
	switch (kind) {
		case "function": return "function";
		case "macro": return "macro";
		case "parameter": return "parameter";
		case "import": return "namespace";
		case "handler":
		case "variable": return "variable";
	}
}
function computeSignatureHelpResult(request, documents) {
	const callCtx = findCallContext(request.source, {
		line: request.line,
		column: request.column
	});
	if (!callCtx) return {
		activeParameter: 0,
		signatures: []
	};
	const ref = allReference[callCtx.functionName];
	if (ref && isFunctionReference$1(ref)) return {
		activeParameter: callCtx.activeParam,
		signatures: ref.variants.map((variant) => {
			const parameters = variant.argumentNames.map((name) => {
				const argInfo = ref.args[name];
				const typeStr = argInfo ? Array.isArray(argInfo.type) ? argInfo.type.join(" | ") : argInfo.type : "";
				return typeStr ? `${name}: ${typeStr}` : name;
			});
			return {
				label: `${callCtx.functionName}(${parameters.join(", ")})`,
				parameters
			};
		})
	};
	const index = new WorkspaceIndex();
	indexBackendDocuments(request.path, documents, index);
	const funcDef = index.getDefinitions(request.path).find((def) => def.name === callCtx.functionName && def.params);
	if (!funcDef?.params) return {
		activeParameter: callCtx.activeParam,
		signatures: []
	};
	return {
		activeParameter: callCtx.activeParam,
		signatures: [{
			label: `${callCtx.functionName}(${funcDef.params.join(", ")})`,
			parameters: funcDef.params
		}]
	};
}
function createBackend(options = {}) {
	const documents = options.documents ?? createInMemoryDocumentStore();
	const runtime = options.runtime ?? createBackendRuntimeAdapter(documents);
	const cancelledRequests = /* @__PURE__ */ new Map();
	const typecheckCache = /* @__PURE__ */ new Map();
	function getOrComputeTypecheck(path, source, version) {
		const cached = typecheckCache.get(path);
		if (cached && cached.version === version) return cached.result;
		const result = computeTypecheckResult(source, path, documents, options.createDvala);
		typecheckCache.set(path, {
			version,
			result
		});
		return result;
	}
	function invalidateTypecheckCache() {
		typecheckCache.clear();
	}
	function runtimeCancelledFailure(requestId, message, path) {
		clearCancelledRequest(cancelledRequests, requestId);
		return requestFailure(requestId, {
			kind: "cancelled",
			message,
			...path ? { path } : {}
		}, path);
	}
	function runtimeErrorFailure(requestId, error, path) {
		clearCancelledRequest(cancelledRequests, requestId);
		return requestFailure(requestId, {
			kind: "runtime-failed",
			message: error instanceof Error ? error.message : `${error}`,
			...path ? { path } : {}
		}, path);
	}
	async function runRuntimeRequest(requestOptions) {
		try {
			if (isCancelled(cancelledRequests, requestOptions.requestId)) return runtimeCancelledFailure(requestOptions.requestId, requestOptions.cancelMessage, requestOptions.path);
			const value = await requestOptions.run();
			if (isCancelled(cancelledRequests, requestOptions.requestId)) {
				await requestOptions.onCancelled?.(value);
				return runtimeCancelledFailure(requestOptions.requestId, requestOptions.cancelMessage, requestOptions.path);
			}
			clearCancelledRequest(cancelledRequests, requestOptions.requestId);
			return requestOptions.success(value);
		} catch (error) {
			return runtimeErrorFailure(requestOptions.requestId, error, requestOptions.path);
		}
	}
	return {
		async openDocument(document) {
			documents.open(document);
			invalidateTypecheckCache();
		},
		async updateDocument(document, previousVersion) {
			const result = documents.update(document, previousVersion);
			invalidateTypecheckCache();
			return result;
		},
		async closeDocument(path) {
			documents.close(path);
			invalidateTypecheckCache();
		},
		async persistFile(request) {
			documents.persistFile(request);
			invalidateTypecheckCache();
		},
		async removeFile(request) {
			documents.removeFile(request);
			invalidateTypecheckCache();
		},
		async requestDiagnostics(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (!openDocument || openDocument.version !== request.version) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const index = new WorkspaceIndex();
				indexBackendDocuments(openDocument.path, documents, index);
				const { parseErrors, unresolvedRefs } = index.getDiagnostics(openDocument.path);
				const parseDiagnostics = buildParseDiagnostics(parseErrors);
				const symbolDiagnostics = buildSymbolDiagnostics(unresolvedRefs);
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend diagnostics request cancelled",
						path: request.path
					}, request.path);
				}
				const typeDiagnostics = buildTypeDiagnostics(getOrComputeTypecheck(openDocument.path, openDocument.source, openDocument.version));
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend diagnostics request cancelled",
						path: request.path
					}, request.path);
				}
				clearCancelledRequest(cancelledRequests, request.requestId);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					diagnostics: [
						...parseDiagnostics,
						...symbolDiagnostics,
						...typeDiagnostics
					]
				};
			} catch (error) {
				clearCancelledRequest(cancelledRequests, request.requestId);
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestFormatting(request) {
			try {
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend formatting request cancelled",
						path: request.path
					}, request.path);
				}
				const openDocument = documents.getOpenDocument(request.path);
				if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
					kind: "resync-required",
					message: `Backend document mirror missing or stale for ${request.path}`,
					path: request.path
				}, request.path);
				const formatted = formatSource(request.source ?? openDocument?.source ?? "");
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend formatting request cancelled",
						path: request.path
					}, request.path);
				}
				clearCancelledRequest(cancelledRequests, request.requestId);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					formatted
				};
			} catch (error) {
				clearCancelledRequest(cancelledRequests, request.requestId);
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestHover(request) {
			try {
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend hover request cancelled",
						path: request.path
					}, request.path);
				}
				const openDocument = documents.getOpenDocument(request.path);
				if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
					kind: "resync-required",
					message: `Backend document mirror missing or stale for ${request.path}`,
					path: request.path
				}, request.path);
				const effectiveSource = request.source ?? openDocument?.source;
				const effectiveVersion = openDocument?.version ?? request.version;
				const inferredType = effectiveSource ? computeHoverResult({
					...request,
					source: effectiveSource
				}, getOrComputeTypecheck(request.path, effectiveSource, effectiveVersion)) : void 0;
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend hover request cancelled",
						path: request.path
					}, request.path);
				}
				clearCancelledRequest(cancelledRequests, request.requestId);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					...inferredType ? { inferredType } : {}
				};
			} catch (error) {
				clearCancelledRequest(cancelledRequests, request.requestId);
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestSignatureHelp(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (!openDocument || openDocument.version !== request.version) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const result = computeSignatureHelpResult(request, documents);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					activeParameter: result.activeParameter,
					signatures: result.signatures
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestDocumentSymbols(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (!openDocument || openDocument.version !== request.version) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.path, documents, index);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					symbols: index.getDocumentSymbols(request.path).map(toBackendDocumentSymbol)
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestSemanticTokens(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.path, documents, index);
				const fileSymbols = index.getFileSymbols(request.path);
				if (!fileSymbols) return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					tokens: []
				};
				const effectiveSource = request.source ?? openDocument?.source ?? "";
				const effectiveVersion = openDocument?.version ?? request.version;
				const typecheckResult = getOrComputeTypecheck(request.path, effectiveSource, effectiveVersion);
				const firstRefNodeByDef = /* @__PURE__ */ new Map();
				for (const ref of fileSymbols.references) {
					if (!ref.resolvedDef || firstRefNodeByDef.has(ref.resolvedDef)) continue;
					firstRefNodeByDef.set(ref.resolvedDef, ref.nodeId);
				}
				const resolveTokenType = (def) => {
					if (def.kind !== "import" || def.importedName === void 0) return symbolKindToTokenType(def.kind);
					const refNode = firstRefNodeByDef.get(def);
					if (refNode === void 0) return "variable";
					const inferred = typecheckResult.typeMap.get(refNode);
					if (inferred && isCallableType(inferred)) return "function";
					return "variable";
				};
				const tokens = [];
				for (const def of fileSymbols.definitions) tokens.push({
					line: def.location.line,
					startColumn: def.location.column,
					length: def.name.length,
					tokenType: resolveTokenType(def),
					modifiers: ["declaration"]
				});
				for (const ref of fileSymbols.references) {
					if (!ref.resolvedDef) continue;
					tokens.push({
						line: ref.location.line,
						startColumn: ref.location.column,
						length: ref.name.length,
						tokenType: resolveTokenType(ref.resolvedDef),
						modifiers: []
					});
				}
				tokens.sort((a, b) => a.line - b.line || a.startColumn - b.startColumn);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					tokens
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestInlayHints(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const effectiveSource = request.source ?? openDocument?.source ?? "";
				const effectiveVersion = openDocument?.version ?? request.version;
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.path, documents, index);
				const fileSymbols = index.getFileSymbols(request.path);
				if (!fileSymbols) return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					hints: []
				};
				const ast = parseToAst(minifyTokenStream(tokenizeSource(effectiveSource, true, request.path), { removeWhiteSpace: true }));
				const typecheckResult = getOrComputeTypecheck(request.path, effectiveSource, effectiveVersion);
				const refByNodeId = new Map(fileSymbols.references.map((ref) => [ref.nodeId, ref]));
				const hints = [];
				collectCallInlayHints(ast.body, typecheckResult.sourceMap, refByNodeId, hints);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					hints
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestCodeActions(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const effectiveSource = request.source ?? openDocument?.source ?? "";
				const ast = parseToAst(minifyTokenStream(tokenizeSource(effectiveSource, true, request.path), { removeWhiteSpace: true }));
				const effectiveVersion = openDocument?.version ?? request.version;
				const typecheckResult = getOrComputeTypecheck(request.path, effectiveSource, effectiveVersion);
				const actions = [];
				const codeActionsIndex = new WorkspaceIndex();
				indexBackendDocuments(request.path, documents, codeActionsIndex);
				for (const diagnostic of request.diagnostics) {
					if (!diagnostic.message.startsWith("Non-exhaustive match")) continue;
					const matchRange = findEnclosingMatchRange(ast.body, typecheckResult.sourceMap, diagnostic.startLine, diagnostic.startColumn);
					if (!matchRange) continue;
					const edit = computeCatchallEdit(effectiveSource, matchRange);
					if (!edit) continue;
					actions.push({
						title: "Add 'case _ then perform(@dvala.error, ...)' catchall",
						kind: "quickfix",
						edits: [edit],
						fixesDiagnostics: [{
							message: diagnostic.message,
							startLine: diagnostic.startLine,
							startColumn: diagnostic.startColumn,
							endLine: diagnostic.endLine,
							endColumn: diagnostic.endColumn
						}]
					});
				}
				const codeActionsFileSymbols = codeActionsIndex.getFileSymbols(request.path);
				let cursorDef = codeActionsIndex.findDefinition(request.path, request.startLine, request.startColumn);
				if (!cursorDef && codeActionsFileSymbols) cursorDef = codeActionsFileSymbols.definitions.find((d) => d.location.line === request.startLine && request.startColumn >= d.location.column && request.startColumn < d.location.column + d.name.length) ?? null;
				if (cursorDef && cursorDef.kind === "variable") {
					const letInfo = findEnclosingLetRanges(ast.body, typecheckResult.sourceMap, cursorDef);
					if (letInfo) {
						const sourceLines = effectiveSource.split("\n");
						const letLineText = sourceLines[letInfo.letStartLine - 1];
						if (letLineText !== void 0 && letInfo.letStartLine === letInfo.letEndLine && letLineText.slice(0, letInfo.letStartColumn - 1).trim() === "") {
							const valueLine = sourceLines[letInfo.valueStartLine - 1];
							if (valueLine !== void 0 && letInfo.valueStartLine === letInfo.valueEndLine) {
								const valueText = valueLine.slice(letInfo.valueStartColumn - 1, letInfo.valueEndColumn - 1);
								const references = (codeActionsFileSymbols?.references ?? []).filter((ref) => ref.resolvedDef === cursorDef).map((ref) => ({
									line: ref.location.line,
									column: ref.location.column,
									length: ref.name.length
								}));
								const inlineEdits = computeInlineVariableEdits({
									source: effectiveSource,
									letRemoveStartLine: letInfo.letStartLine,
									letRemoveStartColumn: 1,
									letRemoveEndLine: letInfo.letStartLine + 1,
									letRemoveEndColumn: 1,
									valueText,
									references
								});
								if (inlineEdits) actions.push({
									title: `Inline variable '${cursorDef.name}'`,
									kind: "refactor.inline",
									edits: inlineEdits
								});
							}
						}
					}
				}
				const hasNonEmptySelection = request.startLine < request.endLine || request.startLine === request.endLine && request.endColumn > request.startColumn;
				const extractRange = hasNonEmptySelection ? {
					startLine: request.startLine,
					startColumn: request.startColumn,
					endLine: request.endLine,
					endColumn: request.endColumn
				} : findSmallestExtractableNode(ast.body, typecheckResult.sourceMap, request.startLine, request.startColumn);
				if (extractRange) {
					const refactorEdits = computeExtractVariableEdit({
						source: effectiveSource,
						expressionStartLine: extractRange.startLine,
						expressionStartColumn: extractRange.startColumn,
						expressionEndLine: extractRange.endLine,
						expressionEndColumn: extractRange.endColumn,
						statementStartLine: extractRange.startLine,
						statementStartColumn: extractRange.startColumn
					});
					if (refactorEdits) {
						const exprText = effectiveSource.split("\n")[extractRange.startLine - 1].slice(extractRange.startColumn - 1, extractRange.endColumn - 1);
						const previewText = exprText.length > 40 ? `${exprText.slice(0, 37)}...` : exprText;
						actions.push({
							title: `Extract '${previewText}' to variable`,
							kind: "refactor.extract",
							edits: [refactorEdits.letInsertion, refactorEdits.expressionReplacement]
						});
					}
				}
				if (hasNonEmptySelection) {
					const positionInsideSelection = (line, column) => (line > request.startLine || line === request.startLine && column >= request.startColumn) && (line < request.endLine || line === request.endLine && column < request.endColumn);
					const freeVars = [];
					const seenFreeVars = /* @__PURE__ */ new Set();
					for (const ref of codeActionsFileSymbols?.references ?? []) {
						if (!ref.resolvedDef) continue;
						if (!positionInsideSelection(ref.location.line, ref.location.column)) continue;
						if (positionInsideSelection(ref.resolvedDef.location.line, ref.resolvedDef.location.column)) continue;
						if (seenFreeVars.has(ref.name)) continue;
						seenFreeVars.add(ref.name);
						freeVars.push(ref.name);
					}
					const fnEdits = computeExtractFunctionEdit({
						source: effectiveSource,
						selectionStartLine: request.startLine,
						selectionStartColumn: request.startColumn,
						selectionEndLine: request.endLine,
						selectionEndColumn: request.endColumn,
						freeVars
					});
					if (fnEdits) {
						const fnSelectionText = effectiveSource.split("\n")[request.startLine - 1].slice(request.startColumn - 1, request.endColumn - 1).trim();
						const fnPreview = fnSelectionText.length > 40 ? `${fnSelectionText.slice(0, 37)}...` : fnSelectionText;
						actions.push({
							title: fnPreview ? `Extract '${fnPreview}' to function` : "Extract selection to function",
							kind: "refactor.extract",
							edits: [fnEdits.letInsertion, fnEdits.selectionReplacement]
						});
					}
				}
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					actions
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestSelectionRange(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const effectiveSource = request.source ?? openDocument?.source ?? "";
				const effectiveVersion = openDocument?.version ?? request.version;
				const typecheckResult = getOrComputeTypecheck(request.path, effectiveSource, effectiveVersion);
				const ast = parseToAst(minifyTokenStream(tokenizeSource(effectiveSource, true, request.path), { removeWhiteSpace: true }));
				const ranges = request.positions.map((position) => collectSelectionRanges(ast.body, typecheckResult.sourceMap, position));
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					ranges
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestCallHierarchyPrepare(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const effectiveSource = request.source ?? openDocument?.source ?? "";
				const effectiveVersion = openDocument?.version ?? request.version;
				const typecheckResult = getOrComputeTypecheck(request.path, effectiveSource, effectiveVersion);
				const ast = parseToAst(minifyTokenStream(tokenizeSource(effectiveSource, true, request.path), { removeWhiteSpace: true }));
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.path, documents, index);
				const fileSymbols = index.getFileSymbols(request.path);
				let cursorDef = index.findDefinition(request.path, request.line, request.column);
				if (!cursorDef && fileSymbols) cursorDef = fileSymbols.definitions.find((d) => d.location.line === request.line && request.column >= d.location.column && request.column < d.location.column + d.name.length) ?? null;
				const items = [];
				if (cursorDef && CALLABLE_DEF_KINDS.has(cursorDef.kind)) {
					const frame = collectCallableFrames(ast.body, typecheckResult.sourceMap, fileSymbols).find((f) => f.defLine === cursorDef.location.line && f.defColumn === cursorDef.location.column);
					if (frame) items.push(callableFrameToBackendItem(frame, cursorDef.location.file));
				}
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					items
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestCallHierarchyIncomingCalls(request) {
			try {
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.item.file, documents, index);
				const groups = /* @__PURE__ */ new Map();
				for (const filePath of listAllKnownPaths(documents)) {
					indexBackendDocuments(filePath, documents, index);
					const fileSymbols = index.getFileSymbols(filePath);
					if (!fileSymbols) continue;
					const matchingRefs = fileSymbols.references.filter((ref) => {
						if (!ref.resolvedDef) return false;
						if (ref.resolvedDef.location.file === request.item.file && ref.resolvedDef.location.line === request.item.selectionStartLine && ref.resolvedDef.location.column === request.item.selectionStartColumn) return true;
						if (ref.resolvedDef.kind === "import" && ref.resolvedDef.importPath !== void 0 && ref.resolvedDef.importedName === request.item.name) {
							if (fileSymbols.imports.get(ref.resolvedDef.importPath) === request.item.file) return true;
						}
						return false;
					});
					if (matchingRefs.length === 0) continue;
					const source = documents.getEffectiveSource(filePath);
					if (source === void 0) continue;
					const typecheckResult = getOrComputeTypecheck(filePath, source, documents.getOpenDocument(filePath)?.version ?? 0);
					const frames = collectCallableFrames(parseToAst(minifyTokenStream(tokenizeSource(source, true, filePath), { removeWhiteSpace: true })).body, typecheckResult.sourceMap, fileSymbols);
					for (const ref of matchingRefs) {
						const enclosing = findEnclosingCallableFrame(frames, ref.location.line, ref.location.column);
						const fromItem = enclosing ? callableFrameToBackendItem(enclosing, filePath) : fileScopeCallHierarchyItem(filePath);
						const key = `${fromItem.file}::${fromItem.name}::${fromItem.selectionStartLine}:${fromItem.selectionStartColumn}`;
						const existing = groups.get(key);
						const callRange = {
							startLine: ref.location.line,
							startColumn: ref.location.column,
							endLine: ref.location.line,
							endColumn: ref.location.column + ref.name.length
						};
						if (existing) existing.fromRanges.push(callRange);
						else groups.set(key, {
							from: fromItem,
							fromRanges: [callRange]
						});
					}
				}
				return {
					ok: true,
					requestId: request.requestId,
					version: request.version,
					calls: Array.from(groups.values())
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.item.file
				}, request.item.file);
			}
		},
		async requestCallHierarchyOutgoingCalls(request) {
			try {
				const source = documents.getEffectiveSource(request.item.file);
				if (source === void 0) return {
					ok: true,
					requestId: request.requestId,
					version: request.version,
					calls: []
				};
				const docVersion = documents.getOpenDocument(request.item.file)?.version ?? request.version;
				const typecheckResult = getOrComputeTypecheck(request.item.file, source, docVersion);
				const ast = parseToAst(minifyTokenStream(tokenizeSource(source, true, request.item.file), { removeWhiteSpace: true }));
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.item.file, documents, index);
				const fileSymbols = index.getFileSymbols(request.item.file);
				if (!fileSymbols || !typecheckResult.sourceMap) return {
					ok: true,
					requestId: request.requestId,
					version: request.version,
					calls: []
				};
				const fileFramesCache = /* @__PURE__ */ new Map();
				const getFramesFor = (file) => {
					const cached = fileFramesCache.get(file);
					if (cached) return cached;
					const fileSource = documents.getEffectiveSource(file);
					if (fileSource === void 0) return null;
					const fileTypecheck = getOrComputeTypecheck(file, fileSource, documents.getOpenDocument(file)?.version ?? 0);
					const fileAst = parseToAst(minifyTokenStream(tokenizeSource(fileSource, true, file), { removeWhiteSpace: true }));
					const fileFileSymbols = index.getFileSymbols(file);
					const frames = collectCallableFrames(fileAst.body, fileTypecheck.sourceMap, fileFileSymbols);
					fileFramesCache.set(file, frames);
					return frames;
				};
				const frames = collectCallableFrames(ast.body, typecheckResult.sourceMap, fileSymbols);
				const sourceFrame = frames.find((f) => f.defLine === request.item.selectionStartLine && f.defColumn === request.item.selectionStartColumn);
				if (!sourceFrame) return {
					ok: true,
					requestId: request.requestId,
					version: request.version,
					calls: []
				};
				fileFramesCache.set(request.item.file, frames);
				const refByNodeId = new Map(fileSymbols.references.map((ref) => [ref.nodeId, ref]));
				const calls = /* @__PURE__ */ new Map();
				for (const callSite of walkCallSites(ast.body, typecheckResult.sourceMap)) {
					const startLine = callSite.callRange.start[0] + 1;
					const startColumn = callSite.callRange.start[1] + 1;
					if (!((startLine > sourceFrame.bodyStartLine || startLine === sourceFrame.bodyStartLine && startColumn >= sourceFrame.bodyStartColumn) && (startLine < sourceFrame.bodyEndLine || startLine === sourceFrame.bodyEndLine && startColumn <= sourceFrame.bodyEndColumn))) continue;
					const ref = refByNodeId.get(callSite.calleeNodeId);
					if (!ref?.resolvedDef) continue;
					const def = ref.resolvedDef;
					if (!CALLABLE_DEF_KINDS.has(def.kind)) continue;
					const defFrames = getFramesFor(def.location.file);
					if (!defFrames) continue;
					const defFrame = defFrames.find((f) => f.defLine === def.location.line && f.defColumn === def.location.column);
					if (!defFrame) continue;
					const toItem = callableFrameToBackendItem(defFrame, def.location.file);
					const key = `${toItem.file}::${toItem.name}::${toItem.selectionStartLine}:${toItem.selectionStartColumn}`;
					const callRange = {
						startLine,
						startColumn,
						endLine: callSite.callRange.end[0] + 1,
						endColumn: callSite.callRange.end[1] + 1
					};
					const existing = calls.get(key);
					if (existing) existing.fromRanges.push(callRange);
					else calls.set(key, {
						to: toItem,
						fromRanges: [callRange]
					});
				}
				return {
					ok: true,
					requestId: request.requestId,
					version: request.version,
					calls: Array.from(calls.values())
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.item.file
				}, request.item.file);
			}
		},
		async requestSymbolAtPosition(request) {
			const openDocument = documents.getOpenDocument(request.path);
			if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
				kind: "resync-required",
				message: `Backend document mirror missing or stale for ${request.path}`,
				path: request.path
			}, request.path);
			try {
				const index = new WorkspaceIndex();
				indexBackendDocuments(request.path, documents, index);
				const symbolAtPos = index.getSymbolAtPosition(request.path, request.line, request.column);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					...symbolAtPos ? { symbol: {
						name: symbolAtPos.name,
						...symbolAtPos.onKey ? { onKey: true } : {}
					} } : {}
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestWorkspaceSymbols(request) {
			try {
				const workspaceIndex = new WorkspaceIndex();
				indexAllBackendDocuments(documents, workspaceIndex);
				const lowerQuery = request.query.toLowerCase();
				const symbols = documents.getOpenDocuments().map((doc) => doc.path).concat(documents.getWorkspaceSnapshot().map((file) => file.path)).filter((path, pathIndex, paths) => paths.indexOf(path) === pathIndex).flatMap((path) => workspaceIndex.getDocumentSymbols(path)).filter((def) => lowerQuery ? def.name.toLowerCase().includes(lowerQuery) : true).map((def) => ({
					file: def.location.file,
					...toBackendDocumentSymbol(def)
				}));
				return {
					ok: true,
					requestId: request.requestId,
					symbols
				};
			} catch (error) {
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`
				});
			}
		},
		async requestCompletion(request) {
			try {
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend completion request cancelled",
						path: request.path
					}, request.path);
				}
				const openDocument = documents.getOpenDocument(request.path);
				if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
					kind: "resync-required",
					message: `Backend document mirror missing or stale for ${request.path}`,
					path: request.path
				}, request.path);
				const items = computeCompletionResult({
					...request,
					source: request.source ?? openDocument?.source
				}, getEffectiveWorkspaceSnapshot(documents));
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend completion request cancelled",
						path: request.path
					}, request.path);
				}
				clearCancelledRequest(cancelledRequests, request.requestId);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					items
				};
			} catch (error) {
				clearCancelledRequest(cancelledRequests, request.requestId);
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async requestNavigation(request) {
			try {
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend navigation request cancelled",
						path: request.path
					}, request.path);
				}
				const openDocument = documents.getOpenDocument(request.path);
				if (request.source === void 0 && (!openDocument || openDocument.version !== request.version)) return requestFailure(request.requestId, {
					kind: "resync-required",
					message: `Backend document mirror missing or stale for ${request.path}`,
					path: request.path
				}, request.path);
				const result = computeNavigationResult({
					...request,
					source: request.source ?? openDocument?.source
				}, getEffectiveWorkspaceSnapshot(documents));
				if (isCancelled(cancelledRequests, request.requestId)) {
					clearCancelledRequest(cancelledRequests, request.requestId);
					return requestFailure(request.requestId, {
						kind: "cancelled",
						message: "Backend navigation request cancelled",
						path: request.path
					}, request.path);
				}
				clearCancelledRequest(cancelledRequests, request.requestId);
				return {
					ok: true,
					requestId: request.requestId,
					path: request.path,
					version: request.version,
					kind: request.kind,
					...result
				};
			} catch (error) {
				clearCancelledRequest(cancelledRequests, request.requestId);
				return requestFailure(request.requestId, {
					kind: "analysis-failed",
					message: error instanceof Error ? error.message : `${error}`,
					path: request.path
				}, request.path);
			}
		},
		async startSession(request) {
			return runRuntimeRequest({
				requestId: request.requestId,
				cancelMessage: "Backend session start request cancelled",
				path: request.path,
				run: () => runtime.start(request),
				onCancelled: (started) => runtime.stop(started.sessionId),
				success: (started) => ({
					ok: true,
					requestId: request.requestId,
					sessionId: started.sessionId,
					runResult: started.runResult
				})
			});
		},
		async resumeSnapshot(request) {
			return runRuntimeRequest({
				requestId: request.requestId,
				cancelMessage: "Backend session resume request cancelled",
				run: () => runtime.resume(request),
				onCancelled: (resumed) => runtime.stop(resumed.sessionId),
				success: (resumed) => ({
					ok: true,
					requestId: request.requestId,
					sessionId: resumed.sessionId,
					runResult: resumed.runResult
				})
			});
		},
		async inspectSnapshot(request) {
			return runRuntimeRequest({
				requestId: request.requestId,
				cancelMessage: "Backend snapshot inspection request cancelled",
				run: () => runtime.inspectSnapshot(request),
				success: (checkpointSnapshots) => ({
					ok: true,
					requestId: request.requestId,
					checkpointSnapshots
				})
			});
		},
		async inspectSnapshotBindings(request) {
			return runRuntimeRequest({
				requestId: request.requestId,
				cancelMessage: "Backend snapshot bindings inspection request cancelled",
				run: () => runtime.inspectSnapshotBindings(request),
				success: (bindings) => ({
					ok: true,
					requestId: request.requestId,
					bindings
				})
			});
		},
		async validateSnapshot(request) {
			try {
				const snapshot = await runtime.validateSnapshot(request);
				if (!snapshot) return requestFailure(request.requestId, {
					kind: "invalid-request",
					message: "Not a valid snapshot object."
				});
				return {
					ok: true,
					requestId: request.requestId,
					snapshot
				};
			} catch (error) {
				return runtimeErrorFailure(request.requestId, error);
			}
		},
		async inspectSession(sessionId) {
			return runtime.inspect(sessionId);
		},
		async stopSession(sessionId) {
			await runtime.stop(sessionId);
		},
		async cancelRequest(requestId) {
			cancelledRequests.set(requestId, true);
			return {
				ok: true,
				requestId
			};
		}
	};
}
//#endregion
//#region src/requests.ts
const BACKEND_REQUEST_ERROR_KINDS = [
	"cancelled",
	"invalid-request",
	"analysis-failed",
	"runtime-failed",
	"resync-required",
	"session-not-found"
];
//#endregion
export { BACKEND_REQUEST_ERROR_KINDS, createBackend, createBackendRuntimeAdapter, createInMemoryDocumentStore };

//# sourceMappingURL=index.js.map